"""
Indicates that a field supports localization through the standard
`Accept-Language` HTTP request header.

The SEEK API defaults to `en-AU` if the requested locales cannot be
satisfied.
"""
directive @localized(
  """
  The locales supported by the field.

  Locales are listed as IETF BCP 47 language tags (e.g. `en-US`) that
  comprise two hyphen-separated subtags:

  1. Primary language: two-letter ISO 639-1:2002 language code, in lowercase
  2. Region: two-letter ISO 3166-1:2013 country code, in uppercase
  """
  locales: [String!]!
) on FIELD_DEFINITION

directive @hirerId on ARGUMENT_DEFINITION | INPUT_FIELD_DEFINITION

directive @paginated(defaultPageSize: Int!) on FIELD_DEFINITION

directive @unpaginated(indicativeLength: Int!) on FIELD_DEFINITION

directive @rateLimit(
  bucketSize: Int!
  refillTokens: Int!
  refillPeriod: Int!
  logOnly: Boolean = false
) on OBJECT | FIELD_DEFINITION

directive @_unstable_hirerRateLimit(
  bucketSize: Int!
  refillTokens: Int!
  refillPeriod: Int!
  logOnly: Boolean = true
) on OBJECT | FIELD_DEFINITION

"""
The schema's entry-point for queries.

This acts as the public, top-level API from which all queries must start.
"""
type Query {
  """
  The list of advertisement products available to the hirer when posting or updating a job.

  This query accepts browser tokens that include the `query:ad-products` scope.
  """
  advertisementProducts(
    """
    The new state of the job ad to be created or updated.
    """
    positionProfile: AdvertisementProducts_PositionProfileInput!

    """
    The product ID that was previously selected.

    The selected product ID may be temporarily stored in a job ad draft.
    When the hirer resumes their posting workflow from the draft at a later date,
    this previous product ID should not be directly used to post the job ad.
    Instead, pass the previous product ID in this field.
    The query will then pre-select an equivalent ad product and return new product IDs that can be used to post the job ad.

    Omit this field when you are newly creating or updating a job ad.
    This is not the identifier of the job ad itself;
    when you are updating a job ad,
    supply the `positionProfile.profileId` instead.
    """
    selectedAdvertisementProductId: String

    """
    The upper limit of advertisement products to return in the list.

    If your user interface can only display a certain number of products,
    set this limit so that SEEK can provide the most relevant set of products within your constraints.
    Do not manually truncate the returned list as that may exclude highly relevant products.

    This must be a positive integer that is greater than or equal to 4.
    Defaults to a unbounded list though expect this to remain within reason;
    we typically return up to 3 products and have only discussed an increase to 4 in future.
    """
    limit: Int
  ): AdvertisementProducts!

  """
  The properties to use to render the Apply with SEEK button on a given page load.

  These include the button label, the URL to redirect the candidate to the SEEK authorization page, and images for the button.
  """
  applyWithSeekButton(
    """
    Information required to generate the Apply with SEEK button link.
    """
    input: ApplyWithSeekButtonInput!
  ): ApplyWithSeekButtonPayload!

  """
  The API version.
  """
  version: String!

  """
  The job category for the given `id`.

  This query accepts browser tokens that include the `query:ontologies` or `query:enhanced-job-posting` scope.
  """
  jobCategory(
    """
    The value of `JobCategory.id` for the requested object.
    """
    id: String!
  ): JobCategory

  """
  A list of top-level job categories for the provided scheme.

  This query accepts browser tokens that include the `query:ontologies` or `query:enhanced-job-posting` scope.
  """
  jobCategories(
    """
    The partial position profile to list job categories for.

    This should include the same field values that will eventually be used to create or post the `PositionProfile`.
    """
    positionProfile: JobCategories_PositionProfileInput

    """
    The scheme ID of the job categories.

    Currently, only `seekAnz` and `seekAnzPublicTest` are supported.
    """
    schemeId: String!

    """
    The country to list job categories for.

    This is a two-letter ISO 3166-1:2013 country code, in uppercase.

    Only use this field if your software has technical limitations that prevent it from dynamically providing a more specific position location to the `jobCategories` query.
    Provide `positionProfile.positionLocation` instead and omit this field where possible.
    """
    countryCode: String
  ): [JobCategory!]!

  """
  An array of suggested job categories for the provided partial `PositionProfile` in decreasing order of relevance.

  A maximum of 10 job categories can be recommended.

  This query accepts browser tokens that include the `query:ontologies` scope or `query:enhanced-job-posting` scope.
  """
  jobCategorySuggestions(
    """
    The partial position profile to suggest a job category for.

    This should include the same field values that will eventually be used to create or post the `PositionProfile`.
    """
    positionProfile: JobCategorySuggestionPositionProfileInput!

    """
    The scheme ID of the suggested job categories.

    Currently, only `seekAnz` and `seekAnzPublicTest` are supported.
    """
    schemeId: String!

    """
    A non-negative limit to the number of job categories to return.

    Defaults to 10.
    """
    first: Int
  ): [JobCategorySuggestionChoice!]!

  """
  A location node with the given location `id`.

  This query accepts browser tokens that include the `query:ontologies` or `query:enhanced-job-posting` scope.
  """
  location(
    """
    The value of `Location.id` for the requested object.
    """
    id: String!
  ): Location

  """
  An array of suggested locations for the text provided in decreasing order of relevance.

  While a maximum of 20 locations can be requested, the current implementation does not return more than 10 recommendations.

  This query accepts browser tokens that include the `query:ontologies` or `query:enhanced-job-posting` scope.
  """
  locationSuggestions(
    """
    The context that the location suggestions will be used in.

    Not all locations are accepted by all SEEK API operations.
    Specifying the correct usage type ensures the returned locations are valid in the context they're consumed.

    Currently two codes are defined:

    -  `PositionPosting` returns locations suitable for posting a position profile.
       They would typically be passed to the `postPosition` or `postPositionProfileForOpening` mutation.

    -  `ProactiveSourcing` returns locations suitable for proactively sourcing candidates.
       They would typically be passed to the `createUnpostedPositionProfileForOpening` mutation.
    """
    usageTypeCode: String!

    """
    The scheme ID of the location dataset to query.

    Currently, only `seekAnz` and `seekAnzPublicTest` are supported.
    """
    schemeId: String!

    """
    The identifier for the `HiringOrganization` used to provide location suggestions weighted by the hirer's SEEK-configured domicile.
    """
    hirerId: String

    """
    Hirer-provided text used for the location suggestions.

    This currently expects a substring of a location's name (e.g. a suburb or city name).
    Postcodes and street addresses are not supported.
    """
    text: String!

    """
    A non-negative limit to the number of locations to return.

    Defaults to 10.
    """
    first: Int
  ): [LocationSuggestion!]

  """
  An array of locations relevant to the provided geoLocation ordered by distance.

  A maximum of 10 locations can be recommended.

  This query accepts browser tokens that include the `query:ontologies` or `query:enhanced-job-posting` scope.
  """
  nearestLocations(
    """
    The scheme ID of the location dataset to query.

    Currently, only `seekAnz` and `seekAnzPublicTest` are supported.
    """
    schemeId: String!

    """
    The geoLocation coordinates input used for the location suggestions.
    """
    geoLocation: GeoLocationInput!

    """
    A non-negative limit to the number of locations to return.

    Defaults to 10.
    """
    first: Int
  ): [Location!]

  """
  A location inferred from the provided address details.

  SEEK will attempt to match the address details to a location in our hierarchy on a best-effort basis.

  This query accepts browser tokens that include the `query:ontologies` or `query:enhanced-job-posting` scope.
  """
  inferLocation(
    """
    The scheme ID of the location dataset to query.

    Currently, only `seekAnz` and `seekAnzPublicTest` are supported.
    """
    schemeId: String!

    """
    The address details to infer a SEEK location from.
    """
    address: SeekPositionAddressInput!

    """
    The identifier for the `HiringOrganization` used to provide location suggestions weighted by the hirer's SEEK-configured domicile.
    """
    hirerId: String
  ): Location

  """
  A list of currencies.

  These may be presented to a hirer for selection in a job posting flow.
  A dropdown is recommended.

  This query accepts browser tokens that include the `query:ontologies` or `query:enhanced-job-posting` scope.
  """
  currencies(
    """
    Determines the list of currencies to be provided.

    Currently only two codes are defined:

    - `All` will provide every currency that SEEK supports in a job posting flow.

    - `SEEKMarket` will provide currencies associated with the markets SEEK is active in.
    """
    usageTypeCode: String!
  ): [Currency!]!

  """
  A list of pay types that specify the method and interval of a payment.

  These may be presented to a hirer for selection in a job posting flow.
  A dropdown or radio group is recommended.

  This query accepts browser tokens that include the `query:ontologies` scope.
  """
  payTypes(
    """
    The scheme ID of the pay types.

    Currently, only `seekAnz` and `seekAnzPublicTest` are supported.
    """
    schemeId: String!
  ): [PayType!]!

  """
  A page of advertisement brandings associated with the specified `hirerId`.

  A maximum of 100 advertisement brandings can be returned in a single page.
  Additional advertisement brandings can be queried using a pagination cursor.

  The result list is returned in ascending creation date & time order.
  It starts from the earliest known advertisement branding if no pagination arguments are provided.

  This query accepts browser tokens that include the `query:advertisement-brandings` or `query:enhanced-job-posting` scope.
  """
  advertisementBrandings(
    """
    An opaque cursor to the earlier bounding advertisement brandings.

    Resulting advertisement brandings will _succeed_ this cursor.
    """
    after: String

    """
    An opaque cursor to the later bounding advertisement brandings.

    Resulting advertisement brandings will _precede_ this cursor.
    """
    before: String

    """
    The upper limit of advertisement brandings to return from the start of the list.

    Defaults to 10 if neither `first` nor `last` are specified.
    Excess advertisement brandings will be trimmed from the end of the list.

    `first` and `last` cannot be specified in the same query.
    """
    first: Int

    """
    The upper limit of advertisement brandings to return from the end of the list.

    Excess advertisement brandings will be trimmed from the start of the list.

    `first` and `last` cannot be specified in the same query.
    """
    last: Int

    """
    The identifier for the `HiringOrganization`.
    """
    hirerId: String!
  ): AdvertisementBrandingsConnection!

  """
  The advertisement branding for the given `id`.

  This query accepts browser tokens that include the `query:advertisement-brandings` or `query:enhanced-job-posting` scope.
  """
  advertisementBranding(
    """
    The value of `AdvertisementBranding.id` for the requested object.
    """
    id: String!
  ): AdvertisementBranding

  """
  The `CandidateProfile` for the given `id`.

  This includes profiles:
    - Uploaded candidate profiles from the hirer's ATS
    - SEEK candidate profiles available for prefill based on Apply with SEEK consent
    - SEEK candidate profiles available for export based on a job application
  """
  candidateProfile(
    """
    The value of `CandidateProfile.profileId` for the requested object.
    """
    id: String!
  ): CandidateProfile

  """
  Get candidate profile summaries by email addresses.

  This returns summary profile information including email, identity verification status, and profile existence.

  This query accepts browser tokens that include the `query:seek-candidate-profiles` scope.
  """
  seekCandidateSummaries(
    """
    Input containing candidates and optional position context.
    """
    input: SeekCandidateSummariesInput!
  ): [SeekCandidateProfileSummary!]!

  """
  The candidate for the given `id`.

  This will include the candidate's personal details along with all application profiles for a single hirer.
  """
  candidate(
    """
    The value of `Candidate.documentId` for the requested object.
    """
    id: String!
  ): Candidate

  """
  The `CandidateProcessHistoryItem` for the given `id`.
  """
  candidateProcessHistoryItem(
    """
    The value of `CandidateProcessHistoryItem.id` for the requested object.
    """
    id: String!
  ): CandidateProcessHistoryItem

  """
  The event for the given `id`.
  """
  event(
    """
    The value of `Event.id` for the requested object.
    """
    id: String!
  ): Event

  """
  A page of events matching the specified criteria.

  A maximum of 100 events can be returned in a single page.
  Additional events can be queried using a pagination cursor.

  The result list is returned in ascending stream date & time order.
  It starts from the earliest known event if no pagination arguments are provided.
  """
  events(
    """
    An opaque cursor to the earlier bounding event.

    Resulting events will _succeed_ this cursor.
    """
    after: String

    """
    An opaque cursor to the later bounding event.

    Resulting events will _precede_ this cursor.
    """
    before: String

    """
    The upper limit of events to return from the start of the list.

    Defaults to 10 if neither `first` nor `last` are specified.
    Excess events will be trimmed from the end of the list.

    `first` and `last` cannot be specified in the same query.
    """
    first: Int

    """
    The upper limit of events to return from the end of the list.

    Excess events will be trimmed from the start of the list.

    `first` and `last` cannot be specified in the same query.
    """
    last: Int

    """
    The additional `Event`-specific criteria to filter by.
    """
    filter: EventsFilterInput

    """
    The scheme ID of the event.

    Currently, only `seekAnz` and `seekAnzPublicTest` are supported.
    """
    schemeId: String!
  ): EventsConnection!

  """
  A page of webhook attempts matching the specified criteria generated by a selected event.

  A maximum of 100 webhook attempts can be returned in a single page.
  Additional webhook attempts can be queried using a pagination cursor.

  The result list is returned in ascending creation date & time order.
  It starts from the earliest known attempt if no pagination arguments are provided.
  """
  webhookAttemptsForEvent(
    """
    An opaque cursor to the earlier bounding webhook attempt.

    Resulting webhook attempts will _succeed_ this cursor.
    """
    after: String

    """
    An opaque cursor to the later bounding webhook attempt.

    Resulting webhook attempts will _precede_ this cursor.
    """
    before: String

    """
    The upper limit of webhook attempts to return from the start of the list.

    Defaults to 10 if neither `first` nor `last` are specified.
    Excess webhook attempts will be trimmed from the end of the list.

    `first` and `last` cannot be specified in the same query.
    """
    first: Int

    """
    The upper limit of webhook attempts to return from the end of the list.

    Excess webhook attempts will be trimmed from the start of the list.

    `first` and `last` cannot be specified in the same query.
    """
    last: Int

    """
    The additional `WebhookAttempt`-specific criteria to filter by.
    """
    filter: WebhookAttemptsFilterInput

    """
    The identifier for the `Event` that generated the attempts.
    """
    eventId: String!
  ): WebhookAttemptsConnection!

  """
  The webhook subscription for the given `id`.
  """
  webhookSubscription(
    """
    The value of `WebhookSubscription.id` for the requested object.
    """
    id: String!
  ): WebhookSubscription

  """
  A page of webhook subscriptions matching the specified criteria.

  A maximum of 100 webhook subscriptions can be returned in a single page.
  Additional webhook subscriptions can be queried using a pagination cursor.

  The result list is returned in ascending creation date & time order.
  It starts from the earliest known subscription if no pagination arguments are provided.
  """
  webhookSubscriptions(
    """
    An opaque cursor to the earlier bounding webhook subscription.

    Resulting webhook subscriptions will _succeed_ this cursor.
    """
    after: String

    """
    An opaque cursor to the later bounding webhook subscription.

    Resulting webhook subscriptions will _precede_ this cursor.
    """
    before: String

    """
    The upper limit of webhook subscriptions to return from the start of the list.

    Defaults to 10 if neither `first` nor `last` are specified.
    Excess webhook subscriptions will be trimmed from the end of the list.

    `first` and `last` cannot be specified in the same query.
    """
    first: Int

    """
    The upper limit of webhook subscriptions to return from the end of the list.

    Excess webhook subscriptions will be trimmed from the start of the list.

    `first` and `last` cannot be specified in the same query.
    """
    last: Int

    """
    The additional `WebhookSubscription`-specific criteria to filter by.
    """
    filter: WebhookSubscriptionsFilterInput

    """
    The scheme ID of the webhook subscription's events.

    Currently, only `seekAnz` and `seekAnzPublicTest` are supported.
    """
    schemeId: String!
  ): WebhookSubscriptionsConnection!

  """
  The webhook subscription replay for the given `id`.
  """
  webhookSubscriptionReplay(
    """
    The value of `WebhookSubscriptionReplay.id` for the requested object.
    """
    id: String!
  ): WebhookSubscriptionReplay

  """
  A page of webhook requests matching the specified criteria generated by a selected subscription.

  A maximum of 100 webhook requests can be returned in a single page.
  Additional webhook requests can be queried using a pagination cursor.

  The result list is returned in ascending creation date & time order.
  It starts from the earliest known request if no pagination arguments are provided.
  """
  webhookRequestsForSubscription(
    """
    An opaque cursor to the earlier bounding webhook request.

    Resulting webhook requests will _succeed_ this cursor.
    """
    after: String

    """
    An opaque cursor to the later bounding webhook request.

    Resulting webhook requests will _precede_ this cursor.
    """
    before: String

    """
    The upper limit of webhook requests to return from the start of the list.

    Defaults to 10 if neither `first` nor `last` are specified.
    Excess webhook requests will be trimmed from the end of the list.

    `first` and `last` cannot be specified in the same query.
    """
    first: Int

    """
    The upper limit of webhook requests to return from the end of the list.

    Excess webhook requests will be trimmed from the start of the list.

    `first` and `last` cannot be specified in the same query.
    """
    last: Int

    """
    The additional `WebhookRequest`-specific criteria to filter by.
    """
    filter: WebhookRequestFilterInput

    """
    The identifier for the `WebhookSubscription` that generated the requests.
    """
    subscriptionId: String!
  ): WebhookRequestsConnection!

  """
  The webhook request for the given `requestId`.
  """
  webhookRequest(
    """
    The scheme ID of the `WebhookSubscription` that generated the request.

    This is used to determine which data source includes the `requestId`.

    Currently, only `seekAnz` and `seekAnzPublicTest` are supported.
    """
    schemeId: String!

    """
    The request ID.

    This is included in the HTTP request as an `X-Request-Id` custom header.
    """
    requestId: String!
  ): WebhookRequest

  """
  The hiring organization for the given `id`.

  This query accepts browser tokens that include the `query:organizations` or `query:enhanced-job-posting` scope.
  """
  hiringOrganization(
    """
    The value of `HiringOrganization.id` for the requested object.
    """
    id: String!
  ): HiringOrganization

  """
  A page of hirers that have an active relationship with the requesting partner.

  This will not return agencies; it's not possible to have a relationship with an agency.

  A maximum of 100 hirers can be returned in a single page.
  Additional hirers can be queried using a pagination cursor.

  The result list is ordered alphabetically by the hirer's name.

  This query accepts browser tokens that include the `query:organizations` scope.
  It will only return the single hirer that the browser token is scoped to.
  """
  hiringOrganizations(
    """
    The scheme ID of the hirers.

    Currently, only `seekAnz` and `seekAnzPublicTest` are supported.
    """
    schemeId: String!

    """
    An opaque cursor to the earlier bounding hirer.

    Resulting hirers will be alphabetically later than this cursor.
    """
    after: String

    """
    An opaque cursor to the later bounding hirers.

    Resulting hirers will be alphabetically earlier than this cursor.
    """
    before: String

    """
    The upper limit of hirers to return from the start of the list.

    Defaults to 10 if neither `first` nor `last` are specified.
    Excess hirers will be trimmed from the end of the list.

    `first` and `last` cannot be specified in the same query.
    """
    first: Int

    """
    The upper limit of hirers to return from the end of the list.

    Excess hirers will be trimmed from the start of the list.

    `first` and `last` cannot be specified in the same query.
    """
    last: Int

    """
    The additional `HiringOrganization`-specific criteria to filter by.
    """
    filter: HiringOrganizationsFilterInput
  ): HiringOrganizationsConnection!

  """
  A hiring organization corresponding to the given legacy SEEK advertiser ID.

  This query accepts browser tokens that include the `query:organizations` or `query:enhanced-job-posting` scope.
  """
  seekAnzAdvertiser(
    """
    The legacy SEEK advertiser ID.

    This is a numeric identifier that was used by the previous version of Apply with SEEK.
    """
    id: Int!
  ): HiringOrganization @deprecated(reason: "Use `hiringOrganization` instead.")

  """
  The organizations the query's access token can act on behalf of.

  For all token types this returns the name of the integration partner.

  This query accepts browser tokens that include the `query:organizations` scope.
  When provided with a browser token this will additionally return the scoped SEEK hirer.
  """
  self: SelfOrganizations!

  """
  A position opening with the given `id`.
  """
  positionOpening(
    """
    The value of `PositionOpening.documentId` for the requested object.
    """
    id: String!
  ): PositionOpening

  """
  A position profile with the given `id`.

  This query accepts browser tokens that include the `query:position-profiles` scope for posted position profiles.
  Note that this scope does not grant access to the containing `PositionProfile.positionOpening`.
  """
  positionProfile(
    """
    The value of `PositionProfile.profileId` for the requested object.
    """
    id: String!
  ): PositionProfile

  """
  A page of position openings for the given `hirerId`.

  Currently, only position openings in the `global` and `globalPublicTest` schemes are returned.

  A maximum of 20 position openings can be returned in a single page.
  Additional position openings can be queried using a pagination cursor.
  """
  positionOpenings(
    """
    The identifier for the `HiringOrganization` to retrieve position openings for.
    """
    hirerId: String!

    """
    An opaque cursor to the earlier bounding page.

    Resulting position openings will _succeed_ this cursor.
    """
    after: String

    """
    The upper limit of position openings to return from the start of the list.

    Defaults to 10 if `first` is not specified.
    Excess position openings will be trimmed from the end of the list.
    """
    first: Int

    """
    The additional `PositionOpening`-specific criteria to filter by.
    """
    filter: PositionOpeningsFilterInput
  ): PositionOpeningConnection!

  """
  The reason why a specific position was not posted on SEEK, identified by its reason ID.
  """
  positionNotPostedReason(
    """
    The input parameter for retrieving a single position not posted reason.
    """
    input: PositionNotPostedReasonInput!
  ): PositionNotPostedReasonPayload

  """
  All reasons why a position was not posted on SEEK, identified by the partner's position ID.
  """
  positionNotPostedReasons(
    """
    The input parameter for retrieving all reasons a position was not posted.
    """
    input: PositionNotPostedReasonsInput!
  ): [PositionNotPostedReasonPayload!]

  """
  A preview of a prospective job ad as it would appear on a SEEK job board.

  This query accepts browser tokens that include the `query:posted-position-profile-previews` or `query:enhanced-job-posting` scope.
  """
  postedPositionProfilePreview(
    """
    The details of the position profile to be previewed.
    """
    positionProfile: PostedPositionProfilePreview_PositionProfileInput!
  ): PostedPositionProfilePreview!

  """
  An array of suggested application questions for the provided partial `PositionProfile` in decreasing order of relevance.

  A maximum of 10 application questions can be recommended.

  This query accepts browser tokens that include the `query:application-library-question-suggestions` or `query:enhanced-job-posting` scope.
  """
  applicationLibraryQuestionSuggestions(
    """
    The partial position profile to suggest application questions.

    This should include the same field values that will eventually be used to create or post the `PositionProfile`.
    """
    positionProfile: ApplicationLibraryQuestionSuggestions_PositionProfileInput!

    """
    The scheme ID of the suggested application questions.

    Currently, only `seekAnz` and `seekAnzPublicTest` are supported.
    """
    schemeId: String!

    """
    A non-negative limit to the number of questions to return.

    Defaults to 10.
    """
    first: Int
  ): [ApplicationLibraryQuestionSuggestion!]!

  """
  Searches SEEK's library of application screening questions.

  A maximum of 10 application questions can be returned.

  This query accepts browser tokens that include the `query:application-library-question-suggestions` or `query:enhanced-job-posting` scope.
  """
  applicationLibraryQuestionSearch(
    """
    The keywords to search SEEK's library of application questions with.
    """
    keywords: String!

    """
    The partial position profile to search application questions.

    This should include the same field values that will eventually be used to create or post the `PositionProfile`.
    """
    positionProfile: ApplicationLibraryQuestionSearch_PositionProfileInput!

    """
    The scheme ID of the suggested application questions.

    Currently, only `seekAnz` and `seekAnzPublicTest` are supported.
    """
    schemeId: String!

    """
    A non-negative limit to the number of questions to return.

    Defaults to 10.
    """
    first: Int
  ): [ApplicationLibraryQuestionSearchResult!]!

  """
  An application questionnaire with the given `id`.

  Questionnaires can be associated with a `PositionProfile`.

  This query accepts browser tokens that include the `query:application-questionnaires` or `query:enhanced-job-posting` scope.
  """
  applicationQuestionnaire(
    """
    The value of `ApplicationQuestionnaire.id` for the requested object.
    """
    id: String!
  ): ApplicationQuestionnaire
}

"""
A list of advertisement products with additional context that applies to all products.
"""
type AdvertisementProducts {
  """
  The list of advertisement products.
  """
  products: [AdvertisementProduct!]!

  """
  Information on this set of available products.

  Typically this is a legal disclaimer.
  """
  information: String
}

"""
The details of an available advertisement product.
"""
type AdvertisementProduct {
  """
  The identifier of this advertisement product.

  Identifiers may become stale, and should not be stored for long periods.

  It would be appropriate to save an identifier for use in a job ad draft, but not for use as a job ad template.
  """
  id: ObjectIdentifier!

  """
  The name of the advertisement product for displaying to the user.

  This is typically a single word that differentiates the product from other options.
  """
  label: String!

  """
  Whether this advertisement product should be preselected or not.

  This field generally indicates the advertisement product that is set in the current state of an existing `PositionProfile`.
  """
  selected: Boolean!

  """
  A short phrase intended for display to a user that describes the advertisement product.
  """
  description: String

  """
  Information about how payment will be made for this advertisement product.
  """
  payment: AdvertisementProductPaymentDetails

  """
  Information about how much this advertisement product costs.
  """
  price: AdvertisementProductPriceDetails

  """
  Additional information that is accepted when posting a job ad to configure the features of this advertisement product.
  """
  features: AdvertisementProductFeatures!

  """
  An array of short phrases that tell the user what value this advertisement product provides.
  """
  sellingPoints: [AdvertisementProductSellingPoint!]!
}

"""
The tax details of the ad product price.
"""
type AdvertisementProductPriceDetails_Tax {
  """
  The tax code of the product price.
  """
  code: String!

  """
  A non-negative integer in the minor currency unit for the ISO currency code.

  For example, this is the number of cents in dollar-denominated currencies.

  The currency of the tax value matches the parent `AdvertisementProductPriceDetails.currency`.
  """
  value: Int!
}

"""
The details of what will be paid for an advertisement product.
"""
type AdvertisementProductPriceDetails {
  """
  The summary of what the price is for an advertisement product.

  This is a human-readable string intended for displaying in a user interface.
  """
  summary: String!

  """
  A non-negative integer in the minor currency unit for the ISO currency code.

  For example, this is the number of cents in dollar-denominated currencies.

  The value excludes tax and is intended to support budgeting capabilities of your software.
  Use `summary` if you need to directly display the price to the hirer.

  Please contact SEEK before you use this field.
  """
  value: Int!

  """
  The three-letter ISO 4217 currency code of the product price, in uppercase.

  This field is intended for budgeting and not to be directly shown to hirer.

  Please contact SEEK before you use this field.
  """
  currency: String!

  """
  The tax detail of the product price.

  This field is intended for budgeting and not to be directly shown to hirer.

  Please contact SEEK before you use this field.
  """
  tax: AdvertisementProductPriceDetails_Tax
}

"""
The details of how an advertisement product will be paid.
"""
type AdvertisementProductPaymentDetails {
  """
  A plain text summary of how payment will be broken down across payment methods.

  This is a human-readable string intended for displaying in a user interface.
  """
  summary: String!

  """
  An alternate version of `summary` that includes HTML markup.

  This is intended to be parsed and rendered by a web browser for displaying in a user interface.
  """
  summaryHtml: String!
}

"""
Additional information that is accepted when posting a job ad with this advertisement product.
"""
type AdvertisementProductFeatures {
  """
  Additional information related to branding that is accepted when posting a job ad.
  """
  branding: AdvertisementProductFeatures_Branding

  """
  Additional information related to search bullet points that is accepted when posting a job ad.
  """
  searchBulletPoints: AdvertisementProductFeatures_SearchBulletPoints
}

"""
A selling point of an advertisement product.

This details a reason why a user should choose this advertisement product over another.
"""
type AdvertisementProductSellingPoint {
  """
  The textual representation of this selling point for displaying to the user.
  """
  text: String!
}

"""
Branding features that are included with a product when posting or updating a job ad.
"""
type AdvertisementProductFeatures_Branding {
  """
  Whether the logo from the provided `AdvertisementBranding` will be visible on the job ad.
  """
  logoIndicator: Boolean!

  """
  Whether the cover image from the provided `AdvertisementBranding` will be visible on the job ad.
  """
  coverImageIndicator: Boolean!
}

"""
Search bullet points that are included with a product.
"""
type AdvertisementProductFeatures_SearchBulletPoints {
  """
  How many search bullet points are accepted when posting a job ad.

  This is always a positive integer; if bullet points are not supported, the entire object will be `null`.
  """
  limit: Int!
}

"""
The proposed state of the job ad to be posted or updated.
"""
input AdvertisementProducts_PositionProfileInput {
  """
  An array of `JobCategory` identifiers.

  This field currently requires a single identifier for a child job category.
  """
  jobCategories: [String!]!

  """
  The remuneration offered for the position.

  This information allows us to better forecast the performance of the advertisement products.
  """
  offeredRemunerationPackage: RemunerationPackageInput

  """
  An array of `Location` identifiers.

  Scheme requirements:

  - This field currently requires a single identifier for a location.
  """
  positionLocation: [String!]!

  """
  Array of identifiers for the `HiringOrganization` that will post or update the job ad.

  The `seekAnz` scheme requires exactly one element.
  """
  positionOrganizations: [String!]!

  """
  A short phrase describing the position as it would be listed on a business card or in a company directory.

  This field has a maximum length of 80 characters.
  """
  positionTitle: String!

  """
  The job ad ID to be updated.

  This field is mandatory when updating a job ad.

  Omit this field when creating a new job ad.
  """
  profileId: String

  """
  A SEEK work type code.

  Currently, four codes are defined for the `seekAnz` scheme:

  - `Casual` indicates a casual position.
  - `ContractTemp` indicates a fixed-length contract position.
  - `FullTime` indicates a full-time position.
  - `PartTime` indicates a part-time position.

  This information allows us to better forecast the performance of the advertisement products.
  """
  seekAnzWorkTypeCode: String
}

"""
A JSON scalar type for representing arbitrary JSON values.
"""
scalar JSON

"""
The input parameter for the `applyWithSeekButton` query.
"""
input ApplyWithSeekButtonInput {
  """
  The HTTPS URI that the candidate will be redirected to after completing their authorization.
  """
  redirectUri: String!

  """
  The identifier of the SEEK hirer that owns the position.

  Their name will be displayed on the SEEK authorization page (i.e. "Partner Name on behalf of Hirer Name").
  """
  hirerId: String!

  """
  The `seek-token` received as part of the query string when the candidate arrived from SEEK.

  If `seek-token` is not available (i.e. the candidate did not arrive from SEEK), this field can be omitted.
  """
  token: String

  """
  The HTTPS URI that the candidate accesses to apply for the position.
  """
  applicationUri: String!

  """
  An optional hirer-provided free-text job reference for the position.
  """
  seekHirerJobReference: String
}

"""
The response from the `applyWithSeekButton` query.
"""
type ApplyWithSeekButtonPayload {
  """
  The link for the button to redirect the candidate to the SEEK authorization page.
  """
  url: String!

  """
  The label for the button, e.g. "Apply with SEEK" or "Apply with Jobstreet".
  """
  buttonLabel: String!

  """
  The image options for the Apply with SEEK button.
  """
  images: ApplyWithSeekButtonImages!
}

"""
The images for the Apply with SEEK button, with different background colours.
"""
type ApplyWithSeekButtonImages {
  """
  The URL of the image, e.g. `https://integration.seek.com/awsk/button-images/seek/en/accent.svg`.
  """
  accent: String!

  """
  The URL of the image, e.g. `https://integration.seek.com/awsk/button-images/seek/en/dark.svg`.
  """
  dark: String!

  """
  The URL of the image, e.g. `https://integration.seek.com/awsk/button-images/seek/en/light.svg`.
  """
  light: String!
}

"""
The schema's entry-point for mutations.

This acts as the public, top-level API from which all mutation queries must start.
"""
type Mutation {
  """
  A placeholder string.
  """
  _empty: String @deprecated(reason: "Placeholder only.")

  """
  Submits a signal representing the progression of a candidate or position through a recruitment process.
  """
  sendSignal(input: SendSignalInput!): Boolean

  """
  Submits a batch of signals representing the progression of candidates and positions through a recruitment process.

  Up to 100 signals can be sent in a single request.
  """
  sendSignals(input: SendSignalsInput!): Boolean

  """
  Deletes an uploaded candidate and their profile from SEEK's systems.

  This will also delete all `CandidateProcessHistoryItem`s belonging to the candidate profile.
  """
  deleteUploadedCandidate(
    input: DeleteUploadedCandidateInput!
  ): DeleteUploadedCandidatePayload

  """
  Uploads a candidate and their profile into SEEK's systems.
  """
  uploadCandidate(input: UploadCandidateInput!): UploadCandidatePayload!

  """
  Verifies a candidate's identity via SEEK Pass.
  """
  verifyCandidate(input: VerifyCandidateInput!): VerifyCandidatePayload

  """
  Updates the personal details of an uploaded candidate.
  """
  updateUploadedCandidatePerson(
    input: UpdateUploadedCandidatePersonInput!
  ): UpdateUploadedCandidatePersonPayload

  """
  Updates the actions associated with an uploaded candidate profile.
  """
  updateUploadedCandidateProfileActions(
    input: UpdateUploadedCandidateProfileActionsInput!
  ): UpdateUploadedCandidateProfileActionsPayload

  """
  Updates the dates associated with an uploaded candidate profile.
  """
  updateUploadedCandidateProfileDates(
    input: UpdateUploadedCandidateProfileDatesInput!
  ): UpdateUploadedCandidateProfileDatesPayload

  """
  Updates the position preferences associated with an uploaded candidate profile.
  """
  updateUploadedCandidateProfilePositionPreferences(
    input: UpdateUploadedCandidateProfilePositionPreferencesInput!
  ): UpdateUploadedCandidateProfilePositionPreferencesPayload

  """
  Adds a process history item to an uploaded candidate's profile.
  """
  createCandidateProcessHistoryItem(
    input: CreateCandidateProcessHistoryItemInput!
  ): CreateCandidateProcessHistoryItemPayload!

  """
  Deletes a candidate process history item from SEEK's systems.
  """
  deleteCandidateProcessHistoryItem(
    input: DeleteCandidateProcessHistoryItemInput!
  ): DeleteCandidateProcessHistoryItemPayload

  """
  Updates a candidate process history item.
  """
  updateCandidateProcessHistoryItem(
    input: UpdateCandidateProcessHistoryItemInput!
  ): UpdateCandidateProcessHistoryItemPayload

  """
  Creates a new webhook subscription.
  """
  createWebhookSubscription(
    input: CreateWebhookSubscriptionInput!
  ): CreateWebhookSubscriptionPayload!

  """
  Updates an existing webhook subscription's delivery configuration.

  This modifies fields related to the URL and payload of an existing webhook subscription.
  Changes may take up to half an hour to take effect.

  The fields that determine which events are to be delivered are immutable.
  A new webhook subscription should be created for such cases.
  """
  updateWebhookSubscriptionDeliveryConfiguration(
    input: UpdateWebhookSubscriptionDeliveryConfigurationInput!
  ): UpdateWebhookSubscriptionDeliveryConfigurationPayload

  """
  Updates an existing webhook subscription's signing configuration.

  This modifies fields related to the signature of an existing webhook subscription.
  Changes may take up to half an hour to take effect.
  """
  updateWebhookSubscriptionSigningConfiguration(
    input: UpdateWebhookSubscriptionSigningConfigurationInput!
  ): UpdateWebhookSubscriptionSigningConfigurationPayload

  """
  Deletes an existing webhook subscription.
  """
  deleteWebhookSubscription(
    input: DeleteWebhookSubscriptionInput!
  ): DeleteWebhookSubscriptionPayload

  """
  Replays a webhook subscription.

  This causes any matching events to be requeued for delivery.

  Resending occurs asynchronously in a background task.
  """
  replayWebhookSubscription(
    input: ReplayWebhookSubscriptionInput!
  ): ReplayWebhookSubscriptionPayload

  """
  Sets the disabled features for a hiring organization.

  This allows partners to disable specific features for a hirer that would otherwise be available based on their relationship types.

  Currently, one feature code is defined:

  - `CandidateVerification` disables the Verify with SEEK Pass functionality for the hirer.

  This mutation accepts tokens that include the `mutate:candidate-verification` scope.
  """
  setDisabledFeatures(
    input: SetDisabledFeaturesInput!
  ): SetDisabledFeaturesPayload

  """
  Creates a new position opening.

  Every position profile belongs to a position opening.
  Up to 25 position profiles may belong to the same position opening.
  """
  createPositionOpening(
    input: CreatePositionOpeningInput!
  ): CreatePositionOpeningPayload!

  """
  Replaces an existing position opening's person contacts.
  """
  updatePositionOpeningPersonContacts(
    input: UpdatePositionOpeningPersonContactsInput!
  ): UpdatePositionOpeningPersonContactsPayload

  """
  Updates the status of a position opening.

  This status is provided to help hirers manage position openings.
  The SEEK API does not use the position opening's status itself.
  """
  updatePositionOpeningStatus(
    input: UpdatePositionOpeningStatusInput!
  ): UpdatePositionOpeningStatusPayload

  """
  Deletes an empty position opening.

  Each position profile that belongs to a position opening must be deleted before the position opening can be deleted.
  """
  deletePositionOpening(
    input: DeletePositionOpeningInput!
  ): DeletePositionOpeningPayload

  """
  Asynchronously posts a job ad for an existing position opening.

  If the position opening already contains 25 position profiles this will fail with a `BAD_USER_INPUT` error.

  Once the job ad has been posted a `PositionProfilePosted` event will be emitted.
  """
  postPositionProfileForOpening(
    input: PostPositionProfileForOpeningInput!
  ): PostPositionProfileForOpeningPayload!

  """
  Asynchronously updates a live job ad.

  The position profile's existing fields will be replaced with the provided input fields.
  This will update the live job ad under its current URL.

  Once the job ad has been updated a `PositionProfileUpdated` event will be emitted.
  """
  updatePostedPositionProfile(
    input: UpdatePostedPositionProfileInput!
  ): UpdatePostedPositionProfilePayload

  """
  Asynchronously closes a live job ad.

  The job ad will be removed from the job board and no refund will be issued.
  The `PositionProfile` and its associated candidate applications will be available for 6 months after its close date.

  Once the job ad has been closed a `PositionProfileClosed` event will be emitted.
  """
  closePostedPositionProfile(
    input: ClosePostedPositionProfileInput!
  ): ClosePostedPositionProfilePayload

  """
  Asynchronously posts a job ad for a new position opening.

  This combines the `createPositionOpening` & `postPositionProfileForOpening` mutations in a single operation.
  """
  postPosition(input: PostPositionInput!): PostPositionPayload!

  """
  Creates a new unposted position profile for an opening.

  If the position opening already contains 25 position profiles this will fail with a `BAD_USER_INPUT` error.
  """
  createUnpostedPositionProfileForOpening(
    input: CreateUnpostedPositionProfileForOpeningInput!
  ): CreateUnpostedPositionProfileForOpeningPayload!

  """
  Updates an existing unposted position profile.
  """
  updateUnpostedPositionProfile(
    input: UpdateUnpostedPositionProfileInput!
  ): UpdateUnpostedPositionProfilePayload

  """
  Deletes an unposted position profile.
  """
  deleteUnpostedPositionProfile(
    input: DeleteUnpostedPositionProfileInput!
  ): DeleteUnpostedPositionProfilePayload

  """
  Syncs a position.
  """
  syncPositions(input: SyncPositionsInput!): SyncPositionsPayload_Success

  """
  Creates a new questionnaire.

  The `input` must not exceed 56 KiB in length.

  This mutation accepts browser tokens that include the `mutate:application-questionnaires` scope.
  """
  createApplicationQuestionnaire(
    input: CreateApplicationQuestionnaireInput!
  ): CreateApplicationQuestionnairePayload!
}

"""
An individual component of a physical address.
"""
type AddressComponent {
  """
  The type of the address component.

  Currently, the following components are defined:

  - `Apartment` indicates an address line for an apartment, unit or suite.
  - `State` indicates a country subdivision such as a district, internal territory, province, or state.
  """
  type: String!

  """
  The actual component value.
  """
  value: String!
}

"""
An individual component of a physical address.
"""
input AddressComponentInput {
  """
  The type of the address component.

  Currently, the following components are defined:

  - `Apartment` indicates an address line for an apartment, unit or suite.
  - `State` indicates a country subdivision such as a district, internal territory, province, or state.
  """
  type: String!

  """
  The actual component value.
  """
  value: String!
}

"""
A physical address.
"""
type Address {
  """
  The street line address.
  """
  line: String

  """
  An array of additional address lines.

  These may include an apartment or suite number.
  """
  extendedLines: [AddressComponent!]!

  """
  The two-letter ISO 3166-1:2013 country code, in uppercase.
  """
  countryCode: String

  """
  An array of further divisions of the country.

  These may include districts, regions, states, provinces, etc.
  """
  countrySubDivisions: [AddressComponent!]!

  """
  The city or suburb of the address.
  """
  city: String

  """
  The postal code of the address.
  """
  postalCode: String

  """
  The geographical coordinates of the address.
  """
  geoLocation: GeoLocation

  """
  The formatted representation of the whole address for display purposes.
  """
  formattedAddress: String
}

"""
A physical address.
"""
input AddressInput {
  """
  The street line address.
  """
  line: String

  """
  An array of additional address lines.

  These may include an apartment or suite number.

  A maximum of 5 extended lines may be provided.
  """
  extendedLines: [AddressComponentInput!]!

  """
  The two-letter ISO 3166-1:2013 country code, in uppercase.
  """
  countryCode: String!

  """
  An array of further divisions of the country.

  These may include districts, regions, states, provinces, etc.

  A maximum of 5 subdivisions may be provided.
  """
  countrySubDivisions: [AddressComponentInput!]!

  """
  The city or suburb of the address.
  """
  city: String!

  """
  The postal code of the address.

  This field has a maximum length of 50 characters.
  """
  postalCode: String!

  """
  The geographical coordinates of the address.
  """
  geoLocation: GeoLocationInput

  """
  The formatted representation of the whole address for display purposes.

  This field has a maximum length of 1,000 characters.
  """
  formattedAddress: String
}

"""
An address of a human-accessible web page.
"""
type WebUrl {
  """
  The URL of the web page.
  """
  url: String!
}

"""
An email address.
"""
type Email {
  """
  The email address.
  """
  address: String!
}

"""
An email address.
"""
input EmailInput {
  """
  The email address.

  This field has a maximum length of 255 bytes in UTF-8 encoding.
  """
  address: String!
}

"""
The phone number of a person.
"""
type Phone {
  """
  The phone number represented as a human readable string.
  """
  formattedNumber: String!
}

"""
The phone number of a person.
"""
input PhoneInput {
  """
  The phone number represented as a human readable string.
  """
  formattedNumber: String!
}

"""
Communication channels for a person.
"""
type Communication {
  """
  An array of phone numbers for the person.

  The phone numbers are ordered in descending preference.

  This field is redacted and an empty array is returned when a candidate or job application is deleted.
  """
  phone: [Phone!]!

  """
  An array of email addresses for the person.

  The email addresses are ordered in descending preference.

  This field is redacted and an empty array is returned when a candidate or job application is deleted.
  """
  email: [Email!]!

  """
  An array of physical addresses for the person.

  The physical addresses are ordered in descending preference.

  This field is redacted and an empty array is returned when a candidate or job application is deleted.
  """
  address: [Address!]!

  """
   Whether the candidate must not be contacted by hirers.

  - For uploaded candidates from the Proactive Sourcing use case,
    this field may be set to prevent hirers from contacting them through SEEK Talent Search.
    A `null` value is treated the same as an explicit `false`.

  - For `PositionOpening` contact people from the Job Posting and Proactive Sourcing use cases,
    this field is always ignored.
  """
  seekDoNotContactIndicator: Boolean
}

"""
Communication channels for a person.
"""
input CommunicationInput {
  """
  An array of phone numbers for the person.

  The phone numbers are ordered in descending preference.

  Between 0 and 5 phone numbers may be provided, inclusive.
  """
  phone: [PhoneInput!]!

  """
  An array of email addresses for the person.

  The email addresses are ordered in descending preference.

  A maximum of 5 email addresses may be provided.
  """
  email: [EmailInput!]!

  """
  An array of physical addresses for the person.

  The physical addresses are ordered in descending preference.

  Between 0 and 5 physical addresses may be provided, inclusive.
  """
  address: [AddressInput!]

  """
  Whether the candidate must not be contacted by hirers.

  - For uploaded candidates from the Proactive Sourcing use case,
    this field may be set to prevent hirers from contacting them through SEEK Talent Search.
    A `null` value is treated the same as an explicit `false`.

  - For `PositionOpening` contact people from the Job Posting and Proactive Sourcing use cases,
    this field is always ignored.
  """
  seekDoNotContactIndicator: Boolean
}

"""
An address of a human-accessible web page.
"""
input WebUrlInput {
  """
  The URL of the web page.
  """
  url: String!
}

"""
A date-time string compliant with RFC 3339.
"""
scalar DateTime

"""
A date string compliant with RFC 3339.
"""
scalar Date

"""
A date string compliant with the ISO 8601 "year", "year and month" or "complete date" formats.

For example, `"2020"`, `"2020-05"` and `"2020-05-27"` are all valid for `HistoryDate`.

This is used for dates in a candidate's position & employment histories where the precise month or day may not have been provided.
"""
scalar HistoryDate

"""
A time period for which an associated object is effective.
"""
type EffectiveTimePeriod {
  """
  The date which the associated object is no longer effective.

  For example, this corresponds to the expiry date of certifications.
  This will be `null` if the associated object does not expire.
  """
  validTo: HistoryDate
}

"""
An opaque identifier for GraphQL objects.

The `value` has a maximum length of 255 characters,
and will always be representable with 255 bytes in UTF-8 encoding.
"""
type ObjectIdentifier {
  """
  The identifier itself.

  This has a maximum length of 255 characters,
  and will always be representable with 255 bytes in UTF-8 encoding.
  """
  value: String!
}

"""
The category of a job's occupation.
"""
type JobCategory {
  """
  The identifier for the `JobCategory`.
  """
  id: ObjectIdentifier!

  """
  The parent job category.

  This is a more general classification that this category belongs to.
  It will be `null` for root categories that do not belong to a more general classification.
  """
  parent: JobCategory

  """
  An array of child job categories.

  These are more specific categories that belong to this general classification.
  """
  children: [JobCategory!]

  """
  Name of the job category.
  """
  name: String!
}

"""
A job category with information of the suggested choice.
"""
type JobCategorySuggestionChoice {
  """
  The job category information of the suggestion choice.

  This will be a child job category suitable for posting a position profile.
  """
  jobCategory: JobCategory!

  """
  The confidence in the returned job category based on the suggestion input.

  This is a floating point value ranging from 0 (lowest) to 1 (highest).
  """
  confidence: Float!
}

"""
The input parameter for the `jobCategorySuggestions` query.
"""
input JobCategorySuggestionPositionProfileInput {
  """
  The position title.
  """
  positionTitle: String!

  """
  An array of identifiers for the position's `Location`s.

  A maximum of 1 location may be provided.
  """
  positionLocation: [String!]!

  """
  An array of identifiers for the `HiringOrganization`s that have the position.

  A maximum of 1 organization may be provided.

  Information such as the organization's domicile may be used to refine the returned suggestions.
  """
  positionOrganizations: [String!]

  """
  The descriptions for the position profile.

  Currently, only the `AdvertisementDetails` description is used.
  Other descriptions will be accepted but are ignored when determining the relevance of suggestion.

  The job category suggestion algorithm is designed to work with the complete advertisement details as they would appear on the final job ad.
  Providing incomplete or placeholder text in this field may result in irrelevant suggestions.
  """
  positionFormattedDescriptions: [PositionFormattedDescriptionInput!]
}

"""
The input parameter for the `jobCategories` query.
"""
input JobCategories_PositionProfileInput {
  """
  An array of identifiers for the position's `Location`s.
  """
  positionLocation: [String!]!
}

"""
A physical location with a persistent identifier.
"""
type Location {
  """
  The identifier for the `Location`.
  """
  id: ObjectIdentifier!

  """
  The parent location.
  """
  parent: Location

  """
  An array of child locations.

  This is always `null` regardless of the existence of child locations.
  """
  children: [Location!] @deprecated(reason: "Not implemented.")

  """
  The location name, e.g. "Richmond".

  This name is ambiguous without the context of its parent location.
  """
  name: String!

  """
  The contextual name of the location, e.g. "Richmond VIC 3121 AU".

  This name is sufficient to unambiguously identify the location to a hirer.
  """
  contextualName: String!

  """
  The two-letter ISO 3166-1:2013 country code, in uppercase.
  """
  countryCode: String!

  """
  A list of currencies used within this location.

  Locations with unsupported currencies will default to a SEEK supported currency.

  At least one currency will be provided.

  As most countries only use a single currency, the first item in the array can be used to preselect the default currency in a job posting flow.
  """
  currencies: [Currency!]!
}

"""
A suggested location.
"""
type LocationSuggestion {
  """
  Location information of the choice.
  """
  location: Location!
}

"""
A geographical coordinate.
"""
type GeoLocation {
  """
  The latitude of the geographical location.
  """
  latitude: Float!

  """
  The longitude of the geographical location.
  """
  longitude: Float!
}

"""
A geographical coordinate.
"""
input GeoLocationInput {
  """
  The latitude of the geographical location.
  """
  latitude: Float!

  """
  The longitude of the geographical location.
  """
  longitude: Float!
}

"""
Pagination metadata for a result list.

This is compliant with the _Relay Cursor Connections Specification_:

<https://facebook.github.io/relay/graphql/connections.htm#sec-undefined.PageInfo>
"""
type PageInfo {
  """
  Whether there is a previous page of results at the time of retrieval.
  """
  hasPreviousPage: Boolean!

  """
  Whether there is a next page of results at the time of retrieval.
  """
  hasNextPage: Boolean!

  """
  An opaque string cursor for retrieving the previous page of results.
  """
  startCursor: String

  """
  An opaque string cursor for retrieving the next page of results.
  """
  endCursor: String
}

"""
The name of a person including a breakdown of name components.
"""
type PersonName {
  """
  The formatted name of a person, as it would be written out together.

  This field is redacted and a static 'redacted' text is returned when a candidate or job application is deleted.
  This redaction is limited to candidate data objects within SEEK optimized apply.
  """
  formattedName: String!

  """
  The given name of a person, if provided.

  This field is redacted and a static 'redacted' text is returned when a candidate or job application is deleted.
  This redaction is limited to candidate data objects within SEEK optimized apply.
  """
  given: String

  """
  The family name (or surname) of a person, if provided.

  This field is redacted and a null value is returned when a candidate or job application is deleted.
  This redaction is limited to candidate data objects within SEEK optimized apply.
  """
  family: String
}

"""
A reference to a person associated with an object.
"""
type SpecifiedPerson {
  """
  The person's name.
  """
  name: PersonName!

  """
  Methods of communication with the person.
  """
  communication: Communication!

  """
  The role of the person.

  Currently, two codes are defined:

  - `Recruiter` indicates a person recruiting for the position.
  - `HiringManager` indicates an employee that requested the position to be filled.
  """
  roleCode: String
}

"""
The name of a person associated with an object.
"""
input PersonNameInput {
  """
  The formatted name of a person, as it would be written out together.
  """
  formattedName: String!

  """
  The optional given name of a person.
  """
  given: String

  """
  The optional family name (or surname) of a person.
  """
  family: String
}

"""
A reference to a person associated with an object.
"""
input SpecifiedPersonInput {
  """
  The person's name.
  """
  name: PersonNameInput!

  """
  Methods of communication with the person.
  """
  communication: CommunicationInput!

  """
  The role of the person.

  Currently, two codes are defined:

  - `Recruiter` indicates a person recruiting for the position.
  - `HiringManager` indicates an employee that requested the position to be filled.

  This field is required when providing a position opening's `personContacts`.
  """
  roleCode: String
}

"""
A currency supported by the SEEK API.

This may be used when specifying a price or salary range.
"""
type Currency {
  """
  A three-letter ISO 4217 currency code, in uppercase.
  """
  code: String!
}

"""
The primary method of payment and the interval in which it is paid for a position.
"""
type PayType {
  """
  A code classifying the primary method of payment for a position.
  """
  basisCode: String!

  """
  A code classifying the interval the remuneration amounts are calculated over.
  """
  intervalCode: String!

  """
  A human-readable description of the pay type.
  """
  label: String!
}

"""
The overarching phase of the application or position.
"""
input SendSignal_PhaseInput {
  """
  The free-text label of the phase.

  Provide a name or description, e.g. `Interview`.
  """
  label: String!

  """
  The relative place of the phase in the overall process.
  """
  index: Int!

  """
  The total number of phases in the process.
  """
  total: Int!
}

"""
The granular status of the application or position.
"""
input SendSignal_StatusInput {
  """
  The free-text label of the status.

  Provide a name or description, e.g. `Final interview`.
  """
  label: String!

  """
  The relative place of the status in the overall process.
  """
  index: Int!

  """
  The total number of statuses in the process.
  """
  total: Int!
}

"""
A state of the application or position.
"""
input SendSignal_StateInput {
  """
  The granular status of the application or position.
  """
  status: SendSignal_StatusInput!

  """
  Whether the signal associates with a successful state.

  For application status updates, a value of `true` indicates the candidate was successful, and `false` indicates the candidate was unsuccessful.

  For position status updates, a value of `true` indicates the position was filled, and `false` indicates it was not (and will not).

  A `status` of null is treated as neither successful nor unsuccessful, meaning the application or position is still progressing.
  """
  success: Boolean

  """
  When the state took effect.
  """
  timestamp: DateTime!

  """
  The overarching phase that the status corresponds to.

  This can be omitted if your software does not model such a hierarchy.
  """
  phase: SendSignal_PhaseInput
}

"""
The input parameter for a signal representing the progression of a candidate or position through a recruitment process.
"""
input SendSignalInput {
  """
  The type of the signal to be sent.

  Currently, only `ApplicationComplete`, `ApplicationStatusUpdate` and `PositionStatusUpdate` are supported.
  """
  typeCode: String!

  """
  The unique tracking token.

  This token is generated by SEEK, and is associated with a given application.
  Do not re-use this field across multiple applications.

  Only applicable to application status updates for external applications.
  """
  token: String

  """
  The identifier of the original SEEK job application.

  Only applicable to application status updates for Optimised Apply.
  """
  candidateApplicationProfileId: String

  """
  The identifiers to correlate the signal back to the SEEK job ads.

  Only applicable to position signals.
  """
  positionProfileIds: [String!]

  """
  The current state of the application or position.
  """
  current: SendSignal_StateInput

  """
  The previous state of the application or position.

  This is an optional field, and can be omitted if your software does not track this information.
  """
  previous: SendSignal_StateInput

  """
  When the signal was generated.
  """
  timestamp: DateTime
}

"""
The input parameter for the `sendSignals` mutation.
"""
input SendSignalsInput {
  """
  The batch of signals to be sent.

  Up to 100 signals can be sent in a single request.
  """
  signals: [SendSignalInput!]!
}

"""
Advertisement branding details and images.

This can be associated with one or more `PositionProfile`s when they are created.

Branding images are visible to candidates searching for positions or viewing a job ad with this associated advertisement branding.
"""
type AdvertisementBranding {
  """
  The identifier for the `AdvertisementBranding`.
  """
  id: ObjectIdentifier!

  """
  The advertisement branding name.
  """
  name: String!

  """
  A list of images associated with the advertisement branding.
  """
  images: [AdvertisementBrandingImage!]!

  """
  The organization that has the advertisement branding.
  """
  hirer: HiringOrganization!
}

"""
An advertisement branding in a paginated list.
"""
type AdvertisementBrandingEdge {
  """
  The opaque cursor to advertisement branding.

  This can be used as a subsequent pagination argument.
  """
  cursor: String!

  """
  The actual advertisement branding.
  """
  node: AdvertisementBranding!
}

"""
A visual representation of advertisement branding.
"""
type AdvertisementBrandingImage {
  """
  The type of advertisement branding image.

  Currently, two codes are defined:

  - `OriginalLogo` indicates the original advertisement branding logo image from which other logo images are derived.

  - `CoverImage` indicates an optional cover image to be displayed on the top of job ads.
  """
  typeCode: String!

  """
  The URL of the advertisement branding image.

  This can be retrieved to visually identify an advertisement branding in a partner system.
  """
  url: String!
}

"""
A page of advertisement brandings for a hirer.
"""
type AdvertisementBrandingsConnection {
  """
  The page of advertisement brandings and their corresponding cursors.

  This list may be empty.
  """
  edges: [AdvertisementBrandingEdge!]!

  """
  The pagination metadata for this page of advertisement brandings.
  """
  pageInfo: PageInfo!

  """
  A deep link to the SEEK employer website where the hirer can manage their brands.

  This field accepts browser tokens that include the `query:organizations` scope.
  """
  brandManagementUrl: WebUrl
}

"""
A person who applied for a position at a company.

If the same person applies to multiple hirers they will have distinct `Candidate` objects created.
"""
type Candidate {
  """
  The identifier for the `Candidate`.

  This is unique for a given hirer & person.
  """
  documentId: ObjectIdentifier!

  """
  The candidate's primary email address.

  The value is only set for candidates with `CandidateSource` value `PartnerUpload`.
  Values will be unique within a given hirer.
  """
  seekPrimaryEmailAddress: String

  """
  Instructions on how this candidate should be distributed.
  """
  distributionGuidelines: DistributionGuidelines!

  """
  Information to identify the person,
  including their name and methods of communicating with the person.
  """
  person: CandidatePerson!

  """
  The list of profiles associated with with the candidate.

  Uploaded candidates sourced from the `uploadCandidate` mutation have a single profile.

  SEEK candidates have a profile per submitted application.
  This field exposes up to 10 recent applications submitted by the candidate.

  We recommend querying specific applications by their `CandidateProfile.profileId`s for the Optimised Apply use case.

  This field is redacted and an empty/filtered list is returned when a candidate or job application is deleted.
  """
  profiles(
    """
    The upper limit of candidate profiles to return from the end of the list.

    Defaults to 10.
    Excess candidate profiles will be trimmed from the start of the list.
    """
    last: Int
  ): [CandidateProfile!]!
}

"""
Information about a person not specific to a candidate profile.
"""
type CandidatePerson {
  """
  The person's name.
  """
  name: PersonName!

  """
  Methods of communication with the person.
  """
  communication: Communication!
}

"""
Information about a person not specific to a candidate profile.
"""
input CandidatePersonInput {
  """
  The person's name.
  """
  name: PersonNameInput!

  """
  Methods of communication with the person.
  """
  communication: CommunicationInput!
}

"""
The role of an attachment within a profile.
"""
enum SeekAttachmentRole {
  """
  A resume or CV.
  """
  RESUME @deprecated(reason: "Use `Attachment.seekRoleCode` instead.")

  """
  A cover letter specific to a position opening.
  """
  COVER_LETTER @deprecated(reason: "Use `Attachment.seekRoleCode` instead.")

  """
  A document supporting a position-specific selection criteria.
  """
  SELECTION_CRITERIA
    @deprecated(reason: "Use `Attachment.seekRoleCode` instead.")
}

"""
The details of a position that a candidate is associated with.
"""
type AssociatedPositionOpening {
  """
  The identifier for the `PositionOpening`.

  This is included for HR-JSON compatibility;
  GraphQL users should use the `positionOpening` nested object instead.
  """
  positionOpeningId: ObjectIdentifier!

  """
  The position opening that the candidate is associated with.
  """
  positionOpening: PositionOpening!

  """
  A resource identifier for the position.

  This identifies the relevant position profile within the position opening.
  It can be matched with the position profile `positionUri` field.

  - For candidate application profiles from the Optimised Apply use case,
    this is the public web URL of the posted job ad.

  - For purchased candidate profiles from the Proactive Sourcing use case,
    this is the object identifier of the relevant unposted position profile.
  """
  positionUri: String!

  """
  Whether the candidate applied to this associated position.

  This is `false` for purchased profiles from the Proactive Sourcing use case.
  """
  candidateAppliedIndicator: Boolean
}

"""
A profile attachment stored at an external URL.
"""
type Attachment {
  """
  The identifier for the `Attachment`.

  This is unique across all attachments.
  """
  id: ObjectIdentifier!

  """
  A download URL for the attachment.

  This URL accepts partner tokens or browser tokens that include the `download:candidate-profile-attachments` scope.
  This is guaranteed to be an absolute URL with an origin of `https://graphql.seek.com`.
  """
  url: String!

  """
  An array of human readable descriptions of the attachment.

  Resumes & cover letters can be programmatically identified using the  `seekRoleCode` field.
  """
  descriptions: [String!]!

  """
  The role of the attachment within a profile.

  Currently, three codes are defined:

  - `CoverLetter` is used for a candidate's cover letter for a specific position.
  - `Resume` is used for a candidate's resume or CV.
  - `SelectionCriteria` is used for a candidate's selection criteria for a specific position.

  Additional documents will have a `null` role code.
  They can be distinguished by their free text `descriptions`.
  """
  seekRoleCode: String

  """
  The role of the attachment within a profile.
  """
  seekRole: SeekAttachmentRole
    @deprecated(reason: "Use `seekRoleCode` instead.")
}

"""
The details about a person's tenure within the position.
"""
type PositionHistory {
  """
  The start date of the position.

  This may only contain the year and month, e.g. `2019-01`.
  """
  start: HistoryDate!

  """
  The end date of the position if known.

  This may only contain the year and month, e.g. `2020-01`.
  """
  end: HistoryDate

  """
  Whether the person is still working at the organization, if known.
  """
  current: Boolean

  """
  The title that the person held for this position.
  """
  title: String!

  """
  An array of descriptions of the person's responsibilities, skills and achievements in the position.
  """
  descriptions: [String!]!
}

"""
The details of a person's employment, work, or relevant experience.
"""
type EmployerHistory {
  """
  The specific organization to which the person held positions.
  """
  organization: Organization!

  """
  An array of positions that the person held or is currently holding at the organization.
  """
  positionHistories: [PositionHistory!]!
}

"""
The details of a student's degree.
"""
type EducationDegree {
  """
  The name of the degree.
  """
  name: String!

  """
  The granted status of the degree.

  Currently, two statuses are defined:

  - `InProgress` indicates the student is still completing the degree.
  - `Granted` indicates the degree has been granted to the student.
  """
  degreeGrantedStatus: String!

  """
  The date the degree was granted or is expected to be granted.

  This may only contain a year and optional month, e.g. `2020` or `2020-06`.
  If the date isn't known this will be `null`.
  """
  date: HistoryDate
}

"""
The details documenting a person's attendance at an educational institution.
"""
type EducationAttendance {
  """
  The institution the person attended.
  """
  institution: Organization!

  """
  An array of degrees which were awarded or are being undertaken at the institution.
  """
  educationDegrees: [EducationDegree!]!

  """
  An array of additional free text descriptions of the person's attendance at the institution.

  This typically includes activities, honours, awards or specialities achieved during their study.
  """
  descriptions: [String!]!
}

"""
A skill or competency asserted by the candidate.
"""
type PersonCompetency {
  """
  The human readable name of the competency.
  """
  competencyName: String!
}

"""
A certification or license held by the candidate.
"""
type Certification {
  """
  The free text name of the certification.
  """
  name: String!

  """
  The time period for which the certification is effective.

  This will be `null` if the expiration date is unknown or not provided.
  """
  effectiveTimePeriod: EffectiveTimePeriod

  """
  The date the certification was last issued.

  This will be `null` if the issue date is unknown or not provided.
  """
  issued: HistoryDate

  """
  The date the certification was issued.

  This will be `null` if the issue date is unknown or not provided.
  """
  issueDate: HistoryDate @deprecated(reason: "Use `issued` instead.")

  """
  The organization that issued the certification.
  """
  issuingAuthority: Organization

  """
  An array of human readable descriptions of the certification.

  This can contain multiple descriptions from two different sources:

  1. Free text descriptions provided by the candidate.
  2. Human readable descriptions automatically generated from SEEK's internal structured data.
  """
  descriptions: [String!]!
}

"""
A source from which the candidate was obtained from.
"""
type CandidateSource {
  """
  Free text description of the source.
  """
  name: String!

  """
  The grouping that the source falls under.

  Currently, three types are defined:

  - `PartnerUpload` indicates that the candidate was uploaded to SEEK from a partner system.
  - `SeekApplication` indicates that the candidate applied for a position on the SEEK job board.
  - `SeekPurchase` indicates that the candidate profile was purchased from SEEK Talent Search.
  """
  type: String!
}

"""
Structured information about a candidate in relation to a particular position.
"""
type CandidateProfile {
  """
  The `Candidate` that this profile relates to.

  This contains the candidate's personal details along with all their profiles for the same hirer.
  """
  candidate: Candidate!

  """
  The identifier for the `CandidateProfile`.

  This profile can be queried at any time by passing this identifier string to `candidateProfile`.
  """
  profileId: ObjectIdentifier!

  """
  The date & time the candidate was associated with the position.
  """
  createDateTime: DateTime!

  """
  An array of position openings associated with this candidate profile.
  """
  associatedPositionOpenings: [AssociatedPositionOpening!]!
    @deprecated(reason: "Use `associatedPositionProfile` instead.")

  """
  The primary position profile for this candidate profile.

  - For candidate applications, this is the job ad that the candidate applied to.
  - For purchased profiles, this is the unposted position profile that the purchase is scoped to.
  - For uploaded candidate profiles, this is always `null`.
  - For Apply with SEEK candidate profiles, this is always `null`.
  """
  associatedPositionProfile: PositionProfile

  """
  An array of attachments related to the candidate's profile.

  This field is redacted and an empty list is returned when a candidate or job application is deleted.
  For Apply with SEEK candidate profiles, only attachments with the `seekRoleCode` of `Resume` are returned.
  """
  attachments: [Attachment!]!

  """
  An array representing the employment history of the candidate.

  This field is redacted and an empty list is returned when a candidate or job application is deleted.
  """
  employment: [EmployerHistory!]!

  """
  An array representing the education history of the candidate.

  This field is redacted and an empty list is returned when a candidate or job application is deleted.
  """
  education: [EducationAttendance!]!

  """
  An array of skills or competencies of the candidate.

  This field is redacted and an empty list is returned when a candidate or job application is deleted.
  """
  qualifications: [PersonCompetency!]!

  """
  An array of certifications and licenses the candidate holds.

  This field is redacted and an empty list is returned when a candidate or job application is deleted.
  """
  certifications: [Certification!]!

  """
  An array of sources from which the candidate was obtained from.
  """
  candidateSources: [CandidateSource!]!

  """
  An array representing the candidate's preferences in an ideal position.

  This is only available for uploaded candidate profiles and Apply with SEEK candidate profiles.
  For candidate applications & purchased profiles this will be an empty list.
  """
  positionPreferences: [PositionPreference!]!

  """
  The date & time the candidate profile was last updated.
  """
  updateDateTime: DateTime!

  """
  An array of executable actions linked to the candidate profile.
  """
  seekActions: [CandidateProcessAction!]!

  """
  The workflow process history of the candidate.

  A maximum of 20 process history items can be returned in a single page.
  Additional items can be queried using a pagination cursor.

  This is null for non-uploaded candidates.
  """
  seekProcessHistory(
    """
    An opaque cursor to the earlier bounding page.

    Resulting items will _succeed_ this cursor.
    """
    after: String

    """
    The upper limit of items to return from the start of the list.

    Defaults to 10 if `first` is not specified.
    Excess items will be trimmed from the end of the list.
    """
    first: Int
  ): CandidateProcessHistoryItemConnection

  """
  The completed candidate submission for the position profile's questionnaire.

  For Apply with SEEK candidate profiles, this is always `null`.

  This field is redacted for a deleted candidate or job application.
  """
  seekQuestionnaireSubmission: ApplicationQuestionnaireSubmission

  """
  The candidate's availability for a new position.

  This is only available for Apply with SEEK candidate profiles.
  For candidate applications, uploaded candidate profiles & purchased profiles this will be `null`.
  """
  personAvailability: PersonAvailability

  """
  A personal summary provided by the candidate.

  This is only available for Apply with SEEK candidate profiles.
  For candidate applications, uploaded candidate profiles & purchased profiles this will be `null`.
  """
  executiveSummary: String
}

"""
The availability of a candidate for a new position.
"""
type PersonAvailability {
  """
  The notice period that is required.
  """
  noticePeriodMeasure: PersonAvailability_NoticePeriodMeasure!

  """
  An indicator that the person could start immediately.

  Derived from `noticePeriodMeasure`. Values other than 0 weeks will result in `false`.
  """
  immediateStartIndicator: Boolean!
}

"""
The notice period that is required.
"""
type PersonAvailability_NoticePeriodMeasure {
  """
  The number of weeks of notice a candidate needs to provide to their current employer to switch jobs.
  """
  value: Int!

  """
  The unit of measure from the UN/CEFACT Unit Codes for Trade (2014, Annex II & Annex III).

  Currently, this will always be "WEE", representing "week".
  """
  unitCode: String!
}

"""
The candidate profile that the process history item belongs to.
"""
input CreateCandidateProcessHistoryItem_CandidateProfileInput {
  """
  The identifier for the `CandidateProfile` that the `CandidateProcessHistoryItem` relates to.
  """
  profileId: String!
}

"""
The candidate process history to create.
"""
input CreateCandidateProcessHistoryItem_CandidateProcessHistoryItemInput {
  """
  An identifier to ensure that multiple candidate process history items are not created on retries.

  The identifier must be unique within the given candidate profile.
  The same identifier must be provided when retrying after create failures.
  """
  idempotencyId: String!

  """
  The date & time the action was executed.
  """
  actionDate: DateTime!

  """
  The executed action that constitutes this history item.

  This action may or may not trigger a change in the status of the underlying process.
  For example, an action may be to add a note against a candidate's profile,
  which has no material effect on the stage through the application process.
  """
  action: CandidateProcessActionInput!

  """
  The current status of the underlying process.

  For example, a candidate in an application process may progress through a series of statuses like applied, interviewed, offered, hired.
  """
  status: CandidateProcessStatusInput

  """
  The position profile that the history item relates to.

  This is null for interactions that are not specific to an individual position,
  such as a general interview with a recruiter.
  """
  positionProfile: CandidateProcessHistoryItem_PositionProfileInput

  """
  The underlying source for the item.

  For example, items related to an application process would note the job board or other channel that sourced the application.

  This is required if `positionProfile` is specified.
  """
  seekSource: SeekProcessHistoryItemSourceInput

  """
  The parties that executed the action.
  """
  primaryParties: [CandidateProcessPartyInput!]!
}

"""
The input parameter for the `createCandidateProcessHistoryItem` mutation.
"""
input CreateCandidateProcessHistoryItemInput {
  """
  The details of the `CandidateProfile` that the `CandidateProcessHistoryItem` belongs to.
  """
  candidateProfile: CreateCandidateProcessHistoryItem_CandidateProfileInput!

  """
  The details of the `CandidateProcessHistoryItem` to be added to the `CandidateProfile`.
  """
  candidateProcessHistoryItem: CreateCandidateProcessHistoryItem_CandidateProcessHistoryItemInput!
}

"""
The conflict result for the `createCandidateProcessHistoryItem` mutation.
"""
type CreateCandidateProcessHistoryItemPayload_Conflict {
  """
  The details of the conflicting `CandidateProcessHistoryItem`.
  """
  conflictingCandidateProcessHistoryItem: CandidateProcessHistoryItem!

  """
  The `CandidateProfile` that the `CandidateProcessHistoryItem` belongs to.
  """
  candidateProfile: CandidateProfile!
}

"""
The success result for the `createCandidateProcessHistoryItem` mutation.
"""
type CreateCandidateProcessHistoryItemPayload_Success {
  """
  The details of the `CandidateProcessHistoryItem` created.
  """
  candidateProcessHistoryItem: CandidateProcessHistoryItem!

  """
  The `CandidateProfile` that the `CandidateProcessHistoryItem` belongs to.
  """
  candidateProfile: CandidateProfile!
}

"""
The response from the `createCandidateProcessHistoryItem` mutation.
"""
union CreateCandidateProcessHistoryItemPayload =
  | CreateCandidateProcessHistoryItemPayload_Conflict
  | CreateCandidateProcessHistoryItemPayload_Success

"""
The details of the `CandidateProcessHistoryItem` to be deleted.
"""
input DeleteCandidateProcessHistoryItem_CandidateProcessHistoryItemInput {
  """
  The identifier for the `CandidateProcessHistoryItem` to be deleted.
  """
  id: String!
}

"""
The input parameter for the `deleteCandidateProcessHistoryItem` mutation.
"""
input DeleteCandidateProcessHistoryItemInput {
  """
  The details of the  `CandidateProcessHistoryItem` to be deleted.
  """
  candidateProcessHistoryItem: DeleteCandidateProcessHistoryItem_CandidateProcessHistoryItemInput!
}

"""
The response from the `deleteCandidateProcessHistoryItem` mutation.
"""
type DeleteCandidateProcessHistoryItemPayload {
  """
  The details of the `CandidateProcessHistoryItem` that was deleted.
  """
  candidateProcessHistoryItem: CandidateProcessHistoryItem!
}

"""
The details of the uploaded candidate to be deleted.
"""
input DeleteUploadedCandidate_CandidateInput {
  """
  The identifier for the uploaded `Candidate` to be deleted.
  """
  documentId: String!
}

"""
The input parameter for the `deleteUploadedCandidate` mutation.
"""
input DeleteUploadedCandidateInput {
  """
  The details of the uploaded `Candidate` to be deleted.
  """
  candidate: DeleteUploadedCandidate_CandidateInput!
}

"""
The response from the `deleteUploadedCandidate` mutation.
"""
type DeleteUploadedCandidatePayload {
  """
  The details of the uploaded candidate that was deleted.

  The uploaded candidate profile is available in the `profiles` field.
  """
  candidate: Candidate!
}

"""
Instructions on how a candidate should be distributed.
"""
type DistributionGuidelines {
  """
  The SEEK products that the candidate may be surfaced in.

  Currently, two codes are defined:

  - `CandidateManagement` indicates that the candidate may be viewed in SEEK Candidate Management.
  - `ProactiveSourcing` indicates that the candidate may be viewed in SEEK Talent Search.
  """
  seekProductCodes: [String!]!
}

"""
Instructions on how a candidate should be distributed.
"""
input DistributionGuidelinesInput {
  """
  The SEEK products that the candidate may be surfaced in.

  Currently, one code is defined:

  - `ProactiveSourcing` indicates that the candidate may be viewed in SEEK Talent Search.
  """
  seekProductCodes: [String!]!
}

"""
Position context for requests scoped to an applicant list.
"""
input PositionContextInput {
  """
  Stable identifier of the position in your software.
  """
  seekPartnerPositionId: String!

  """
  The title of the position.
  """
  positionTitle: String!

  """
  The URI of the job ad.
  """
  positionUri: String!

  """
  The ID of the SEEK job ad, if known.
  """
  profileId: String

  """
  The location of the position.
  """
  positionLocation: String

  """
  The job categories for the position.
  """
  jobCategories: String
}

"""
A candidate's preferences in an ideal position.
"""
type PositionPreference {
  """
  An array of locations which are preferred by the candidate.

  The locations are ordered in descending preference.
  """
  locations: [PreferredLocation!]!

  """
  The preference for the types of work for a position.

  This is only available for Apply with SEEK candidate profiles.
  For candidate applications, uploaded candidate profiles & purchased profiles this will be an empty list.

  Currently, four codes are defined:

  - `Casual` indicates a casual position.
  - `ContractTemp` indicates a fixed-length contract position.
  - `FullTime` indicates a full-time position.
  - `PartTime` indicates a part-time position.
  """
  seekAnzWorkTypeCodes: [String!]!

  """
  The preference for remuneration (including salary or hourly rate, benefits, etc.) for a position in a given country.

  This is only available for Apply with SEEK candidate profiles.
  For candidate applications, uploaded candidate profiles & purchased profiles this will be an empty list.
  """
  seekSalaryExpectations: [SeekSalaryExpectation!]!
}

"""
The preference for remuneration (including salary or hourly rate, benefits) for a position.
"""
type SeekSalaryExpectation {
  """
  The two-letter ISO 3166-1:2013 country code, in uppercase.
  """
  countryCode: String!

  """
  The amount the candidate expects to receive.
  """
  amount: RemunerationAmount!

  """
  The interval the remuneration amounts are calculated over.

  Currently three interval codes are defined:

  - `Hour` is used to express hourly rates.
  - `Month` is used to express monthly salaries.
  - `Year` is used to express annual salaries or commissions.
  """
  intervalCode: String!
}

"""
A candidate's preferences in an ideal position.
"""
input PositionPreferenceInput {
  """
  An array of locations which are preferred by the candidate.

  The locations are ordered in descending preference.

  A maximum of 5 locations may be provided.
  """
  locations: [PreferredLocationInput!]!
}

"""
A candidate's preferences in the location of a position.
"""
type PreferredLocation {
  """
  The address that represents the preferred location.
  """
  referenceLocation: Address!
}

"""
A candidate's preferences in the location of a position.
"""
input PreferredLocationInput {
  """
  The address that represents the preferred location.
  """
  referenceLocation: AddressInput!
}

"""
An action that can be executed as part of a workflow process.
"""
type CandidateProcessAction {
  """
  The code of the action.

  Currently, the following action codes are defined:

  - `AgencySubmission`
  - `CandidateWorkflowTransition`
  - `Document`
  - `Email`
  - `Note`
  - `PhoneCall`
  - `PostPlacementActivity`
  - `Screening`
  - `StatusChange`
  - `VerificationActivity`
  - `Other`

  For profile actions, one action code is defined:

  - `ViewProfile` indicates that the URL is used to view the candidate's profile.
  """
  code: String!

  """
  The free-form description of the action.
  """
  description: String

  """
  A short human-readable name for the workflow step.

  This is non-null for a process history action of an uploaded candidate.
  """
  name: String

  """
  A deep link to the action.
  """
  seekUrl: WebUrl
}

"""
An action that can be executed as part of a workflow process.
"""
input CandidateProcessActionInput {
  """
  The code of the action.

  For process history actions, the following action codes are defined:

  - `AgencySubmission`
  - `CandidateWorkflowTransition`
  - `Document`
  - `Email`
  - `Note`
  - `PhoneCall`
  - `PostPlacementActivity`
  - `Screening`
  - `StatusChange`
  - `VerificationActivity`
  - `Other`

  For profile actions, one action code is defined:

  - `ViewProfile` indicates that the URL is used to view the candidate's profile.
  """
  code: String!

  """
  The free-form description of the action.

  This is required for a process history action of an uploaded candidate.

  This field has a maximum length of 1,000 characters.
  """
  description: String

  """
  A short human-readable name of the workflow process.

  This is required for a process history action of an uploaded candidate.
  """
  name: String

  """
  A deep link to the action.

  This is required for a profile action of an uploaded candidate.

  The `url` field has a maximum length of 1,000 characters.
  """
  seekUrl: WebUrlInput
}

"""
A status of a workflow process.
"""
type CandidateProcessStatus {
  """
  The code of the status of the underlying process.

  Currently, the following status codes are defined:

  - `AgencyShortlist`
  - `AgencySubmission`
  - `Application`
  - `HirerShortlist`
  - `Interview`
  - `Offer`
  - `ReferenceCheck`
  - `Successful`
  - `TestingOrQualificationsCheck`
  - `Unsuccessful`
  """
  code: String!
}

"""
A status of a workflow process.
"""
input CandidateProcessStatusInput {
  """
  The code of the status of the underlying process.

  Currently, the following status codes are defined:

  - `AgencyShortlist`
  - `AgencySubmission`
  - `Application`
  - `HirerShortlist`
  - `Interview`
  - `Offer`
  - `ReferenceCheck`
  - `Successful`
  - `TestingOrQualificationsCheck`
  - `Unsuccessful`
  """
  code: String!
}

"""
A party that contributes to a workflow process.

This may be a person, organization, or both.
"""
type CandidateProcessParty {
  """
  The individual person that is contributing to the workflow process.
  """
  person: SpecifiedPerson

  """
  The organization that is contributing to the workflow process.
  """
  organization: Organization
}

"""
A party that contributes to a workflow process.

This may be a person, organization, or both.
"""
input CandidateProcessPartyInput {
  """
  The individual person that is contributing to the workflow process.
  """
  person: SpecifiedPersonInput

  """
  The organization that is contributing to the workflow process.
  """
  organization: OrganizationInput
}

"""
The source system for the process history item.
"""
type SeekProcessHistoryItemSource {
  """
  Free text description of the source.
  """
  name: String!
}

"""
The source system for the process history item.
"""
input SeekProcessHistoryItemSourceInput {
  """
  Free text description of the source.
  """
  name: String!
}

"""
A single item in a `CandidateProfile`'s workflow process history.
"""
type CandidateProcessHistoryItem {
  """
  The identifier for the `CandidateProcessHistoryItem`.
  """
  id: ObjectIdentifier!

  """
  The `CandidateProfile` that the `CandidateProcessHistoryItem` relates to.
  """
  candidateProfile: CandidateProfile!

  """
  The date & time the action was executed.
  """
  actionDate: DateTime!

  """
  The executed action that constitutes this history item.

  This action may or may not trigger a change in the status of the underlying process.
  For example, an action may be to add a note against a candidate's profile,
  which has no material effect on the stage through the application process.
  """
  action: CandidateProcessAction!

  """
  The current status of the underlying process.

  For example, a candidate in an application process may progress through a series of statuses like applied, interviewed, offered, hired.
  """
  status: CandidateProcessStatus

  """
  The position profile that the history item relates to.

  This is null for interactions that are not specific to an individual position,
  such as a general interview with a recruiter.
  It will also be null where an unknown `profileId` was provided or the position profile has since been deleted.
  """
  positionProfile: PositionProfile

  """
  The underlying source for the item.

  For example, items related to an application process would note the job board or other channel that sourced the application.
  """
  seekSource: SeekProcessHistoryItemSource

  """
  The parties that executed the action.
  """
  primaryParties: [CandidateProcessParty!]!
}

"""
A single item in a `CandidateProfile`'s workflow process history.
"""
input CandidateProcessHistoryItemInput {
  """
  The date & time the action was executed.
  """
  actionDate: DateTime!

  """
  The executed action that constitutes this history item.

  This action may or may not trigger a change in the status of the underlying process.
  For example, an action may be to add a note against a candidate's profile,
  which has no material effect on the stage through the application process.
  """
  action: CandidateProcessActionInput!

  """
  The current status of the underlying process.

  For example, a candidate in an application process may progress through a series of statuses like applied, interviewed, offered, hired.
  """
  status: CandidateProcessStatusInput

  """
  The position profile that the history item relates to.

  This is null for interactions that are not specific to an individual position,
  such as a general interview with a recruiter.
  """
  positionProfile: CandidateProcessHistoryItem_PositionProfileInput

  """
  The underlying source for the item.

  For example, items related to an application process would note the job board or other channel that sourced the application.

  This is required if `positionProfile` is specified.
  """
  seekSource: SeekProcessHistoryItemSourceInput

  """
  The parties that executed the action.

  At least one party is required for a process history item of an uploaded candidate.
  A maximum of 10 primary parties may be provided.
  """
  primaryParties: [CandidateProcessPartyInput!]!
}

"""
A position profile associated with a workflow process.
"""
input CandidateProcessHistoryItem_PositionProfileInput {
  """
  The identifier for the `PositionProfile`.
  """
  profileId: String!
}

"""
A candidate process history item in a paginated list.
"""
type CandidateProcessHistoryItemEdge {
  """
  The opaque cursor to the candidate process history item.

  This can be used as a subsequent pagination argument.
  """
  cursor: String!

  """
  The actual candidate process history item.
  """
  node: CandidateProcessHistoryItem!
}

"""
A page of candidate process history items.
"""
type CandidateProcessHistoryItemConnection {
  """
  The page of candidate process history items and their corresponding cursors.

  This list may be empty.
  """
  edges: [CandidateProcessHistoryItemEdge!]!

  """
  The pagination metadata for this page of candidate process history items.
  """
  pageInfo: PageInfo!
}

"""
Input for a single candidate in a summaries request.
"""
input SeekCandidateSummaryInput {
  """
  Email address to check for candidate profile.
  """
  email: String!
}

"""
Input for the seekCandidateSummaries query.
"""
input SeekCandidateSummariesInput {
  """
  Candidates to retrieve summaries for (1-100 per request).
  """
  candidates: [SeekCandidateSummaryInput!]!

  """
  Position context. Required in applicant list context; may be omitted in candidate list context.
  """
  position: PositionContextInput
}

"""
The image URLs for the identity verified icon in both light and dark mode.
"""
type SeekPassIdentityVerifiedImages {
  """
  The URL of the light mode image to indicate candidate identity has been verified.
  This should be the default image used if no preference is indicated.
  """
  light: String!

  """
  The URL of the dark mode image to indicate candidate identity has been verified.
  """
  dark: String!
}

"""
The icon to indicate candidate identity has been verified, including the images and tooltip label.
"""
type SeekPassIdentityIcon {
  """
  The tooltip label to indicate candidate identity has been verified.
  """
  tooltipLabel: String!

  """
  The icon images to indicate candidate identity has been verified.
  """
  images: SeekPassIdentityVerifiedImages!
}

"""
Identity information from SEEK Pass.
"""
type SeekPassIdentity {
  """
  The formatted name of the candidate.
  """
  formattedName: String

  """
  Whether the candidate's identity has been verified.
  """
  verifiedIndicator: Boolean!

  """
  The icon image and tooltip to indicate candidate identity has been verified.
  This field should be included if verifiedIndicator is true, and omitted if verifiedIndicator is false.
  """
  icon: SeekPassIdentityIcon
}

"""
The icon images to indicate profile existence.
"""
type SeekProfileSummaryIconImages {
  """
  The icon image to indicate profile exists.
  """
  profileIcon: String!
}

"""
Profile existence information.
"""
type SeekProfileSummary {
  """
  Whether a candidate profile exists.
  """
  exists: Boolean!

  """
  The icon images to indicate profile existence.
  """
  images: SeekProfileSummaryIconImages
}

"""
Summary candidate profile information.
"""
type SeekCandidateProfileSummary {
  """
  The primary email address of the candidate.
  """
  seekPrimaryEmailAddress: String!

  """
  SEEK Pass identity information for the candidate.
  """
  identity: SeekPassIdentity!

  """
  Profile existence information.
  """
  seekProfile: SeekProfileSummary!
}

"""
Response payload containing candidate profile summaries.
"""
type SeekCandidateSummaries {
  """
  Array of candidate profile summaries.
  """
  seekCandidateSummaries: [SeekCandidateProfileSummary!]!
}

"""
The input parameter for the `toggleVerifyWithSeekPass` mutation.
"""
input ToggleVerifyWithSeekPassInput {
  """
  The identifier for the `HiringOrganization` to toggle verification enrollment for.
  """
  hiringOrganization: HiringOrganizationInput!

  """
  Whether verification with SEEK Pass should be enabled for this hirer.

  - `true`: Enable verification (opt in)
  - `false`: Disable verification (opt out)
  """
  enabled: Boolean!
}

"""
The response from the `toggleVerifyWithSeekPass` mutation.
"""
type ToggleVerifyWithSeekPassPayload {
  """
  The identifier for the `HiringOrganization` that was updated.
  """
  hirerId: String!

  """
  Whether verification with SEEK Pass is now enabled for this hirer-partner combination.
  """
  enabled: Boolean!
}

"""
The details of the `CandidateProcessHistoryItem` to be updated.
"""
input UpdateCandidateProcessHistoryItem_CandidateProcessHistoryItemInput {
  """
  The identifier for the `CandidateProcessHistoryItem` to be updated.
  """
  id: String!

  """
  The date & time the action was executed.
  """
  actionDate: DateTime!

  """
  The executed action that constitutes this history item.

  This action may or may not trigger a change in the status of the underlying process.
  For example, an action may be to add a note against a candidate's profile,
  which has no material effect on the stage through the application process.
  """
  action: CandidateProcessActionInput!

  """
  The current status of the underlying process.

  For example, a candidate in an application process may progress through a series of statuses like applied, interviewed, offered, hired.
  """
  status: CandidateProcessStatusInput

  """
  The position profile that the history item relates to.

  This is null for interactions that are not specific to an individual position,
  such as a general interview with a recruiter.
  """
  positionProfile: CandidateProcessHistoryItem_PositionProfileInput

  """
  The underlying source for the item.

  For example, items related to an application process would note the job board or other channel that sourced the application.

  This is required if `positionProfile` is specified.
  """
  seekSource: SeekProcessHistoryItemSourceInput

  """
  The parties that executed the action.
  """
  primaryParties: [CandidateProcessPartyInput!]!
}

"""
The input parameter for the `updateCandidateProcessHistoryItem` mutation.
"""
input UpdateCandidateProcessHistoryItemInput {
  """
  The details of the  `CandidateProcessHistoryItem` to be updated.
  """
  candidateProcessHistoryItem: UpdateCandidateProcessHistoryItem_CandidateProcessHistoryItemInput!
}

"""
The response from the `updateCandidateProcessHistoryItem` mutation.
"""
type UpdateCandidateProcessHistoryItemPayload {
  """
  The details of the `CandidateProcessHistoryItem` that was updated.
  """
  candidateProcessHistoryItem: CandidateProcessHistoryItem!
}

"""
The details of the uploaded candidate to be updated.
"""
input UpdateUploadedCandidatePerson_CandidateInput {
  """
  The identifier for the uploaded `Candidate` to be updated.
  """
  documentId: String!

  """
  The personal details of the uploaded candidate to be updated.

  At least one email address is required in `communication`,
  and one of the provided values must match `seekPrimaryEmailAddress`.

  If no value is provided for physical addresses in `communication` it will be treated as an empty list and will remove any previously uploaded addresses.
  """
  person: CandidatePersonInput!

  """
  The candidate's primary email address.

  The value must match one of the candidate's email addresses.

  This field has a maximum length of 255 bytes in UTF-8 encoding.
  """
  seekPrimaryEmailAddress: String!
}

"""
The input parameter for the `updateUploadedCandidatePerson` mutation.
"""
input UpdateUploadedCandidatePersonInput {
  """
  The details of the uploaded candidate to be updated.
  """
  candidate: UpdateUploadedCandidatePerson_CandidateInput!
}

"""
The conflict result for the `updateUploadedCandidatePerson` mutation.
"""
type UpdateUploadedCandidatePersonPayload_Conflict {
  """
  The details of the conflicting candidate.
  """
  conflictingCandidate: Candidate!
}

"""
The success result for the `updateUploadedCandidatePerson` mutation.
"""
type UpdateUploadedCandidatePersonPayload_Success {
  """
  The details of the uploaded candidate that was updated.
  """
  candidate: Candidate!
}

"""
The response from the `updateUploadedCandidatePerson` mutation.
"""
union UpdateUploadedCandidatePersonPayload =
  | UpdateUploadedCandidatePersonPayload_Conflict
  | UpdateUploadedCandidatePersonPayload_Success

"""
The details of the uploaded candidate profile to be updated.
"""
input UpdateUploadedCandidateProfileActions_CandidateProfileInput {
  """
  The identifier for the uploaded `CandidateProfile` to be updated.
  """
  profileId: String!

  """
  Any associated actions that can be performed on the candidate profile.

  Only one of each type of action is permitted for the candidate profile.
  Currently, only a `ViewProfile` action type is defined to provide a URL to view the candidate's profile.
  """
  seekActions: [CandidateProcessActionInput!]!
}

"""
The input parameter for the `updateUploadedCandidateProfileActions` mutation.
"""
input UpdateUploadedCandidateProfileActionsInput {
  """
  The details of the uploaded candidate profile to be updated.
  """
  candidateProfile: UpdateUploadedCandidateProfileActions_CandidateProfileInput!
}

"""
The response from the `updateUploadedCandidateProfileActions` mutation.
"""
type UpdateUploadedCandidateProfileActionsPayload {
  """
  The details of the uploaded candidate profile that was updated.

  The uploaded candidate is available in the `candidate` field.
  """
  candidateProfile: CandidateProfile!
}

"""
The details of the uploaded candidate profile to be updated.
"""
input UpdateUploadedCandidateProfileDates_CandidateProfileInput {
  """
  The identifier for the uploaded `CandidateProfile` to be updated.
  """
  profileId: String!

  """
  When the candidate profile was first created in the partner system.

  Expects a RFC 3339 compliant date time.
  Millisecond precision is optional, and set to 0 if not provided.
  """
  createDateTime: DateTime!

  """
  When the candidate profile was last updated in the partner system.

  Expects a RFC 3339 compliant date time.
  Millisecond precision is optional, and set to 0 if not provided.
  """
  updateDateTime: DateTime!
}

"""
The input parameter for the `updateUploadedCandidateProfileDates` mutation.
"""
input UpdateUploadedCandidateProfileDatesInput {
  """
  The details of the uploaded candidate profile to be updated.
  """
  candidateProfile: UpdateUploadedCandidateProfileDates_CandidateProfileInput!
}

"""
The response from the `updateUploadedCandidateProfileDates` mutation.
"""
type UpdateUploadedCandidateProfileDatesPayload {
  """
  The details of the uploaded candidate profile that was updated.

  The uploaded candidate is available in the `candidate` field.
  """
  candidateProfile: CandidateProfile!
}

"""
The details of the uploaded candidate profile to be updated.
"""
input UpdateUploadedCandidateProfilePositionPreferences_CandidateProfileInput {
  """
  The identifier for the uploaded `CandidateProfile` to be updated.
  """
  profileId: String!

  """
  The candidate's preferences in an ideal position.

  `ProactiveSourcing` candidates may have 0–1 position preferences.
  """
  positionPreferences: [PositionPreferenceInput!]!
}

"""
The input parameter for the `updateUploadedCandidateProfilePositionPreferences` mutation.
"""
input UpdateUploadedCandidateProfilePositionPreferencesInput {
  """
  The details of the uploaded candidate profile to be updated.
  """
  candidateProfile: UpdateUploadedCandidateProfilePositionPreferences_CandidateProfileInput!
}

"""
The response from the `updateUploadedCandidateProfilePositionPreferences` mutation.
"""
type UpdateUploadedCandidateProfilePositionPreferencesPayload {
  """
  The details of the uploaded candidate profile that was updated.

  The uploaded candidate is available in the `candidate` field.
  """
  candidateProfile: CandidateProfile!
}

"""
The details of the candidate to be uploaded.
"""
input UploadCandidate_CandidateInput {
  """
  The candidate's primary email address.

  The value must match one of the candidate's email addresses.
  Duplicate uploads will result in a `BAD_USER_INPUT` error.

  This field has a maximum length of 255 bytes in UTF-8 encoding.
  """
  seekPrimaryEmailAddress: String!

  """
  Instructions on how this candidate should be distributed.

  Currently, one product code is defined for uploaded candidates:

  - `ProactiveSourcing` indicates that the candidate may be viewed in SEEK Talent Search.
  """
  distributionGuidelines: DistributionGuidelinesInput!

  """
  The personal details of the candidate to be uploaded.

  At least one email address is required in `communication`,
  and one of the provided values must match `seekPrimaryEmailAddress`.
  """
  person: CandidatePersonInput!
}

"""
The details of the candidate profile to be uploaded.
"""
input UploadCandidate_CandidateProfileInput {
  """
  When the candidate profile was first created in the partner system.

  Expects a RFC 3339 compliant date time.
  Millisecond precision is optional, and set to 0 if not provided.
  """
  createDateTime: DateTime!

  """
  When the candidate profile was last updated in the partner system.

  Expects a RFC 3339 compliant date time.
  Millisecond precision is optional, and set to 0 if not provided.
  """
  updateDateTime: DateTime!

  """
  The workflow process history of the candidate.

  A maximum of 20 process history items may be provided on initial upload.
  """
  seekProcessHistory: [CandidateProcessHistoryItemInput!]!

  """
  The candidate's preferences in an ideal position.

  `ProactiveSourcing` candidates may have 0–1 position preferences.
  """
  positionPreferences: [PositionPreferenceInput!]!

  """
  Any associated actions that can be performed on the candidate profile.

  Only one of each type of action is permitted for the candidate profile.
  Currently, only a `ViewProfile` action type is defined to provide a URL to view the candidate's profile.
  """
  seekActions: [CandidateProcessActionInput!]!
}

"""
Details of the `HiringOrganization` that submitted the candidate profile.
"""
input UploadCandidate_HiringOrganizationInput {
  """
  The identifier for the `HiringOrganization` that submitted the candidate profile.
  """
  id: String!
}

"""
The input parameter for the `uploadCandidate` mutation.
"""
input UploadCandidateInput {
  """
  The details of the `Candidate` to be uploaded.
  """
  candidate: UploadCandidate_CandidateInput!

  """
  The details of the `CandidateProfile` to be uploaded.
  """
  candidateProfile: UploadCandidate_CandidateProfileInput!

  """
  The details of the `HiringOrganization` that submitted the candidate profile.
  """
  hiringOrganization: UploadCandidate_HiringOrganizationInput!
}

"""
The conflict result for the `uploadCandidate` mutation.
"""
type UploadCandidatePayload_Conflict {
  """
  The details of the conflicting candidate.

  The conflicting candidate profile is available in the `profiles` field.
  """
  conflictingCandidate: Candidate!

  """
  The details of the hiring organization that uploaded the candidate and their profile.
  """
  hiringOrganization: HiringOrganization!
}

"""
The success result for the `uploadCandidate` mutation.
"""
type UploadCandidatePayload_Success {
  """
  The details of the uploaded candidate.

  The uploaded candidate profile is available in the `profiles` field.
  """
  candidate: Candidate!

  """
  The details of the process history items uploaded alongside the candidate.

  The upload operation is atomic;
  if you receive an `UploadCandidatePayload_Success` payload,
  all process history items were successfully uploaded.
  Input order is preserved to allow your software to record the `id` assigned to each item.
  """
  candidateProcessHistoryItems: [CandidateProcessHistoryItem!]!

  """
  The details of the hiring organization that uploaded the candidate and their profile.
  """
  hiringOrganization: HiringOrganization!
}

"""
The response from the `uploadCandidate` mutation.
"""
union UploadCandidatePayload =
  | UploadCandidatePayload_Conflict
  | UploadCandidatePayload_Success

"""
The details of the candidate to be verified.
"""
input VerifyCandidate_CandidateInput {
  """
  The candidate's email address.
  """
  email: String!
}

"""
The input parameter for the `verifyCandidate` mutation.
"""
input VerifyCandidateInput {
  """
  The details of the `HiringOrganization` requesting the verification.
  """
  hiringOrganization: HiringOrganizationInput!

  """
  The details of the candidate to be verified.
  """
  candidate: VerifyCandidate_CandidateInput!

  """
  The details of the position associated with the verification request.
  """
  position: PositionContextInput!
}

"""
The images for the verification button, with different background colours.
"""
type VerifyButtonImages {
  """
  The URL of the image, e.g. `https://integration.seek.com/assets/verify-with-seek-pass/button-images/en/standard-accent.svg`.
  """
  accent: String!

  """
  The URL of the image, e.g. `https://integration.seek.com/assets/verify-with-seek-pass/button-images/en/standard-dark.svg`.
  """
  dark: String!

  """
  The URL of the image, e.g. `https://integration.seek.com/assets/verify-with-seek-pass/button-images/en/standard-neutral.svg`.
  """
  light: String!
}

"""
The button details for initiating verification.
"""
type Button {
  """
  The label text for the verification button.
  """
  label: String!

  """
  The URI to navigate to when the button is clicked.
  """
  uri: String!

  """
  The image options for the verification button.
  """
  images: VerifyButtonImages!
}

"""
The result for the `verifyCandidate` mutation when the candidate is already verified.
"""
type VerifyCandidatePayload_Complete {
  """
  The verification status code.
  """
  verificationStatusCode: VerificationStatusCode!
}

"""
The result for the `verifyCandidate` mutation when verification is pending.
"""
type VerifyCandidatePayload_Pending {
  """
  The verification status code.
  """
  verificationStatusCode: VerificationStatusCode!

  """
  The external identifier for the verification request.
  """
  externalId: String!

  """
  The URI to redirect to for verification.
  """
  redirectUri: String!

  """
  The button details for initiating verification.
  """
  button: Button!
}

"""
The result for the `verifyCandidate` mutation when verification is disabled by the hirer.
"""
type VerifyCandidatePayload_HirerDisabled {
  """
  The verification status code.
  """
  verificationStatusCode: VerificationStatusCode!
}

"""
The result for the `verifyCandidate` mutation when verification is expired.
"""
type VerifyCandidatePayload_Expired {
  """
  The verification status code.
  """
  verificationStatusCode: VerificationStatusCode!
}

"""
The response from the `verifyCandidate` mutation.
"""
union VerifyCandidatePayload =
  | VerifyCandidatePayload_Complete
  | VerifyCandidatePayload_Pending
  | VerifyCandidatePayload_HirerDisabled
  | VerifyCandidatePayload_Expired

"""
Verification status code for the `verifyCandidate` mutation.
"""
enum VerificationStatusCode {
  """
  The candidate has already been verified.
  """
  COMPLETE

  """
  Verification is pending and requires user action.
  """
  PENDING

  """
  Verification is disabled by the hiring organization for this partner.
  """
  HIRER_DISABLED

  """
  Verification is expired.
  """
  EXPIRED
}

"""
The information of the draft position required for the posting.
"""
input PostPositionFromDraft_DraftPositionInput {
  """
  The identifier for the draft that the `PositionProfile` to be posted from.
  """
  id: String!

  """
  An array of identifiers for the `HiringOrganization`s that have the position.

  This must match the `positionOrganizations` field that was provided when creating the draft.
  """
  positionOrganizations: [String!]!

  """
  An identifier to ensure that multiple ads are not created on retries.

  A unique identifier needs to be generated by your software for each position profile.
  The same identifier must be provided when retrying after create failures.
  Your identifiers are isolated from and will not conflict with those generated by other recruitment software providers.
  """
  idempotencyId: String!
}

"""
The information of the draft position required for the posting.
"""
input UpdatePositionProfileFromDraft_DraftPositionInput {
  """
  The identifier for the draft that the `PositionProfile` to be updated from.
  """
  id: String!

  """
  An array of identifiers for the `HiringOrganization`s that have the position.

  This must match the `positionOrganizations` field that was provided when creating the draft.
  """
  positionOrganizations: [String!]!

  """
  An identifier to ensure that multiple ads are not created on retries.

  A unique identifier needs to be generated by your software for each position profile.
  The same identifier must be provided when retrying after create failures.
  Your identifiers are isolated from and will not conflict with those generated by other recruitment software providers.
  """
  idempotencyId: String!
}

"""
The information of the position profile to be updated.
"""
input UpdatePositionProfileFromDraft_PositionProfileInput {
  """
  The ID of the position profile to be updated.
  """
  id: String!
}

"""
A signal that an action has been performed on the SEEK platform that may be of interest to an integration partner.

Events can be delivered via:

- Webhook, using the `createWebhookSubscription` mutation
- GraphQL polling, using the paginated `events` query
"""
interface Event {
  """
  The identifier for the `Event`.
  """
  id: ObjectIdentifier!

  """
  The scheme ID of the event.

  Currently, only the `seekAnz` and `seekAnzPublicTest` schemes emit events.
  """
  schemeId: String!

  """
  The type of event.

  See `Event` implementations for a list of supported values.
  """
  typeCode: String!

  """
  The date & time the event was created.

  This is commonly linked to the creation of an object that can be retrieved from the SEEK API.

  The data source for this field differs by event type and scheme.
  This field has weak ordering guarantees, so it should not be used as a pagination argument.
  """
  createDateTime: DateTime!

  """
  A page of webhook attempts for the current event matching the specified criteria.

  A maximum of 100 webhook attempts can be returned in a single page.
  Additional webhook attempts can be queried using a pagination cursor.

  The result list is returned in ascending creation date & time order.
  It starts from the earliest known attempt if no pagination arguments are provided.
  """
  webhookAttempts(
    """
    An opaque cursor to the earlier bounding webhook attempt.

    Resulting webhook attempts will _succeed_ this cursor.
    """
    after: String

    """
    An opaque cursor to the later bounding webhook attempt.

    Resulting webhook attempts will _precede_ this cursor.
    """
    before: String

    """
    The upper limit of webhook attempts to return from the start of the list.

    Defaults to 10 if neither `first` nor `last` are specified.
    Excess webhook attempts will be trimmed from the end of the list.

    `first` and `last` cannot be specified in the same query.
    """
    first: Int

    """
    The upper limit of webhook attempts to return from the end of the list.

    Excess webhook attempts will be trimmed from the start of the list.

    `first` and `last` cannot be specified in the same query.
    """
    last: Int

    """
    The additional `WebhookAttempt`-specific criteria to filter by.
    """
    filter: WebhookAttemptsFilterInput
  ): WebhookAttemptsConnection!
}

"""
The event signaling that a candidate has applied for a `PositionOpening`.

A candidate may apply for the same position opening more than once.
Each application will trigger a new event with a distinct `id`.
"""
type CandidateApplicationCreatedEvent implements Event {
  """
  The identifier for the `Event`.
  """
  id: ObjectIdentifier!

  """
  The scheme ID of the event.

  Currently, only the `seekAnz` and `seekAnzPublicTest` schemes emit `CandidateApplicationCreated` events.
  """
  schemeId: String!

  """
  The type of event, i.e. `CandidateApplicationCreated`.
  """
  typeCode: String!

  """
  The date & time the application was accepted from the candidate.

  This field has weak ordering guarantees, so it should not be used as a pagination argument.
  """
  createDateTime: DateTime!

  """
  The identifier for the specific `CandidateProfile` associated with the application.

  This can be used to retrieve structured candidate details with the `candidateProfile` query.
  """
  candidateApplicationProfileId: String!

  """
  The identifier for the `Candidate` that applied for the position opening.
  """
  candidateId: String!

  """
  The `Candidate` that applied for the position opening.

  This will include the candidate's personal details along with all application profiles for a single hirer.

  This field is only accessible while you have an active `ApplicationExport` relationship with the hirer.
  If this relationship has been removed, it will return null along with a `FORBIDDEN` error.
  """
  candidate: Candidate

  """
  The `CandidateProfile` associated with the application.

  This field is only accessible while you have an active `ApplicationExport` relationship with the hirer.
  If this relationship has been removed, it will return null along with a `FORBIDDEN` error.
  """
  candidateApplicationProfile: CandidateProfile

  """
  A page of webhook attempts for the current event matching the specified criteria.

  A maximum of 100 webhook attempts can be returned in a single page.
  Additional webhook attempts can be queried using a pagination cursor.

  The result list is returned in ascending creation date & time order.
  It starts from the earliest known attempt if no pagination arguments are provided.
  """
  webhookAttempts(
    """
    An opaque cursor to the earlier bounding webhook attempt.

    Resulting webhook attempts will _succeed_ this cursor.
    """
    after: String

    """
    An opaque cursor to the later bounding webhook attempt.

    Resulting webhook attempts will _precede_ this cursor.
    """
    before: String

    """
    The upper limit of webhook attempts to return from the start of the list.

    Defaults to 10 if neither `first` nor `last` are specified.
    Excess webhook attempts will be trimmed from the end of the list.

    `first` and `last` cannot be specified in the same query.
    """
    first: Int

    """
    The upper limit of webhook attempts to return from the end of the list.

    Excess webhook attempts will be trimmed from the start of the list.

    `first` and `last` cannot be specified in the same query.
    """
    last: Int

    """
    The additional `WebhookAttempt`-specific criteria to filter by.
    """
    filter: WebhookAttemptsFilterInput
  ): WebhookAttemptsConnection!
}

"""
The event signaling that a `Position` failed to post or update.
"""
type PositionNotPostedEvent implements Event {
  """
  The identifier for the `Event`.
  """
  id: ObjectIdentifier!

  """
  The scheme ID of the event.

  Currently, only the `seekAnz` and `seekAnzPublicTest` schemes emit `PositionNotPosted` events.
  """
  schemeId: String!

  """
  The type of event, i.e. `PositionNotPosted`.
  """
  typeCode: String!

  """
  The date & time the position failed to post or update.

  This field has weak ordering guarantees, so it should not be used as a pagination argument.
  """
  createDateTime: DateTime!

  """
  The identifier for the reason why the position failed to post or update.
  """
  reasonId: String!

  """
  The identifier for the `Position` that failed to post or update.
  """
  seekPartnerPositionId: String!

  """
  A page of webhook attempts for the current event matching the specified criteria.

  A maximum of 100 webhook attempts can be returned in a single page.
  Additional webhook attempts can be queried using a pagination cursor.

  The result list is returned in ascending creation date & time order.
  It starts from the earliest known attempt if no pagination arguments are provided.
  """
  webhookAttempts(
    """
    An opaque cursor to the earlier bounding webhook attempt.

    Resulting webhook attempts will _succeed_ this cursor.
    """
    after: String

    """
    An opaque cursor to the later bounding webhook attempt.

    Resulting webhook attempts will _precede_ this cursor.
    """
    before: String

    """
    The upper limit of webhook attempts to return from the start of the list.

    Defaults to 10 if neither `first` nor `last` are specified.
    Excess webhook attempts will be trimmed from the end of the list.

    `first` and `last` cannot be specified in the same query.
    """
    first: Int

    """
    The upper limit of webhook attempts to return from the end of the list.

    Excess webhook attempts will be trimmed from the start of the list.

    `first` and `last` cannot be specified in the same query.
    """
    last: Int

    """
    The additional `WebhookAttempt`-specific criteria to filter by.
    """
    filter: WebhookAttemptsFilterInput
  ): WebhookAttemptsConnection!
}

"""
The event signaling that a posted `PositionProfile` has been closed.
"""
type PositionProfileClosedEvent implements Event {
  """
  The identifier for the `Event`.
  """
  id: ObjectIdentifier!

  """
  The scheme ID of the event.

  Currently, only the `seekAnz` and `seekAnzPublicTest` schemes emit `PositionProfileClosed` events.
  """
  schemeId: String!

  """
  The type of event, i.e. `PositionProfileClosed`.
  """
  typeCode: String!

  """
  The date & time the `PositionProfile` was closed.

  `PositionProfile`s are closed automatically when they reach their `PostingInstruction.end` date.
  They can also be closed early using the `closePostedPositionProfile` mutation.

  This field has weak ordering guarantees, so it should not be used as a pagination argument.
  """
  createDateTime: DateTime!

  """
  The identifier for the `PositionProfile` that was closed.
  """
  positionProfileId: String!

  """
  The `PositionProfile` that was closed.

  This may return null if the `PositionProfile` has been closed for an extended period of time.

  This field is only accessible while you have an active `ApplicationExport` or `JobPosting` relationship with the hirer.
  If these relationships have been removed, it will return null along with a `FORBIDDEN` error.
  """
  positionProfile: PostedPositionProfile

  """
  A page of webhook attempts for the current event matching the specified criteria.

  A maximum of 100 webhook attempts can be returned in a single page.
  Additional webhook attempts can be queried using a pagination cursor.

  The result list is returned in ascending creation date & time order.
  It starts from the earliest known attempt if no pagination arguments are provided.
  """
  webhookAttempts(
    """
    An opaque cursor to the earlier bounding webhook attempt.

    Resulting webhook attempts will _succeed_ this cursor.
    """
    after: String

    """
    An opaque cursor to the later bounding webhook attempt.

    Resulting webhook attempts will _precede_ this cursor.
    """
    before: String

    """
    The upper limit of webhook attempts to return from the start of the list.

    Defaults to 10 if neither `first` nor `last` are specified.
    Excess webhook attempts will be trimmed from the end of the list.

    `first` and `last` cannot be specified in the same query.
    """
    first: Int

    """
    The upper limit of webhook attempts to return from the end of the list.

    Excess webhook attempts will be trimmed from the start of the list.

    `first` and `last` cannot be specified in the same query.
    """
    last: Int

    """
    The additional `WebhookAttempt`-specific criteria to filter by.
    """
    filter: WebhookAttemptsFilterInput
  ): WebhookAttemptsConnection!
}

"""
The event signaling that a `PositionProfile` has been posted.
"""
type PositionProfilePostedEvent implements Event {
  """
  The identifier for the `Event`.
  """
  id: ObjectIdentifier!

  """
  The scheme ID of the event.

  Currently, only the `seekAnz` and `seekAnzPublicTest` schemes emit `PositionProfilePosted` events.
  """
  schemeId: String!

  """
  The type of event, i.e. `PositionProfilePosted`.
  """
  typeCode: String!

  """
  The date & time the `PositionProfile` was considered as successfully posted inside of SEEK's internal systems.

  This field has weak ordering guarantees, so it should not be used as a pagination argument.
  """
  createDateTime: DateTime!

  """
  The identifier for the `PositionProfile` that was posted.
  """
  positionProfileId: String!

  """
  The `PositionProfile` that was posted.

  This may return null if the `PositionProfile` has been closed for an extended period of time.

  This field is only accessible while you have an active `ApplicationExport` or `JobPosting` relationship with the hirer.
  If these relationships have been removed, it will return null along with a `FORBIDDEN` error.
  """
  positionProfile: PostedPositionProfile

  """
  A page of webhook attempts for the current event matching the specified criteria.

  A maximum of 100 webhook attempts can be returned in a single page.
  Additional webhook attempts can be queried using a pagination cursor.

  The result list is returned in ascending creation date & time order.
  It starts from the earliest known attempt if no pagination arguments are provided.
  """
  webhookAttempts(
    """
    An opaque cursor to the earlier bounding webhook attempt.

    Resulting webhook attempts will _succeed_ this cursor.
    """
    after: String

    """
    An opaque cursor to the later bounding webhook attempt.

    Resulting webhook attempts will _precede_ this cursor.
    """
    before: String

    """
    The upper limit of webhook attempts to return from the start of the list.

    Defaults to 10 if neither `first` nor `last` are specified.
    Excess webhook attempts will be trimmed from the end of the list.

    `first` and `last` cannot be specified in the same query.
    """
    first: Int

    """
    The upper limit of webhook attempts to return from the end of the list.

    Excess webhook attempts will be trimmed from the start of the list.

    `first` and `last` cannot be specified in the same query.
    """
    last: Int

    """
    The additional `WebhookAttempt`-specific criteria to filter by.
    """
    filter: WebhookAttemptsFilterInput
  ): WebhookAttemptsConnection!
}

"""
The event signaling that a `PositionProfile` has been updated.
"""
type PositionProfileUpdatedEvent implements Event {
  """
  The identifier for the `Event`.
  """
  id: ObjectIdentifier!

  """
  The scheme ID of the event.

  Currently, only the `seekAnz` and `seekAnzPublicTest` schemes emit `PositionProfileUpdated` events.
  """
  schemeId: String!

  """
  The type of event, i.e. `PositionProfileUpdated`.
  """
  typeCode: String!

  """
  The date & time the `PositionProfile` was considered as successfully updated inside of SEEK's internal systems.

  This field has weak ordering guarantees, so it should not be used as a pagination argument.
  """
  createDateTime: DateTime!

  """
  The identifier for the `PositionProfile` that was updated.
  """
  positionProfileId: String!

  """
  The `PositionProfile` that was updated.

  This may return null if the `PositionProfile` has been closed for an extended period of time.

  This field is only accessible while you have an active `ApplicationExport` or `JobPosting` relationship with the hirer.
  If these relationships have been removed, it will return null along with a `FORBIDDEN` error.
  """
  positionProfile: PostedPositionProfile

  """
  A page of webhook attempts for the current event matching the specified criteria.

  A maximum of 100 webhook attempts can be returned in a single page.
  Additional webhook attempts can be queried using a pagination cursor.

  The result list is returned in ascending creation date & time order.
  It starts from the earliest known attempt if no pagination arguments are provided.
  """
  webhookAttempts(
    """
    An opaque cursor to the earlier bounding webhook attempt.

    Resulting webhook attempts will _succeed_ this cursor.
    """
    after: String

    """
    An opaque cursor to the later bounding webhook attempt.

    Resulting webhook attempts will _precede_ this cursor.
    """
    before: String

    """
    The upper limit of webhook attempts to return from the start of the list.

    Defaults to 10 if neither `first` nor `last` are specified.
    Excess webhook attempts will be trimmed from the end of the list.

    `first` and `last` cannot be specified in the same query.
    """
    first: Int

    """
    The upper limit of webhook attempts to return from the end of the list.

    Excess webhook attempts will be trimmed from the start of the list.

    `first` and `last` cannot be specified in the same query.
    """
    last: Int

    """
    The additional `WebhookAttempt`-specific criteria to filter by.
    """
    filter: WebhookAttemptsFilterInput
  ): WebhookAttemptsConnection!
}

"""
The event signaling that a `CandidateProfile` has been purchased.
"""
type CandidateProfilePurchasedEvent implements Event {
  """
  The identifier for the `Event`.
  """
  id: ObjectIdentifier!

  """
  The scheme ID of the event.

  Currently, only the `seekAnz` and `seekAnzPublicTest` schemes emit `CandidateProfilePurchased` events.
  """
  schemeId: String!

  """
  The type of event, i.e. `CandidateProfilePurchased`.
  """
  typeCode: String!

  """
  The date & time the `CandidateProfile` was purchased.

  This field has weak ordering guarantees, so it should not be used as a pagination argument.
  """
  createDateTime: DateTime!

  """
  The identifier for the `CandidateProfile` that was purchased.
  """
  candidateProfileId: String!

  """
  The `CandidateProfile` that was purchased.

  This field is only accessible while you have an active `ProactiveSourcing` relationship with the hirer.
  If this relationship has been removed, it will return null along with a `FORBIDDEN` error.
  """
  candidateProfile: CandidateProfile

  """
  A page of webhook attempts for the current event matching the specified criteria.

  A maximum of 100 webhook attempts can be returned in a single page.
  Additional webhook attempts can be queried using a pagination cursor.

  The result list is returned in ascending creation date & time order.
  It starts from the earliest known attempt if no pagination arguments are provided.
  """
  webhookAttempts(
    """
    An opaque cursor to the earlier bounding webhook attempt.

    Resulting webhook attempts will _succeed_ this cursor.
    """
    after: String

    """
    An opaque cursor to the later bounding webhook attempt.

    Resulting webhook attempts will _precede_ this cursor.
    """
    before: String

    """
    The upper limit of webhook attempts to return from the start of the list.

    Defaults to 10 if neither `first` nor `last` are specified.
    Excess webhook attempts will be trimmed from the end of the list.

    `first` and `last` cannot be specified in the same query.
    """
    first: Int

    """
    The upper limit of webhook attempts to return from the end of the list.

    Excess webhook attempts will be trimmed from the start of the list.

    `first` and `last` cannot be specified in the same query.
    """
    last: Int

    """
    The additional `WebhookAttempt`-specific criteria to filter by.
    """
    filter: WebhookAttemptsFilterInput
  ): WebhookAttemptsConnection!
}

"""
The event signaling that a hirer relationship has been added or removed.
"""
type HirerRelationshipChangedEvent implements Event {
  """
  The identifier for the `Event`.
  """
  id: ObjectIdentifier!

  """
  The scheme ID of the event.

  Currently, only the `seekAnz` and `seekAnzPublicTest` schemes emit `HirerRelationshipChanged` events.
  """
  schemeId: String!

  """
  The type of event, i.e. `HirerRelationshipChanged`.
  """
  typeCode: String!

  """
  The date & time the hirer relationship was changed.

  This field has weak ordering guarantees, so it should not be used as a pagination argument.
  """
  createDateTime: DateTime!

  """
  The identifier for the hiring organization for whom the relationship was changed.
  """
  hirerId: String!

  """
  The optional hiring organization for whom the relationship was changed.

  This field is only accessible while you have an active relationship with the hirer.
  If all relationships have been removed, it will return null along with a `FORBIDDEN` error.
  """
  hirer: HiringOrganization

  """
  A page of webhook attempts for the current event matching the specified criteria.

  A maximum of 100 webhook attempts can be returned in a single page.
  Additional webhook attempts can be queried using a pagination cursor.

  The result list is returned in ascending creation date & time order.
  It starts from the earliest known attempt if no pagination arguments are provided.
  """
  webhookAttempts(
    """
    An opaque cursor to the earlier bounding webhook attempt.

    Resulting webhook attempts will _succeed_ this cursor.
    """
    after: String

    """
    An opaque cursor to the later bounding webhook attempt.

    Resulting webhook attempts will _precede_ this cursor.
    """
    before: String

    """
    The upper limit of webhook attempts to return from the start of the list.

    Defaults to 10 if neither `first` nor `last` are specified.
    Excess webhook attempts will be trimmed from the end of the list.

    `first` and `last` cannot be specified in the same query.
    """
    first: Int

    """
    The upper limit of webhook attempts to return from the end of the list.

    Excess webhook attempts will be trimmed from the start of the list.

    `first` and `last` cannot be specified in the same query.
    """
    last: Int

    """
    The additional `WebhookAttempt`-specific criteria to filter by.
    """
    filter: WebhookAttemptsFilterInput
  ): WebhookAttemptsConnection!
}

"""
A page of events from a stream.
"""
type EventsConnection {
  """
  The page of events and their corresponding cursors.
  """
  edges: [EventEdge!]!

  """
  The pagination metadata for this page of events.
  """
  pageInfo: PageInfo!
}

"""
An event in a stream.
"""
type EventEdge {
  """
  The opaque cursor to the event in the stream.

  This can be used as a subsequent pagination argument.
  """
  cursor: String!

  """
  The actual event.
  """
  node: Event!

  """
  The date & time the event was added to the stream.

  Initial `afterDateTime` and `beforeDateTime` filters apply to this field.
  """
  streamDateTime: DateTime!
}

"""
The criteria to filter events by.

These are `Event`-specific extensions on top of the standard pagination arguments `before`, `after`, `first` and `last`.
"""
input EventsFilterInput {
  """
  The stream date & time that resulting events must succeed.

  This can be used to initiate the retrieval of paginated results.
  Subsequent queries should use the opaque cursors returned from `EventsConnection`.
  """
  afterDateTime: DateTime

  """
  The stream date & time that resulting events must precede.

  This can be used to initiate the retrieval of paginated results.
  Subsequent queries should use the opaque cursors returned from `EventsConnection`.
  """
  beforeDateTime: DateTime

  """
  Whether the event was successfully delivered via the specified webhook `subscriptionId`.

  This filter does not apply if `subscriptionId` is not specified.
  """
  deliveredIndicator: Boolean

  """
  The types of events to retrieve.

  See `Event` implementations for a list of supported values.
  """
  eventTypeCodes: [String!]

  """
  The subscription stream to retrieve events from.

  This can be used in combination with `deliveredIndicator` to identify events that were not successfully delivered through a particular webhook subscription.

  Omit this field to consume events solely through GraphQL polling.
  This will retrieve events from a persistent stream that is not associated with a webhook subscription.
  """
  subscriptionId: String
}

"""
The input parameter for the `createWebhookSubscription` mutation.
"""
input CreateWebhookSubscriptionInput {
  """
  The details of the webhook subscription to be created.
  """
  webhookSubscription: CreateWebhookSubscription_SubscriptionInput!
}

"""
The details of the webhook subscription to be created.
"""
input CreateWebhookSubscription_SubscriptionInput {
  """
  The type of event to subscribe to.

  See `Event` implementations for a list of supported values.
  """
  eventTypeCode: String!

  """
  The scheme ID of the event.

  Currently, only `seekAnz` and `seekAnzPublicTest` are supported.
  """
  schemeId: String!

  """
  The optional hirer ID to receive events from.

  By default webhook subscriptions will send events from all hirers the partner has access to.
  Providing a hirer ID will filter events to the specified hirer.
  """
  hirerId: String

  """
  The subscriber-owned URL where events will be sent to.
  """
  url: String!

  """
  The maximum number of events that will be sent in each HTTP request.

  This number must be between 1 and 10 inclusive. Defaults to 10.
  """
  maxEventsPerAttempt: Int

  """
  The algorithm for signing webhooks.

  Currently, one code is defined:

  - `SeekHmacSha512` indicates a HMAC SHA-512 hex digest will be attached to each request as a `Seek-Signature` header.

  A webhook's signature can be used to validate that the request originated from SEEK.

  Use a constant-time algorithm to validate the signature.
  Regular comparison methods like the `==` operator are susceptible to timing attacks.
  """
  signingAlgorithmCode: String!

  """
  The secret for signing webhooks.

  The secret should be a random string with high entropy that is not reused for any other purpose.
  It is used as the key to generate a message authentication code for each request.

  This field has a minimum length of 16 characters and a maximum length of 255 bytes in UTF-8 encoding.
  """
  secret: String!
}

"""
The response from the `createWebhookSubscription` mutation.
"""
union CreateWebhookSubscriptionPayload =
  | CreateWebhookSubscriptionPayload_Conflict
  | CreateWebhookSubscriptionPayload_Success

"""
The conflict result for the `createWebhookSubscription` mutation.

Webhook subscriptions must have a unique combination of `eventTypeCode`, `schemeId`, `url` & `hirerId` fields.
Attempting to create a duplicate webhook subscription will result in a conflict.
"""
type CreateWebhookSubscriptionPayload_Conflict {
  """
  The details of the conflicting webhook subscription.
  """
  conflictingWebhookSubscription: WebhookSubscription!
}

"""
The success result for the `createWebhookSubscription` mutation.
"""
type CreateWebhookSubscriptionPayload_Success {
  """
  The details of the created webhook subscription.
  """
  webhookSubscription: WebhookSubscription!
}

"""
The details of the webhook subscription delivery configuration to be updated.
"""
input UpdateWebhookSubscriptionDeliveryConfiguration_SubscriptionInput {
  """
  The identifier for the `WebhookSubscription`.
  """
  id: String!

  """
  The subscriber-owned URL where events will be sent to.
  """
  url: String!

  """
  The maximum number of events that will be sent in each HTTP request.

  This number must be between 1 and 10 inclusive. Defaults to 10.
  """
  maxEventsPerAttempt: Int
}

"""
The input parameter for the `updateWebhookSubscriptionDeliveryConfiguration` mutation.
"""
input UpdateWebhookSubscriptionDeliveryConfigurationInput {
  """
  The details of the webhook subscription to be updated.
  """
  webhookSubscription: UpdateWebhookSubscriptionDeliveryConfiguration_SubscriptionInput!
}

"""
The details of the webhook subscription signing configuration to be updated.
"""
input UpdateWebhookSubscriptionSigningConfiguration_SubscriptionInput {
  """
  The identifier for the `WebhookSubscription`.
  """
  id: String!

  """
  The algorithm for signing webhooks.

  Currently, one code is defined:

  - `SeekHmacSha512` indicates a HMAC SHA-512 hex digest will be attached to each request as a `Seek-Signature` header.

  A webhook's signature can be used to validate that the request originated from SEEK.

  Use a constant-time algorithm to validate the signature.
  Regular comparison methods like the `==` operator are susceptible to timing attacks.
  """
  signingAlgorithmCode: String!

  """
  The secret for signing webhooks.

  The secret should be a random string with high entropy that is not reused for any other purpose.
  It is used as the key to generate a message authentication code for each request.

  This field has a maximum length of 255 bytes in UTF-8 encoding.
  """
  secret: String
}

"""
The input parameter for the `updateWebhookSubscriptionSigningConfiguration` mutation.
"""
input UpdateWebhookSubscriptionSigningConfigurationInput {
  """
  The details of the webhook subscription to be updated.
  """
  webhookSubscription: UpdateWebhookSubscriptionSigningConfiguration_SubscriptionInput!
}

"""
The response from the `updateWebhookSubscriptionDeliveryConfiguration` mutation.
"""
union UpdateWebhookSubscriptionDeliveryConfigurationPayload =
  | UpdateWebhookSubscriptionDeliveryConfigurationPayload_Conflict
  | UpdateWebhookSubscriptionDeliveryConfigurationPayload_Success

"""
The success result for the `updateWebhookSubscriptionDeliveryConfiguration` mutation.
"""
type UpdateWebhookSubscriptionDeliveryConfigurationPayload_Success {
  """
  The details of the updated webhook subscription.
  """
  webhookSubscription: WebhookSubscription!
}

"""
The conflict result for the `updateWebhookSubscriptionDeliveryConfiguration` mutation.

Webhook subscriptions must have a unique combination of `eventTypeCode`, `schemeId`, `url` & `hirerId` fields.
Attempting to update a webhook subscription to match an existing subscription will result in a conflict.
"""
type UpdateWebhookSubscriptionDeliveryConfigurationPayload_Conflict {
  """
  The details of the conflicting webhook subscription.
  """
  conflictingWebhookSubscription: WebhookSubscription!
}

"""
The response from the `updateWebhookSubscriptionSigningConfiguration` mutation.
"""
type UpdateWebhookSubscriptionSigningConfigurationPayload {
  """
  The details of the updated webhook subscription.
  """
  webhookSubscription: WebhookSubscription!
}

"""
The input parameter for the `deleteWebhookSubscription` mutation.
"""
input DeleteWebhookSubscriptionInput {
  """
  The details of the webhook subscription to be deleted.
  """
  webhookSubscription: DeleteWebhookSubscription_SubscriptionInput!
}

"""
The details of the webhook subscription to be deleted.
"""
input DeleteWebhookSubscription_SubscriptionInput {
  """
  The identifier for the `WebhookSubscription`.
  """
  id: String!
}

"""
The input parameter for the `replayWebhookSubscription` mutation.
"""
input ReplayWebhookSubscriptionInput {
  """
  The details of the webhook subscription to be replayed.
  """
  webhookSubscription: ReplayWebhookSubscription_SubscriptionInput!

  """
  Additional fields to filter which events are to be replayed.

  This is an alternative to the `eventIds` argument, and allows replaying events within a designated time range.
  `eventIds` and `filter` cannot be specified in the same mutation.
  """
  filter: ReplayWebhookSubscription_FilterInput

  """
  List of event IDs to filter which events are to be replayed.

  This is an alternative to the `filter` argument, providing the ability to replay a list of specific events by their IDs.
  `eventIds` and `filter` cannot be specified in the same mutation.

  A maximum of 100 event IDs may be provided.
  """
  eventIds: [String!]
}

"""
The details of the webhook subscription to be replayed.
"""
input ReplayWebhookSubscription_SubscriptionInput {
  """
  The identifier for the `WebhookSubscription`.
  """
  id: String!
}

"""
Criteria used to decide which events will be replayed.
"""
input ReplayWebhookSubscription_FilterInput {
  """
  Whether to include all events, or only events that have failed to be delivered.
  """
  replayDeliveredEventsIndicator: Boolean!

  """
  The earliest event to replay.
  """
  createdAfterDateTime: DateTime!

  """
  The latest event to replay.
  """
  createdBeforeDateTime: DateTime!

  """
  The hirer ID to replay events for.

  If you omit this field, behaviour differs based on the scope of the webhook subscription:

  - A partner-scoped subscription defaults to replaying all related hirers.
  - A hirer-scoped subscription defaults to its corresponding `WebhookSubscription.hirerId`.
  """
  hirerId: String
}

"""
The response from the `deleteWebhookSubscription` mutation.
"""
type DeleteWebhookSubscriptionPayload {
  """
  The details of the deleted webhook subscription.
  """
  webhookSubscription: WebhookSubscription!
}

"""
The response from the `replayWebhookSubscription` mutation.
"""
type ReplayWebhookSubscriptionPayload {
  """
  The details of the webhook subscription to have its messages redelivered.
  """
  webhookSubscription: WebhookSubscription!
}

"""
An attempt to deliver a specific `Event` to a `WebhookSubscription`.
"""
type WebhookAttempt {
  """
  The identifier for the `WebhookAttempt`.
  """
  id: ObjectIdentifier!

  """
  The date & time that the webhook attempt was acknowledged or timed out.
  """
  createDateTime: DateTime!

  """
  The event that was attempted to be delivered.
  """
  event: Event!

  """
  The webhook subscription that was the target of the delivery attempt.

  This will be `null` if the subscription has since been deleted.
  """
  webhookSubscription: WebhookSubscription

  """
  The HTTP request containing the webhook attempt.
  """
  webhookRequest: WebhookRequest!
}

"""
A page of webhook attempts.
"""
type WebhookAttemptsConnection {
  """
  The page of webhook attempts and their corresponding cursors.
  """
  edges: [WebhookAttemptEdge!]!

  """
  The pagination metadata for this page of webhook attempts.
  """
  pageInfo: PageInfo!
}

"""
A webhook attempt in a paginated list.
"""
type WebhookAttemptEdge {
  """
  The opaque cursor to the webhook attempt.

  This can be used as a subsequent pagination argument.
  """
  cursor: String!

  """
  The actual webhook attempt.
  """
  node: WebhookAttempt!
}

"""
The criteria to filter webhook attempts by.

These are `WebhookAttempt`-specific extensions on top of the standard pagination arguments `before`, `after`, `first` and `last`.
"""
input WebhookAttemptsFilterInput {
  """
  The creation date & time that resulting webhook attempts must succeed.

  This can be used to initiate the retrieval of paginated results.
  Subsequent queries should use the opaque cursors returned from `WebhookAttemptsConnection`.
  """
  afterDateTime: DateTime

  """
  The creation date & time that resulting webhook attempts must precede.

  This can be used to initiate the retrieval of paginated results.
  Subsequent queries should use the opaque cursors returned from `WebhookAttemptsConnection`.
  """
  beforeDateTime: DateTime

  """
  The high-level HTTP result of the webhook attempts to retrieve.

  See the `WebhookRequest.descriptionCode` documentation for a list of possible description codes.

  If this is not provided then all attempts will be returned regardless of their result.
  """
  descriptionCodes: [String!]
}

"""
An HTTP request to a `WebhookSubscription`.

HTTP requests are associated with one or more `WebhookAttempt`s representing each `Event` in the request body.
"""
type WebhookRequest {
  """
  The identifier for the HTTP request.

  This is included in the request as an `X-Request-Id` custom header.
  """
  requestId: String!

  """
  The webhook subscription that was the target of the HTTP request.

  This will be `null` if the subscription has since been deleted.
  """
  webhookSubscription: WebhookSubscription

  """
  The HTTP status code returned by the subscription endpoint.

  When an HTTP response wasn't received a synthetic status code will be generated:

  - For `InvalidUrl` the status code will be set to `400`.
  - For `RequestTimeout` the status code will be set to `504`.
  """
  statusCode: Int!

  """
  The high-level description of the HTTP request's result.

  Currently, four codes are defined:

  - `BadResponse` indicates the subscription endpoint returned a non-2xx HTTP response.
  - `InvalidUrl` indicates the subscription URL did not pass validation.
  - `RequestTimeout` indicates the subscription endpoint took more than 10 seconds to respond.
  - `Success` indicates the subscription endpoint returned a 2xx HTTP response.
  """
  descriptionCode: String!

  """
  The latency of the HTTP request in milliseconds.

  This will be `null` if the request wasn't made (i.e. an `InvalidUrl` error occurred).
  """
  latencyMs: Int

  """
  The date & time the HTTP request occurred.

  This field has weak ordering guarantees, so it should not be used as a pagination argument.
  """
  createDateTime: DateTime!

  """
  The list of events that were attempted to be delivered in the request body.
  """
  attempts: [WebhookAttempt!]!
}

"""
The criteria to filter webhook requests by.

These are `WebhookRequest`-specific extensions on top of the standard pagination arguments `before`, `after`, `first` and `last`.
"""
input WebhookRequestFilterInput {
  """
  The creation date & time that resulting webhook requests must succeed.

  This can be used to initiate the retrieval of paginated results.
  Subsequent queries should use the opaque cursors returned from `WebhookRequestsConnection`.
  """
  afterDateTime: DateTime

  """
  The creation date & time that resulting webhook requests must precede.

  This can be used to initiate the retrieval of paginated results.
  Subsequent queries should use the opaque cursors returned from `WebhookRequestsConnection`.
  """
  beforeDateTime: DateTime

  """
  The high-level HTTP result of the webhook requests to retrieve.

  See the `WebhookRequest.descriptionCode` documentation for a list of possible description codes.

  If this is not provided then all requests will be returned regardless of their result.
  """
  descriptionCodes: [String!]
}

"""
A page of webhook requests.
"""
type WebhookRequestsConnection {
  """
  The page of webhook requests and their corresponding cursors.
  """
  edges: [WebhookRequestEdge!]!

  """
  The pagination metadata for this page of webhook requests.
  """
  pageInfo: PageInfo!
}

"""
A webhook request in a paginated list.
"""
type WebhookRequestEdge {
  """
  The opaque cursor to the webhook request.

  This can be used as a subsequent pagination argument.
  """
  cursor: String!

  """
  The actual webhook request.
  """
  node: WebhookRequest!
}

"""
A subscription for a given event type and scheme to be delivered via webhook.

Events are delivered in batches with a HTTP POST request to the specified subscription URL.
"""
type WebhookSubscription {
  """
  The identifier for the `WebhookSubscription`.
  """
  id: ObjectIdentifier!

  """
  The type of event.

  See `Event` implementations for a list of supported values.
  """
  eventTypeCode: String!

  """
  The scheme ID of the event.

  Currently, only `seekAnz` and `seekAnzPublicTest` are supported.
  """
  schemeId: String!

  """
  The optional hirer ID to receive events from.

  By default webhook subscriptions will send events from all hirers the partner has access to.
  A non-null `hirerId` indicates that this subscription is filtered to a single hirer.
  """
  hirerId: ObjectIdentifier

  """
  The optional hirer associated with this webhook subscription.

  This will only be accessible if there is an active relationship between the partner and hirer.

  By default webhook subscriptions will send events from all hirers the partner has access to.
  A non-null `hirer` field indicates that this subscription is filtered to a single hirer.
  """
  hirer: HiringOrganization

  """
  The subscriber-owned URL where events are sent to.
  """
  url: String!

  """
  The maximum number of events that will be sent in each HTTP request.

  This number is between 1 and 10 inclusive. Defaults to 10.
  """
  maxEventsPerAttempt: Int!

  """
  The algorithm for signing webhooks.

  Currently, one code is defined:

  - `SeekHmacSha512` indicates a HMAC SHA-512 hex digest will be attached to each request as a `Seek-Signature` header.

  A webhook's signature can be used to validate that the request originated from SEEK.

  Use a constant-time algorithm to validate the signature.
  Regular comparison methods like the `==` operator are susceptible to timing attacks.
  """
  signingAlgorithmCode: String!

  """
  The date & time the webhook subscription was created.

  Initial `afterDateTime` and `beforeDateTime` filters apply to this field.
  """
  createDateTime: DateTime!

  """
  The date & time the webhook subscription was last updated.
  """
  updateDateTime: DateTime!

  """
  A page of webhook requests for the subscription matching the specified criteria.

  A maximum of 100 webhook requests can be returned in a single page.
  Additional webhook requests can be queried using a pagination cursor.

  The result list is returned in ascending creation date & time order.
  It starts from the earliest known request if no pagination arguments are provided.
  """
  webhookRequests(
    """
    An opaque cursor to the earlier bounding webhook request.

    Resulting webhook requests will _succeed_ this cursor.
    """
    after: String

    """
    An opaque cursor to the later bounding webhook request.

    Resulting webhook requests will _precede_ this cursor.
    """
    before: String

    """
    The upper limit of webhook requests to return from the start of the list.

    Defaults to 10 if neither `first` nor `last` are specified.
    Excess webhook requests will be trimmed from the end of the list.

    `first` and `last` cannot be specified in the same query.
    """
    first: Int

    """
    The upper limit of webhook requests to return from the end of the list.

    Excess webhook requests will be trimmed from the start of the list.

    `first` and `last` cannot be specified in the same query.
    """
    last: Int

    """
    The additional `WebhookRequest`-specific criteria to filter by.
    """
    filter: WebhookRequestFilterInput
  ): WebhookRequestsConnection!

  """
  A page of replays for the current webhook subscription matching the specified criteria.

  A maximum of 100 webhook subscription replays can be returned in a single page.
  Additional subscription replays can be queried using a pagination cursor.

  The result list is returned in ascending creation date & time order.
  It starts from the earliest subscription replay if no pagination arguments are provided.
  """
  webhookSubscriptionReplays(
    """
    An opaque cursor to the earlier bounding webhook subscription replay.

    Resulting subscription replays will _succeed_ this cursor.
    """
    after: String

    """
    An opaque cursor to the later bounding webhook subscription replay.

    Resulting subscription replays will _precede_ this cursor.
    """
    before: String

    """
    The upper limit of webhook subscription replays to return from the start of the list.

    Defaults to 10 if neither `first` nor `last` are specified.
    Excess subscription replays will be trimmed from the end of the list.

    `first` and `last` cannot be specified in the same query.
    """
    first: Int

    """
    The upper limit of webhook subscription replays to return from the end of the list.

    Excess subscription replays will be trimmed from the start of the list.

    `first` and `last` cannot be specified in the same query.
    """
    last: Int

    """
    The additional `WebhookSubscriptionReplay`-specific criteria to filter by.
    """
    filter: WebhookSubscriptionReplaysFilterInput
  ): WebhookSubscriptionReplaysConnection!
}

"""
The state of a request to replay events for a `WebhookSubscription`.
"""
type WebhookSubscriptionReplay {
  """
  The identifier for the `WebhookSubscriptionReplay`.
  """
  id: ObjectIdentifier!

  """
  The original request for the `WebhookSubscriptionReplay`.
  """
  request: WebhookSubscriptionReplayRequest!

  """
  The date & time that the webhook subscription replay was created.
  """
  createDateTime: DateTime!

  """
  The date & time that the subscription replay was last updated.

  Subscription replays are updated whenever the replay makes forward progress.
  """
  updateDateTime: DateTime!

  """
  The webhook subscription that was the target of the replay.

  This will be `null` if the subscription has since been deleted.
  """
  webhookSubscription: WebhookSubscription

  """
  The current status of the replay.

  Currently, the following status codes are defined:

  - `Submitted`
  - `Running`
  - `Completed`
  - `Cancelled`
  """
  statusCode: String!
}

"""
The original criteria used to determine which events will be replayed.
"""
interface WebhookSubscriptionReplayRequest {
  """
  The type of the replay.

  Currently, two codes are defined:

  - `RangeReplayRequest` which corresponds to the `WebhookSubscriptionReplayByRangeRequest` type.
  - `IdReplayRequest` which corresponds to the `WebhookSubscriptionReplayByIdRequest` type.
  """
  typeCode: String!
}

"""
The date range criteria used to determine which events will be replayed.
"""
type WebhookSubscriptionReplayByRangeRequest implements WebhookSubscriptionReplayRequest {
  """
  The type of the component.

  This is always `RangeReplayRequest`.
  """
  typeCode: String!

  """
  The earliest event to include.
  """
  afterDateTime: DateTime!

  """
  The latest event to include.
  """
  beforeDateTime: DateTime!

  """
  Whether previously delivered events should be included in the request.

  This also includes events that were not delivered because the relevant hirer relationship or webhook subscription was not in place at time of occurrence.
  """
  replayDeliveredEventsIndicator: Boolean!

  """
  The hirer to replay events for.
  """
  hirer: HiringOrganization
}

"""
The event ID criteria used to determine which events will be replayed.
"""
type WebhookSubscriptionReplayByIdRequest implements WebhookSubscriptionReplayRequest {
  """
  The type of the component.

  This is always `IdReplayRequest`.
  """
  typeCode: String!

  """
  The list of `Event.id` values to replay.
  """
  eventIds: [String!]!
}

"""
A page of webhook subscription replays.
"""
type WebhookSubscriptionReplaysConnection {
  """
  The page of webhook subscription replays and their corresponding cursors.
  """
  edges: [WebhookSubscriptionReplayEdge!]!

  """
  The pagination metadata for this page of webhook subscription replays.

  Disclaimer:
  - The `hasPreviousPage` field will always be false when paginating by `first`.
  - The `hasNextPage` field will always be false when paginating by `last`.

  To discern whether a next/previous page exists in these conditions, an additional request will need to be made to retrieve the next/previous page.
  """
  pageInfo: PageInfo!
}

"""
A webhook subscription replay in a paginated list.
"""
type WebhookSubscriptionReplayEdge {
  """
  The opaque cursor to the webhook subscription replay.

  This can be used as a subsequent pagination argument.
  """
  cursor: String!

  """
  The actual webhook subscription replay.
  """
  node: WebhookSubscriptionReplay!
}

"""
The criteria to filter webhook subscription replays by.

These are `WebhookSubscriptionReplay`-specific extensions on top of the standard pagination arguments `before`, `after`, `first` and `last`.
"""
input WebhookSubscriptionReplaysFilterInput {
  """
  The replay status to filter by.

  See the `WebhookSubscriptionReplay.statusCode` documentation for a list of possible status codes.

  If this is not provided then replays of all statuses will be returned.
  """
  statusCode: String
}

"""
A page of webhook subscriptions.
"""
type WebhookSubscriptionsConnection {
  """
  The page of webhook subscriptions and their corresponding cursors.
  """
  edges: [WebhookSubscriptionEdge!]!

  """
  The pagination metadata for this page of subscriptions.
  """
  pageInfo: PageInfo!

  """
  The total number of webhook subscriptions in the entire list.
  This is not influenced by pagination parameters.
  """
  totalCount: Int!
}

"""
A webhook subscription in a paginated list.
"""
type WebhookSubscriptionEdge {
  """
  The opaque cursor to the webhook subscription.

  This can be used as a subsequent pagination argument.
  """
  cursor: String!

  """
  The actual webhook subscription.
  """
  node: WebhookSubscription!
}

"""
The criteria to filter webhook subscriptions by.

These are `WebhookSubscription`-specific extensions on top of the standard pagination arguments `before`, `after`, `first` and `last`.
"""
input WebhookSubscriptionsFilterInput {
  """
  The creation date & time that resulting webhook subscriptions must succeed.

  This can be used to initiate the retrieval of paginated results.
  Subsequent queries should use the opaque cursors returned from `WebhookSubscriptionsConnection`.
  """
  afterDateTime: DateTime

  """
  The creation date & time that resulting webhook subscriptions must precede.

  This can be used to initiate the retrieval of paginated results.
  Subsequent queries should use the opaque cursors returned from `WebhookSubscriptionsConnection`.
  """
  beforeDateTime: DateTime

  """
  The event types of webhook subscriptions to retrieve.

  See `Event` implementations for a list of supported values.

  If this is not provided then events of all types will be returned.
  """
  eventTypeCodes: [String!]

  """
  The hirer IDs of the hirer-filtered webhook subscriptions to retrieve.

  A maximum of 100 identifiers may be provided.

  If this is not provided then both hirer-filtered and unfiltered subscriptions will be returned.
  """
  hirerIds: [String!]

  """
  Whether to include only hirer-scoped webhook subscriptions (true), only partner-scoped webhook subscriptions (false), or all webhook subscriptions (null / not provided).
  """
  hirerScopedIndicator: Boolean
}

"""
An organization hiring for an open position.
"""
type HiringOrganization {
  """
  The identifier for the `HiringOrganization`.
  """
  id: ObjectIdentifier!

  """
  The name of the hiring organization.
  """
  name: String!

  """
  The capabilities of the requesting partner to act on behalf of the hiring organization.
  """
  seekApiCapabilities: HiringOrganizationApiCapabilities!

  """
  The legacy SEEK advertiser ID.

  This is a numeric identifier that was used by the previous version of Apply with SEEK.
  """
  seekAnzAdvertiserId: Int @deprecated(reason: "Use `id` instead.")
}

"""
The details of the hiring organization for input types.
"""
input HiringOrganizationInput {
  """
  The identifier for the `HiringOrganization`.
  """
  id: String!
}

"""
The capabilities of the requesting partner to act on behalf of a hirer.

This provides a read-only view of the configuration of a SEEK hirer for the current partner.
"""
type HiringOrganizationApiCapabilities {
  """
  The active relationship types between the requesting partner and the hirer.

  Currently, the following relationship type codes are defined:

  - `ApplicationExport` enables exporting candidate applications from SEEK's Apply Form.

  - `ApplicationPrefill` enables retrieving candidate profile data to pre-fill an external apply form.

  - `JobPosting` enables posting job ads to the SEEK job board.

  - `ProactiveSourcing` enables hirers to proactively search for and connect with suitable candidates.

  - `CandidateProfiles` enables hirers to display the Candidate Profile Panel and query for SEEK Candidate Profiles.

  - `CandidateVerification` enables hirers to request verification of candidates with SEEK Pass and query candidate identity verifications.
  """
  relationshipTypeCodes: [String!]!

  """
  The supported methods of applying to job ads posted by the hirer.

  All SEEK hirers can use SEEK's Apply Form by omitting the `ApplicationMethod` object when posting.
  This enumerates additional application methods SEEK has configured for the hirer.

  Currently, the following application method codes are defined:

  - `ApplicationUri` indicates that job ads can link out to an external apply form.
  """
  applicationMethodCodes: [String!]!

  """
  These indicate features that may be available (via a relationship or for all SEEK hirers) but have been disabled by a hirer.
  They are separate from relationship types.

  Currently, the following feature codes are defined:

  - `CandidateVerification` disables the Verify with SEEK Pass functionality for the hirer, even if the partner has a relationship type that would normally allow it.
      This also disables the ability to query for candidate identity verifications.
  """
  disabledFeatures: [String!]!
}

"""
A page of hirers.
"""
type HiringOrganizationsConnection {
  """
  The page of hirers and their corresponding cursors.
  """
  edges: [HiringOrganizationEdge!]!

  """
  The pagination metadata for this page of hirers.
  """
  pageInfo: PageInfo!
}

"""
A hirer in a paginated list.
"""
type HiringOrganizationEdge {
  """
  The opaque cursor to the hirer.

  This can be used as a subsequent pagination argument.
  """
  cursor: String!

  """
  The actual hirer.
  """
  node: HiringOrganization!
}

"""
The criteria to filter hirers by.

These are `HiringOrganization`-specific extensions on top of the standard pagination arguments `before`, `after`, `first` and `last`.
"""
input HiringOrganizationsFilterInput {
  """
  Filters on relationship types between the hirer and requesting partner.

  See the `HiringOrganizationApiCapabilities.relationshipTypeCodes` documentation for a list of relationship types.

  If this is not provided then all related hirers are returned.
  """
  relationshipTypeCodes: [String!]

  """
  Filters on hirer names containing the provided case-insensitive substring.

  This is intended to support ad-hoc searches for hirers by name.
  """
  nameSearch: String
}

"""
Basic information to identify and reference a specific organization.
"""
type Organization {
  """
  The human readable name of the organization.
  """
  name: String!
}

"""
Basic information to identify and reference a specific organization.
"""
input OrganizationInput {
  """
  The human readable name of the organization.
  """
  name: String!
}

"""
A partner organization for a `self` query.
"""
type PartnerOrganization {
  """
  The name of the querying partner.
  """
  name: String!

  """
  Whether the querying partner has been approved to self-service their live SEEK API client credentials.

  SEEK will approve partners once they have demonstrated progress through the development process.
  When true, administrative users will be able to issue and rotate client credentials in the Developer Dashboard.
  """
  credentialSelfServiceApprovedIndicator: Boolean!
}

"""
The organizations the query's access token can act on behalf of.
"""
type SelfOrganizations {
  """
  The partner organization making the request.
  """
  partner: PartnerOrganization!

  """
  The hirer the browser token is scoped to.

  This will be `null` when queried with a partner token.
  """
  hirer: HiringOrganization
}

"""
The input parameter for the `setDisabledFeatures` mutation.
"""
input SetDisabledFeaturesInput {
  """
  The identifier for the `HiringOrganization` to set disabled features for.
  """
  hiringOrganization: HiringOrganizationInput!

  """
  The list of feature codes to disable for this hirer.

  Currently, one disabled feature code is defined:

  - `CandidateVerification` disables the Verify with SEEK Pass functionality for the hirer.
  """
  disabledFeatures: [String!]!
}

"""
The response from the `setDisabledFeatures` mutation.
"""
type SetDisabledFeaturesPayload {
  """
  The updated list of disabled feature codes for this hirer-partner combination.
  """
  disabledFeatures: [String!]!
}

"""
The output of the `closePostedPositionProfile` mutation.
"""
type ClosePostedPositionProfilePayload {
  """
  Attributes of the closed position profile.
  """
  positionProfile: ClosePostedPositionProfile_PositionProfilePayload!
}

"""
Attributes of the closed position profile.
"""
type ClosePostedPositionProfile_PositionProfilePayload {
  """
  The identifier for the closed `PositionProfile`.
  """
  profileId: ObjectIdentifier!
}

"""
The response from the `createPositionOpening` mutation.
"""
type CreatePositionOpeningPayload {
  """
  The details of the created position opening.
  """
  positionOpening: PositionOpening!
}

"""
The response from the `createUnpostedPositionProfileForOpening` mutation.
"""
type CreateUnpostedPositionProfileForOpeningPayload {
  """
  Attributes of the newly created unposted position profile.
  """
  positionProfile: UnpostedPositionProfile!
}

"""
The response from the `deletePositionOpening` mutation.
"""
type DeletePositionOpeningPayload {
  """
  The details of the deleted position opening.
  """
  positionOpening: PositionOpening!
}

"""
The response from the `deleteUnpostedPositionProfile` mutation.
"""
type DeleteUnpostedPositionProfilePayload {
  """
  The details of the deleted unposted position profile.
  """
  positionProfile: UnpostedPositionProfile!
}

"""
The reason why a position was not posted on SEEK.
"""
type PositionNotPostedReasonPayload {
  """
  A short status describing why the position was not posted on SEEK.

  For example, "Couldn't post".
  """
  status: String!

  """
  A human-readable message explaining why the position was not posted on SEEK.

  For example, "This ad wasn't posted on SEEK because it wasn't mapped to a SEEK account. To post it, contact your SEEK account manager to update your SEEK preferences.".
  """
  message: String!

  """
  An array of field-level validation errors associated with the reason.

  This will be null if there are no field-level details.
  """
  errors: [PositionNotPostedReason_DataItem!]

  """
  A suggested action the user can take to resolve the issue.

  Currently only two codes are defined: "ContactSeek" and "ManagePosition".

  This will be null if no call to action is applicable.
  """
  callToAction: String

  """
  The time that the reason was generated.
  """
  timestamp: String!
}

"""
A field-level validation detail associated with a position not being posted on SEEK.
"""
type PositionNotPostedReason_DataItem {
  """
  The name of the field that caused the issue.

  For example, "positionLocation.city".
  """
  field: String!

  """
  The value that was provided for the field.

  For example, "this is not a valid city".
  """
  value: String!

  """
  A human-readable message describing the issue with the field value.

  For example, "Value could not be used to infer a valid SEEK location".
  """
  message: String
}

"""
A job requisition or position opening within an organization.

This is a container object that groups multiple `PositionProfile`s together along with their owner.
"""
type PositionOpening {
  """
  The status of the position opening.

  Currently, three codes are defined:

  - `Incomplete` indicates the position opening is in a draft state.
  - `Active` indicates the position opening is open.
  - `Closed` indicates the position opening has been closed.
  """
  statusCode: String!

  """
  The identifier for the `PositionOpening`.
  """
  documentId: ObjectIdentifier!

  """
  An optional field for storing additional data with a `PositionOpening`.

  The metadata is not used by SEEK and won't be seen by hirers or candidates.

  This field has a maximum length of 1,000 characters.
  """
  seekPartnerMetadata: String

  """
  The party that owns the position opening.

  This may be different from the hiring organization if the position opening was created by a recruitment agency outside of the SEEK API.
  """
  postingRequester: PostingRequester!

  """
  A page of position profiles that belong to the opening.

  A maximum of 50 position profiles can be returned in a single page.
  Additional position profiles can be queried using a pagination cursor.

  The result list is returned in ascending creation date & time order.
  It starts from the earliest known position profile if no pagination arguments are provided.
  """
  paginatedPositionProfiles(
    """
    An opaque cursor to the earlier bounding page.

    Resulting position profiles will _succeed_ this cursor.
    """
    after: String

    """
    The upper limit of position profiles to return from the start of the list.

    Defaults to 10 if `first` is not specified.
    Excess position profiles will be trimmed from the end of the list.
    """
    first: Int
  ): PositionProfileConnection!

  """
  An array of up to 25 profiles for the position opening.

  Each profile represents a posted job ad or an unposted internal requisition associated with this opening.
  """
  positionProfiles: [PositionProfile!]!
    @deprecated(
      reason: "There is no guarantee all profiles will be returned. Use `paginatedPositionProfiles` instead."
    )
}

"""
The party that owns the position opening.
"""
type PostingRequester {
  """
  The identifier for the `HiringOrganization` that owns the position opening.

  This is typically a hirer,
  but may be a recruitment agency for position openings created outside of the SEEK API.
  """
  id: ObjectIdentifier!

  """
  The name of the party that owns the position opening.
  """
  name: String!

  """
  The legacy SEEK advertiser ID.

  This is a numeric identifier that was used by the previous version of Apply with SEEK.
  """
  seekAnzAdvertiserId: Int @deprecated(reason: "Use `id` instead.")

  """
  The role of the owner of the position opening.

  This field always returns `Company`.
  """
  roleCode: String!
    @deprecated(
      reason: "This field is always `Company` and can be safely removed."
    )

  """
  Specific contact people for the position opening at the party.
  """
  personContacts: [SpecifiedPerson!]!
}

"""
A position opening in a paginated list.
"""
type PositionOpeningEdge {
  """
  The opaque cursor to the position opening.

  This can be used as a subsequent pagination argument.
  """
  cursor: String!

  """
  The actual position opening.
  """
  node: PositionOpening!
}

"""
A page of position openings.
"""
type PositionOpeningConnection {
  """
  The page of position openings and their corresponding cursors.

  This list may be empty.
  """
  edges: [PositionOpeningEdge!]!

  """
  The pagination metadata for this page of position openings.
  """
  pageInfo: PageInfo!
}

"""
A profile of a position opening.

This contains information of how a position opening is presented on a job board or as an internal requisition.
"""
interface PositionProfile {
  """
  The identifier for the `PositionProfile`.
  """
  profileId: ObjectIdentifier!

  """
  The `PositionOpening` that this profile was created under.
  """
  positionOpening: PositionOpening!

  """
  The type of position profile.

  See `PositionProfile` implementations for a list of supported values.
  """
  seekTypeCode: String!

  """
  A short phrase describing the position as it would be listed on a business card or in a company directory.
  """
  positionTitle: String!

  """
  An array of identifiers for the `HiringOrganization`s that have the position.
  """
  positionOrganizations: [HiringOrganization!]!

  """
  An optional hirer-provided free-text job reference. In the syncPosition flow, this is mapped to the seekPartnerPositionId field.
  """
  seekHirerJobReference: String

  """
  An optional free-text billing reference.

  This appears on the invoice when SEEK bills the hirer for a job ad.
  """
  seekBillingReference: String

  """
  A unique resource identifier the position profile.

  - For posted position profiles, this is the public web URL of the posted job ad.

  - For unposted position profiles, this is the profile's object identifier.
  """
  positionUri: String!

  """
  An array of formatted position profile descriptions.
  """
  positionFormattedDescriptions: [PositionFormattedDescription!]!

  """
  An array of codes for the offered schedules for the position.

  Currently, two codes are defined:

  - `FullTime` indicates a full-time schedule.
  - `PartTime` indicates a part-time schedule.

  If the offered schedule isn't known this will be `null`.
  """
  positionScheduleTypeCodes: [String!]

  """
  The salary or compensation offered for the position.
  """
  offeredRemunerationPackage: RemunerationPackage!

  """
  A work type code for the `seekAnz` scheme.

  Currently, four codes are defined:

  - `Casual` indicates a casual position.
  - `ContractTemp` indicates a fixed-length contract position.
  - `FullTime` indicates a full-time position.
  - `PartTime` indicates a part-time position.

  For positions in other schemes this will be `null`.
  """
  seekAnzWorkTypeCode: String

  """
  The occupational categories of the job.
  """
  jobCategories: [JobCategory!]!

  """
  The locations of the position.
  """
  positionLocation: [Location!]!

  """
  The set of questions presented to candidates during an application.

  The questionnaire responses will be available as `CandidateProfile.seekQuestionnaireSubmission` on the candidate's application profile.
  """
  seekApplicationQuestionnaire: ApplicationQuestionnaire

  """
  The video to render within the job ad.
  """
  seekVideo: SeekVideo

  """
  The instructions related to posting the job ad.
  """
  postingInstructions: [PostingInstruction!]!

  """
  An optional field for storing additional data with a `PositionProfile`.

  The metadata is not used by SEEK and won't be seen by hirers or candidates.

  This field has a maximum length of 1,000 characters.
  """
  seekPartnerMetadata: String

  """
  Whether the position profile was created by the requesting partner.

  If your software cannot ingest objects that are created by another source,
  this can be used to filter out such position profiles.
  """
  seekCreatedBySelfIndicator: Boolean!
}

"""
A profile of a position opening.

This contains information of how a position opening is presented on a job board.
"""
type PostedPositionProfile implements PositionProfile {
  """
  The identifier for the `PositionProfile`.
  """
  profileId: ObjectIdentifier!

  """
  The `PositionOpening` that this profile was created under.
  """
  positionOpening: PositionOpening!

  """
  The type of position profile, i.e. `PostedPositionProfile`.
  """
  seekTypeCode: String!

  """
  A short phrase describing the position as it would be listed on a business card or in a company directory.
  """
  positionTitle: String!

  """
  An array of identifiers for the `HiringOrganization`s that have the position.
  """
  positionOrganizations: [HiringOrganization!]!

  """
  An optional hirer-provided free-text job reference.
  """
  seekHirerJobReference: String

  """
  An optional free-text billing reference.

  This appears on the invoice when SEEK bills the hirer for the job ad.
  """
  seekBillingReference: String

  """
  The public web URL of the posted job ad on SEEK.
  """
  positionUri: String!

  """
  The public web URL to submit an application for the posted job ad on SEEK.

  This is null for job ads that use a link-out application method.
  """
  seekApplicationUri: WebUrl

  """
  An array of formatted position profile descriptions.
  """
  positionFormattedDescriptions: [PositionFormattedDescription!]!

  """
  An array of codes for the offered schedules for the position.

  Currently, two codes are defined:

  - `FullTime` indicates a full-time schedule.
  - `PartTime` indicates a part-time schedule.

  If the offered schedule isn't known this will be `null`.
  """
  positionScheduleTypeCodes: [String!]

  """
  The salary or compensation offered for the position.
  """
  offeredRemunerationPackage: RemunerationPackage!

  """
  A work type code for the `seekAnz` scheme.

  Currently, four codes are defined:

  - `Casual` indicates a casual position.
  - `ContractTemp` indicates a fixed-length contract position.
  - `FullTime` indicates a full-time position.
  - `PartTime` indicates a part-time position.

  For positions in other schemes this will be `null`.
  """
  seekAnzWorkTypeCode: String

  """
  The occupational categories of the job.
  """
  jobCategories: [JobCategory!]!

  """
  The locations of the position.
  """
  positionLocation: [Location!]!

  """
  The set of questions presented to candidates during an application.

  The questionnaire responses will be available as `CandidateProfile.seekQuestionnaireSubmission` on the candidate's application profile.
  """
  seekApplicationQuestionnaire: ApplicationQuestionnaire

  """
  The video to render within the job ad.
  """
  seekVideo: SeekVideo

  """
  The instructions related to posting the job ad.
  """
  postingInstructions: [PostingInstruction!]!

  """
  An optional field for storing additional data with a `PositionProfile`.

  The metadata is not used by SEEK and won't be seen by hirers or candidates.

  This field has a maximum length of 1,000 characters.
  """
  seekPartnerMetadata: String

  """
  Whether the job ad was initially posted by the requesting partner.

  If your software cannot ingest objects that are created by another source,
  this can be used to filter out such job ads and their associated applications.

  `seekPostingSourceCode` can be used to determine more details about the source that initially posted the job ad.
  """
  seekCreatedBySelfIndicator: Boolean!

  """
  The source that initially posted the job ad.

  This can be used to determine the source of the job ad posting, and whether to display & enable functionality for the job ad.

  Currently, four codes are defined:

  - `CurrentPartner` indicates the job ad was posted by the requesting partner. This will accompany a `seekCreatedBySelfIndicator` value of `true`.

  - `Seek` indicates the job ad was posted via the SEEK employer website. This will accompany a `seekCreatedBySelfIndicator` value of `false`.

  - `AdSync` indicates the job ad was posted via the SEEK Ad Sync integration. This will accompany a `seekCreatedBySelfIndicator` value of `false`.

  - `Other` indicates the job ad was posted via another source, including a separate SEEK API partner or other SEEK posting methods. This will accompany a `seekCreatedBySelfIndicator` value of `false`.
  """
  seekPostingSourceCode: String!

  """
  An array of SEEK work arrangement codes.

  Currently, three codes are defined:

  - `Hybrid` indicates a hybrid position.
  - `OnSite` indicates an on-site position.
  - `Remote` indicates a remote position.

  This field is limited to a single element.
  """
  seekWorkArrangementCodes: [String!]!
}

"""
A description type identifier.

This specifies the meaning of the description and determines where it's presented to the candidate.
"""
type PositionFormattedDescriptionIdentifier {
  """
  The description type.

  Currently, three identifiers are defined:

  - `AdvertisementDetails` is the detailed description of the position that appears on the job ad.
  - `SearchBulletPoint` is a highlight or selling point of the position that appears in search results.
    This will not appear on the job ad details page.
    The number of bullet points that a job can provide is based on the `AdvertisementProductFeatures`'s `searchBulletPoints.limit`.
  - `SearchSummary` is a short description that appears in search results.
    This will not appear on the job ad details page.
  """
  value: String!
}

"""
A profile of a position opening.

This contains information of how a position opening is presented as an internal requisition.
"""
type UnpostedPositionProfile implements PositionProfile {
  """
  The identifier for the `PositionProfile`.
  """
  profileId: ObjectIdentifier!

  """
  A human-readable name given to the profile.

  This in addition to the `positionTitle` can help identify the profile to an end user.
  """
  profileName: String

  """
  The `PositionOpening` that this profile was created under.
  """
  positionOpening: PositionOpening!

  """
  The type of position profile, i.e. `UnpostedPositionProfile`.
  """
  seekTypeCode: String!

  """
  A short phrase describing the position as it would be listed on a business card or in a company directory.
  """
  positionTitle: String!

  """
  An array of identifiers for the `HiringOrganization`s that have the position.
  """
  positionOrganizations: [HiringOrganization!]!

  """
  An optional hirer-provided free-text job reference.
  """
  seekHirerJobReference: String

  """
  An optional free-text billing reference.

  SEEK does not use this field on unposted position profiles.
  """
  seekBillingReference: String

  """
  The object identifier for the `PositionProfile`.

  This is duplicated from the `profileId` field to satisfy the `PositionProfile` interface.
  """
  positionUri: String!

  """
  An array of formatted position profile descriptions.
  """
  positionFormattedDescriptions: [PositionFormattedDescription!]!

  """
  An array of codes for the offered schedules for the position.

  Currently, two codes are defined:

  - `FullTime` indicates a full-time schedule.
  - `PartTime` indicates a part-time schedule.

  If the offered schedule isn't known this will be `null`.
  """
  positionScheduleTypeCodes: [String!]

  """
  The salary or compensation offered for the position.
  """
  offeredRemunerationPackage: RemunerationPackage!

  """
  A work type code for the `seekAnz` scheme.

  Currently, four codes are defined:

  - `Casual` indicates a casual position.
  - `ContractTemp` indicates a fixed-length contract position.
  - `FullTime` indicates a full-time position.
  - `PartTime` indicates a part-time position.

  For positions in other schemes this will be `null`.
  """
  seekAnzWorkTypeCode: String

  """
  The occupational categories of the job.
  """
  jobCategories: [JobCategory!]!

  """
  The locations of the position.
  """
  positionLocation: [Location!]!

  """
  The set of questions presented to candidates during an application.

  The questionnaire responses will be available as `CandidateProfile.seekQuestionnaireSubmission` on the candidate's application profile.
  """
  seekApplicationQuestionnaire: ApplicationQuestionnaire

  """
  The video to render within the job ad.
  """
  seekVideo: SeekVideo

  """
  The instructions related to posting the job ad.
  """
  postingInstructions: [PostingInstruction!]!

  """
  An optional field for storing additional data with a `PositionProfile`.

  The metadata is not used by SEEK and won't be seen by hirers or candidates.

  This field has a maximum length of 1,000 characters.
  """
  seekPartnerMetadata: String

  """
  Whether the position profile was created by the requesting partner.

  If your software cannot ingest objects that are created by another source,
  this can be used to filter out such position profiles.
  """
  seekCreatedBySelfIndicator: Boolean!
}

"""
A formatted description of the position profile.
"""
type PositionFormattedDescription {
  """
  The description type.
  """
  descriptionId: PositionFormattedDescriptionIdentifier!

  """
  The HTML content of the description.
  """
  content: String!
}

"""
The salary or compensation for a position.
"""
type RemunerationPackage {
  """
  A code classifying the primary method of payment for a position.

  Currently, three codes are defined:

  - `Hourly` employment is paid for the number of hours worked.

  - `Salaried` employment is paid on a monthly or annual basis.

  - `SalariedPlusCommission` employment is paid on an annual basis plus a results-based commission.
  """
  basisCode: String!

  """
  An array of offered salary ranges.

  The `seekAnz` scheme will always have a single element containing the amount for the `basisCode`.
  """
  ranges: [RemunerationRange!]!

  """
  Human readable descriptions of the remuneration package.

  The `seekAnz` scheme will be limited to a single element.

  An empty array signifies that no salary descriptions exist for the remuneration package.
  """
  descriptions: [String!]!
}

"""
A salary or compensation range for a position.
"""
type RemunerationRange {
  """
  The minimum amount an organization is willing to pay for a position.

  The value must be greater than 0.
  """
  minimumAmount: RemunerationAmount!

  """
  The maximum amount an organization is willing to pay for a position.

  The value must be greater than or equal to the value of `minimumAmount` and the currency must match `minimumAmount`.

  The associated `RemunerationRangeInput.maximumAmount` field will be required in our schema in future.
  Currently, omitting the field will default it to `minimumAmount` and harm the performance of the job ad.
  """
  maximumAmount: RemunerationAmount

  """
  The interval the remuneration amounts are calculated over.

  Currently three interval codes are defined:

  - `Hour` is used to express hourly rates.
  - `Month` is used to express monthly salaries.
  - `Year` is used to express annual salaries or commissions.
  """
  intervalCode: String!
}

"""
A monetary amount of remuneration in a specified currency.
"""
type RemunerationAmount {
  """
  A non-negative float in the major currency unit for the ISO currency code.

  For example, this is the number of dollars in dollar-denominated currencies.
  """
  value: Float!

  """
  The three-letter ISO 4217 currency code, in uppercase.
  """
  currency: String!
}

"""
Information about how to post a job ad and where to direct its candidate applications.
"""
type PostingInstruction {
  """
  The start date of the posting.
  """
  start: DateTime!

  """
  The end date of the posting.
  """
  end: DateTime!

  """
  A SEEK advertisement product label.

  This field is for display purposes only and should not be used to determine any features of an ad.
  """
  seekAdvertisementProduct: PostedPositionProfile_AdvertisementProduct

  """
  An array of methods for applying to the position.

  If no methods are provided, SEEK's Apply Form will be used to receive candidate applications.
  SEEK's Apply Form will emit a `CandidateApplicationCreated` event that points to a `CandidateProfile` object.
  """
  applicationMethods: [ApplicationMethod!]!

  """
  The identifier for the `AdvertisementBranding` applied to the posted job ad.

  This is included for HR-JSON compatibility;
  GraphQL users should use the `branding` nested object instead.
  """
  brandingId: String

  """
  The branding applied to the posted job ad.
  """
  branding: AdvertisementBranding
}

"""
A method of applying to a position.
"""
type ApplicationMethod {
  """
  A URL of an external apply form.

  When this is provided, SEEK's Apply Form will be disabled and the candidate will be redirected to the supplied URL.
  """
  applicationUri: WebUrl

  """
  The email address that candidate applications are directed to.

  Do not select this in your operations,
  or your integration will break when the field is removed.
  """
  applicationEmail: Email
    @deprecated(
      reason: "This is always `null` and has been replaced by Optimised Apply."
    )
}

"""
The details of the advertisement product for a job ad.
"""
type PostedPositionProfile_AdvertisementProduct {
  """
  The name of the advertisement product for displaying to the user.

  This field is for display purposes only and should not be used to determine any features of an ad.
  """
  label: String!
}

"""
An externally hosted video to display alongside advertisement details.
"""
type SeekVideo {
  """
  The URL of the video.
  """
  url: String!

  """
  The position relative to the advertisement details where the video is rendered.

  Currently, two codes are defined:

  - `Above` indicates the video will render above the advertisement details.
  - `Below` indicates the video will render below the advertisement details.
  """
  seekAnzPositionCode: String!
}

"""
A position profile in a paginated list.
"""
type PositionProfileEdge {
  """
  The opaque cursor to the position profile.

  This can be used as a subsequent pagination argument.
  """
  cursor: String!

  """
  The actual position profile.
  """
  node: PositionProfile!
}

"""
A page of position profiles.
"""
type PositionProfileConnection {
  """
  The page of position profiles and their corresponding cursors.

  This list may be empty.
  """
  edges: [PositionProfileEdge!]!

  """
  The pagination metadata for this page of position profiles.
  """
  pageInfo: PageInfo!
}

"""
The response from the `postPosition` mutation.
"""
union PostPositionPayload =
  | PostPositionPayload_Success
  | PostPositionPayload_Conflict

"""
The success result for the `postPosition` mutation.
"""
type PostPositionPayload_Success {
  """
  Attributes of the newly created position opening.
  """
  positionOpening: PostPosition_PositionOpeningPayload!

  """
  Attributes of the newly created position profile for the job ad.
  """
  positionProfile: PostPosition_PositionProfilePayload!
}

"""
The conflict result for the `postPosition` mutation.

The  `idempotencyId` provided as part of `CreatePostingInstructionInput` while creating a position profile must be unique.
Providing the same `idempotencyId` will result in a conflict.
"""
type PostPositionPayload_Conflict {
  """
  Attributes of the conflicting position opening.
  """
  conflictingPositionOpening: PostPosition_PositionOpeningPayload!

  """
  Attributes of the conflicting position profile.
  """
  conflictingPositionProfile: PostPosition_PositionProfilePayload!
}

"""
Attributes of the position opening.
"""
type PostPosition_PositionOpeningPayload {
  """
  The identifier for the `PositionOpening`.

  Scheme restrictions:

  - The `seekAnz` scheme creates the position opening asynchronously.
    This identifier will initially reference a missing object;
    the object should be created within a few minutes.
  """
  documentId: ObjectIdentifier!
}

"""
Attributes of the position profile.
"""
type PostPosition_PositionProfilePayload {
  """
  The identifier for the `PositionProfile`.

  Scheme restrictions:

  - The `seekAnz` scheme creates the position profile asynchronously.
    This identifier will initially reference a missing object;
    the object should be created within a few minutes.
  """
  profileId: ObjectIdentifier!
}

"""
The response from the `postPositionProfileForOpening` mutation.
"""
union PostPositionProfileForOpeningPayload =
  | PostPositionProfileForOpeningPayload_Success
  | PostPositionProfileForOpeningPayload_Conflict

"""
The success result for the `postPositionProfileForOpening` mutation.
"""
type PostPositionProfileForOpeningPayload_Success {
  """
  Attributes of the newly created position profile for the job ad.
  """
  positionProfile: PostPositionProfileForOpening_PositionProfilePayload!
}

"""
The conflict result for the `postPositionProfileForOpening` mutation.

The  `idempotencyId` provided as part of `CreatePostingInstructionInput` while creating a position profile must be unique.
Providing the same `idempotencyId` will result in a conflict.
"""
type PostPositionProfileForOpeningPayload_Conflict {
  """
  Attributes of the conflicting position profile.
  """
  conflictingPositionProfile: PostPositionProfileForOpening_PositionProfilePayload!
}

"""
Attributes of the position profile.
"""
type PostPositionProfileForOpening_PositionProfilePayload {
  """
  The identifier for the `PositionProfile`.
  """
  profileId: ObjectIdentifier!
}

"""
The criteria to filter position openings by.

These are `PositionOpening`-specific extensions on top of the standard pagination arguments `after` and `first`.
"""
input PositionOpeningsFilterInput {
  """
  Optionally filter results to only include position openings with the specified status code.

  Currently, three codes are defined:

  - `Incomplete` indicates the position opening is in a draft state.
  - `Active` indicates the position opening is open.
  - `Closed` indicates the position opening has been closed.
  """
  statusCode: String
}

"""
The successful result of sync positions.
"""
type SyncPositionsPayload_Success {
  """
  An array of SEEK position profile identifiers assigned after sync.
  """
  positions: [String!]

  """
  An array of synchronization errors for positions that could not be processed.

  This will be null if all positions were synced successfully.
  """
  errors: [SyncPositionsPayload_Error!]
}

"""
An error that occurred during position sync.
"""
type SyncPositionsPayload_Error {
  """
  The partner's unique identifier for the position that failed to sync.

  This corresponds to the `seekPartnerPositionId` provided in the input.
  """
  seekPartnerPositionId: String!

  """
  A human-readable error message describing why the position could not be synced.

  For example, "Invalid job category code" or "Position location not found".
  """
  error: String!
}

"""
The response from the `updatePositionOpeningPersonContacts` mutation.
"""
type UpdatePositionOpeningPersonContactsPayload {
  """
  The details of the updated position opening.
  """
  positionOpening: PositionOpening!
}

"""
The response from the `updatePositionOpeningStatus` mutation.
"""
type UpdatePositionOpeningStatusPayload {
  """
  The details of the updated position opening.
  """
  positionOpening: PositionOpening!
}

"""
The output of the `updatePostedPositionProfile` mutation.
"""
type UpdatePostedPositionProfilePayload {
  """
  Attributes of the updated position profile.
  """
  positionProfile: UpdatePostedPositionProfile_PositionProfilePayload!
}

"""
Attributes of the updated position profile.
"""
type UpdatePostedPositionProfile_PositionProfilePayload {
  """
  The identifier for the updated `PositionProfile`.
  """
  profileId: ObjectIdentifier!
}

"""
The response from the `updateUnpostedPositionProfile` mutation.
"""
type UpdateUnpostedPositionProfilePayload {
  """
  Attributes of the updated unposted position profile.
  """
  positionProfile: UnpostedPositionProfile!
}

"""
A method of applying to a position.
"""
input ApplicationMethodInput {
  """
  A URL of an external apply form.

  When this is provided, SEEK's Apply Form will be disabled and the candidate will link out to the supplied URL.
  This requires that the hirer has an `HiringOrganizationApiCapabilities.applicationMethodCodes` that includes `ApplicationUri`.

  This field has a maximum length of 500 characters.
  """
  applicationUri: WebUrlInput
}

"""
The input parameter for the `closePostedPositionProfile` mutation.
"""
input ClosePostedPositionProfileInput {
  """
  The details of the position profile to be closed.
  """
  positionProfile: ClosePostedPositionProfile_PositionProfileInput!
}

"""
The details of the position profile to be closed.
"""
input ClosePostedPositionProfile_PositionProfileInput {
  """
  The identifier for the posted `PositionProfile` to be closed.
  """
  profileId: String!
}

"""
Information about how to post a job ad and where to direct its candidate applications.
"""
input CreatePostingInstructionInput {
  """
  The identifier for the `AdvertisementProduct`.
  """
  seekAdvertisementProductId: String

  """
  The end date of the posting.

  Scheme requirements:

  - For the `seekAnz` scheme this must be no more than 30 days in the future.

    If an end date is omitted, the job ad will default to the maximum period of 30 calendar days.
    The precise end date can be queried from the `PostingInstruction.end` field once the job ad goes live.
  """
  end: DateTime

  """
  An identifier to ensure that multiple ads are not created on retries.

  A unique identifier needs to be generated by your software for each position profile.
  The same identifier must be provided when retrying after create failures.
  Your identifiers are isolated from and will not conflict with those generated by other recruitment software providers.
  """
  idempotencyId: String!

  """
  An array of methods for applying to the position.

  If no methods are provided, SEEK's Apply Form will be used to receive candidate applications.
  SEEK's Apply Form will emit a `CandidateApplicationCreated` event that points to a `CandidateProfile` object.

  Scheme requirements:

  - For the `seekAnz` scheme, this field is limited to a single element.
    Requests with more than 1 element will fail.
  """
  applicationMethods: [ApplicationMethodInput!]

  """
  The identifier for the `AdvertisementBranding` to apply to the posted job ad.

  Scheme requirements:

  - For the `seekAnz` scheme, this field's behavior is dependent on the `AdvertisementProductFeatures` of the product set in the `seekAdvertisementProductId` field.

    When the product's `AdvertisementProductFeatures.branding` value is null, this field will be silently ignored.
  """
  brandingId: String
}

"""
A formatted description of the position profile.
"""
input PositionFormattedDescriptionInput {
  """
  The description type.

  Currently, three identifiers are defined:

  - `AdvertisementDetails` is the detailed description of the position that appears on the job ad.

  - `SearchBulletPoint` is a highlight or selling point of the position that appears in search results.
    The number of bullet points that a job can provide is based on the `AdvertisementProductFeatures`'s `searchBulletPoints.limit`.
    The length limit applies to each search bullet point separately.

  - `SearchSummary` is a short description that appears in search results.

  Scheme requirements:

  - The `seekAnz` scheme requires `AdvertisementDetails` and `SearchSummary` to be included.
  """
  descriptionId: String!

  """
  The HTML content of the description.

  The maximum length differs by `descriptionId`:

    - `AdvertisementDetails` has a maximum length of 15,000 characters.
    - `SearchBulletPoint` has a maximum length of 80 characters per bullet point.
    - `SearchSummary` has a maximum length of 150 characters.

  Scheme requirements:

  - The `seekAnz` scheme supports the following HTML tags in `AdvertisementDetails`:

    - `a` (HTTPS only. Available on a per hirer basis. Hirer must contact SEEK to enable.)
    - `b`
    - `br`
    - `div`
    - `em`
    - `h2`
    - `li`
    - `ol`
    - `p`
    - `strong`
    - `ul`

    Other descriptions will have all HTML tags stripped.
  """
  content: String!
}

"""
The input parameter for the `positionNotPostedReason` query.
"""
input PositionNotPostedReasonInput {
  """
  The identifier for the `PositionNotPostedReason`.
  """
  reasonId: String!
}

"""
The input parameter for the `positionNotPostedReasons` query.
"""
input PositionNotPostedReasonsInput {
  """
  The partner's unique identifier for the position.

  This identifier is used to look up all reasons why the position was not posted on SEEK.
  """
  seekPartnerPositionId: String!

  """
  The SEEK Ad Sync identifier for the hirer.

  This is assigned by SEEK when the hirer is onboarded to Ad Sync.
  This field must be exactly 24 characters.
  """
  seekAdSyncId: String!

  """
  The partner's unique reference for the sub-organization.

  This identifies a subsidiary or regional entity within the hiring organization.
  """
  seekSubOrgReference: String
}

"""
The input parameter for the `createPositionOpening` mutation.
"""
input CreatePositionOpeningInput {
  """
  The details of the position opening to be created.
  """
  positionOpening: CreatePositionOpening_PositionOpeningInput!
}

"""
The details of the position opening to be created.
"""
input CreatePositionOpening_PositionOpeningInput {
  """
  The party that owns the position opening.
  """
  postingRequester: PostingRequesterInput!

  """
  An optional field for storing additional data with a `PositionOpening`.

  The metadata is not used by SEEK and won't be seen by hirers or candidates.

  This field has a maximum length of 1,000 characters.
  """
  seekPartnerMetadata: String

  """
  The status of the position opening.

  Defaults to `Active` if no value is provided.

  Currently, three codes are defined:

  - `Incomplete` indicates the position opening is in draft.
  - `Active` indicates the position opening is open.
  - `Closed` indicates the position opening has been closed.
  """
  statusCode: String
}

"""
The input parameter for the `updatePositionOpeningPersonContacts` mutation.
"""
input UpdatePositionOpeningPersonContactsInput {
  """
  The details of the position opening to be updated.
  """
  positionOpening: UpdatePositionOpeningPersonContactsPositionOpeningInput!
}

"""
The details of the position opening to be updated.
"""
input UpdatePositionOpeningPersonContactsPositionOpeningInput {
  """
  The identifier for the `PositionOpening` to be updated.
  """
  documentId: String!

  """
  Specific contact people for the position opening at the party.

  The name, email address & role of at least one contact person must be provided.
  A maximum of 10 contact people may be provided.
  """
  personContacts: [SpecifiedPersonInput!]!
}

"""
The input parameter for the `updatePositionOpeningStatus` mutation.
"""
input UpdatePositionOpeningStatusInput {
  """
  The details of the position opening to be updated.
  """
  positionOpening: UpdatePositionOpeningStatusPositionOpeningInput!
}

"""
The details of the position opening to have its status updated.
"""
input UpdatePositionOpeningStatusPositionOpeningInput {
  """
  The identifier for the `PositionOpening` to be updated.
  """
  documentId: String!

  """
  The status of the position opening.

  Currently, three codes are defined:

  - `Incomplete` indicates the position opening is in a draft state.
  - `Active` indicates the position opening is open.
  - `Closed` indicates the position opening has been closed.
  """
  statusCode: String!
}

"""
The input parameter for the `deletePositionOpening` mutation.
"""
input DeletePositionOpeningInput {
  """
  The details of the position opening to be deleted.
  """
  positionOpening: DeletePositionOpening_PositionOpeningInput!
}

"""
The details of the position opening to be deleted.
"""
input DeletePositionOpening_PositionOpeningInput {
  """
  The identifier for the `PositionOpening` to be deleted.
  """
  documentId: String!
}

"""
The party that owns the position opening.
"""
input PostingRequesterInput {
  """
  The identifier for the `HiringOrganization` that owns the position opening.
  """
  id: String!

  """
  The role of the owner of the position opening.

  Currently, one code is defined:

  - `Company` indicates a company hiring on behalf of themselves.
  """
  roleCode: String!

  """
  Specific contact people for the position opening at the party.

  The name, email address & role of at least one contact person must be provided.
  A maximum of 10 contact people may be provided.
  """
  personContacts: [SpecifiedPersonInput!]!
}

"""
The information required to post a job ad for a newly created position.
"""
input PostPositionInput {
  """
  The details of the position opening to be created.
  """
  positionOpening: CreatePositionOpening_PositionOpeningInput!

  """
  A profile of a position opening.
  """
  positionProfile: PostPosition_PositionProfileInput!
}

"""
The details of the position profile to be created.
"""
input PostPosition_PositionProfileInput {
  """
  A short phrase describing the position as it would be listed on a business card or in a company directory.

  This field has a maximum length of 80 characters.
  """
  positionTitle: String!

  """
  An array of identifiers for the `HiringOrganization`s that have the position.

  Scheme requirements:

  - The `seekAnz` scheme requires exactly one element that matches the position opening's `PostingRequester.id`.
  """
  positionOrganizations: [String!]!

  """
  An optional hirer-provided free-text job reference.

  This field has a maximum length of 50 characters.
  """
  seekHirerJobReference: String

  """
  An optional free-text billing reference.

  This appears on the invoice when SEEK bills the hirer for the job ad.

  This field has a maximum length of 50 characters.
  """
  seekBillingReference: String

  """
  An array of formatted position profile descriptions.
  """
  positionFormattedDescriptions: [PositionFormattedDescriptionInput!]!

  """
  An array of codes for the offered schedules for the position.

  Currently, two codes are defined:

  - `FullTime` indicates a full-time schedule.
  - `PartTime` indicates a part-time schedule.

  Omit this field for the `seekAnz` scheme.
  """
  positionScheduleTypeCodes: [String!]

  """
  The salary or compensation offered for the position.
  """
  offeredRemunerationPackage: RemunerationPackageInput!

  """
  A SEEK work type code.

  For positions in `seekAnz` scheme, this field is used instead of `positionScheduleTypeCodes`.

  Currently, four codes are defined:

  - `Casual` indicates a casual position.
  - `ContractTemp` indicates a fixed-length contract position.
  - `FullTime` indicates a full-time position.
  - `PartTime` indicates a part-time position.

  Scheme requirements:

  - This field is required for the `seekAnz` scheme.
  - Omit this field for other schemes.
  """
  seekAnzWorkTypeCode: String

  """
  An array of `JobCategory` identifiers.

  Scheme requirements:

  - The `seekAnz` scheme requires exactly one identifier for a child job category.
  """
  jobCategories: [String!]!

  """
  An array of `Location` identifiers.

  Scheme requirements:

  - The `seekAnz` scheme requires exactly one element.
  """
  positionLocation: [String!]!

  """
  The identifier for the `ApplicationQuestionnaire` to present to a candidate during their application.

  The questionnaire responses will be available as `CandidateProfile.seekQuestionnaireSubmission` on the candidate's application profile.

  SEEK questionnaires are only supported on SEEK's Apply Form;
  omit this field if an `ApplicationMethodInput.applicationUri` is provided.
  """
  seekApplicationQuestionnaireId: String

  """
  The video to render within the job ad.
  """
  seekVideo: SeekVideoInput

  """
  The instructions related to posting the job ad.

  Scheme requirements:

  - The `seekAnz` scheme requires exactly one element.
  """
  postingInstructions: [CreatePostingInstructionInput!]!

  """
  An optional field for storing additional data with a `PositionProfile`.

  The metadata is not used by SEEK and won't be seen by hirers or candidates.

  This field has a maximum length of 1,000 characters.
  """
  seekPartnerMetadata: String

  """
  An array of SEEK work arrangement codes.

  Currently, three codes are defined:

  - `Hybrid` indicates a hybrid position.
  - `OnSite` indicates an on-site position.
  - `Remote` indicates a remote position.

  This field is limited to a single element.
  """
  seekWorkArrangementCodes: [String!]
}

"""
The input parameter for the `postPositionProfileForOpening` mutation.
"""
input PostPositionProfileForOpeningInput {
  """
  A profile for posting a job ad against an existing position opening.

  This contains information of how a position opening is presented on a given channel or job board.
  """
  positionProfile: PostPositionProfileForOpeningPositionProfileInput!
}

"""
A profile for posting a job ad against an existing position opening.

This contains information of how a position opening is presented on a given channel or job board.
"""
input PostPositionProfileForOpeningPositionProfileInput {
  """
  The identifier for the `PositionOpening` that this position profile belongs to.
  """
  positionOpeningId: String!

  """
  A short phrase describing the position as it would be listed on a business card or in a company directory.

  This field has a maximum length of 80 characters.
  """
  positionTitle: String!

  """
  An array of identifiers for the `HiringOrganization`s that have the position.

  Scheme requirements:

  - The `seekAnz` scheme requires exactly one element that matches the position opening's `PostingRequester.id`.
  """
  positionOrganizations: [String!]!

  """
  An optional hirer-provided free-text job reference.

  This field has a maximum length of 50 characters.
  """
  seekHirerJobReference: String

  """
  An optional free-text billing reference.

  This appears on the invoice when SEEK bills the hirer for the job ad.

  This field has a maximum length of 50 characters.
  """
  seekBillingReference: String

  """
  An array of formatted position profile descriptions.
  """
  positionFormattedDescriptions: [PositionFormattedDescriptionInput!]!

  """
  An array of codes for the offered schedules for the position.

  Currently, two codes are defined:

  - `FullTime` indicates a full-time schedule.
  - `PartTime` indicates a part-time schedule.

  Omit this field for the `seekAnz` scheme.
  """
  positionScheduleTypeCodes: [String!]

  """
  The salary or compensation offered for the position.
  """
  offeredRemunerationPackage: RemunerationPackageInput!

  """
  A SEEK work type code.

  For positions in `seekAnz` scheme, this field is used instead of `positionScheduleTypeCodes`.

  Currently, four codes are defined:

  - `Casual` indicates a casual position.
  - `ContractTemp` indicates a fixed-length contract position.
  - `FullTime` indicates a full-time position.
  - `PartTime` indicates a part-time position.

  Scheme requirements:

  - This field is required for the `seekAnz` scheme.
  - Omit this field for other schemes.
  """
  seekAnzWorkTypeCode: String

  """
  An array of `JobCategory` identifiers.

  Scheme requirements:

  - The `seekAnz` scheme requires exactly one identifier for a child job category.
  """
  jobCategories: [String!]!

  """
  An array of `Location` identifiers.

  Scheme requirements:

  - The `seekAnz` scheme requires exactly one element.
  """
  positionLocation: [String!]!

  """
  The identifier for the `ApplicationQuestionnaire` to present to a candidate during their application.

  The questionnaire responses will be available as `CandidateProfile.seekQuestionnaireSubmission` on the candidate's application profile.

  SEEK questionnaires are only supported on SEEK's Apply Form;
  omit this field if an `ApplicationMethodInput.applicationUri` is provided.
  """
  seekApplicationQuestionnaireId: String

  """
  The video to render within the job ad.
  """
  seekVideo: SeekVideoInput

  """
  The instructions related to posting the job ad.

  Scheme requirements:

  - The `seekAnz` scheme requires exactly one element.
  """
  postingInstructions: [CreatePostingInstructionInput!]!

  """
  An optional field for storing additional data with a `PositionProfile`.

  The metadata is not used by SEEK and won't be seen by hirers or candidates.

  This field has a maximum length of 1,000 characters.
  """
  seekPartnerMetadata: String

  """
  An array of SEEK work arrangement codes.

  Currently, three codes are defined:

  - `Hybrid` indicates a hybrid position.
  - `OnSite` indicates an on-site position.
  - `Remote` indicates a remote position.

  This field is limited to a single element.
  """
  seekWorkArrangementCodes: [String!]
}

"""
The salary or compensation for a position.
"""
input RemunerationPackageInput {
  """
  A code classifying the primary method of payment for a position.

  Currently, three codes are defined:

  - `Hourly` employment is paid for the number of hours worked.

  - `Salaried` employment is paid on a monthly or annual basis.

  - `SalariedPlusCommission` employment is paid on an annual basis plus a results-based commission.

  The specified value must correspond to `RemunerationRangeInput.intervalCode`:

  - When `RemunerationRangeInput.intervalCode` equals `Hour`, the `RemunerationPackageInput.basisCode` must be `Hourly`.

  - When `RemunerationRangeInput.intervalCode` equals `Month`, the `RemunerationPackageInput.basisCode` must be `Salaried`.

  - When `RemunerationRangeInput.intervalCode` equals `Year`, the `RemunerationPackageInput.basisCode` must be `Salaried` or `SalariedPlusCommission`.
  """
  basisCode: String!

  """
  An array of offered salary ranges.

  Scheme requirements:

  - The `global` scheme has a maximum of 10 elements for `UnpostedPositionProfile`s.
  - The `seekAnz` scheme is limited to a single element containing the amount for the `basisCode`.

  Salary ranges are used to refine candidate job searches.
  While the monetary values in `minimumAmount` and `maximumAmount` are not visible on job ads,
  the currency and interval may be displayed alongside the `descriptions` of the remuneration package for clarity.
  """
  ranges: [RemunerationRangeInput!]!

  """
  Human readable descriptions of the remuneration package.

  Scheme requirements:

  - The `global` scheme has a maximum of 10 elements for `UnpostedPositionProfile`s.
  - The `seekAnz` scheme is limited to a single element with a maximum length of 50 characters.

  An empty array must be provided to signify the absence of salary descriptions.
  """
  descriptions: [String!]!
}

"""
A salary or compensation range for a position.

Salary ranges are used to refine candidate job searches.
While the monetary values in `minimumAmount` and `maximumAmount` are not visible on job ads,
the currency and interval may be displayed alongside the `descriptions` of the remuneration package for clarity.
"""
input RemunerationRangeInput {
  """
  The minimum amount an organization is willing to pay for a position.

  The value must be greater than 0.
  """
  minimumAmount: RemunerationAmountInput!

  """
  The maximum amount an organization is willing to pay for a position.

  The value must be greater than or equal to the value of `minimumAmount` and the currency must match `minimumAmount`.

  This should be a mandatory input in your software and will be required in our schema in future.
  Currently, omitting the field will default it to `minimumAmount` and harm the performance of the job ad.
  """
  maximumAmount: RemunerationAmountInput

  """
  The interval the remuneration amounts are calculated over.

  Currently three interval codes are defined:

  - `Hour` is used to express hourly rates.
  - `Month` is used to express monthly salaries.
  - `Year` is used to express annual salaries.

  The specified value must correspond to `RemunerationPackageInput.basisCode`:

  - When `RemunerationPackageInput.basisCode` equals `Hourly`, the `RemunerationRangeInput.intervalCode` must be `Hour`.

  - When `RemunerationPackageInput.basisCode` equals `Salaried`, the `RemunerationRangeInput.intervalCode` must be `Month` or `Year`.

  - When `RemunerationPackageInput.basisCode` equals `SalariedPlusCommission`, the `RemunerationRangeInput.intervalCode` must be `Year`.
  """
  intervalCode: String!
}

"""
A monetary amount of remuneration in a specified currency.
"""
input RemunerationAmountInput {
  """
  A non-negative float in the major currency unit for the ISO currency code.

  For example, this is the number of dollars in dollar-denominated currencies.
  """
  value: Float!

  """
  The three-letter ISO 4217 currency code, in uppercase.

  For the `seekAnz` scheme, the following currencies are accepted:

  - `AUD`
  - `BDT`
  - `CNY`
  - `EUR`
  - `GBP`
  - `HKD`
  - `IDR`
  - `INR`
  - `JPY`
  - `MYR`
  - `NZD`
  - `PHP`
  - `SGD`
  - `THB`
  - `USD`
  - `VND`
  """
  currency: String!
}

"""
The information required for inferring a SEEK-specific location.
"""
input SeekPositionAddressInput {
  """
  The address of the location in a single formatted line.

  Example: `60-88 Cremorne St, Cremorne VIC 3121, Australia`.
  """
  formattedAddress: String!

  """
  The postal code of the location.

  Include this field to improve inference if you have access to a reliable postal code.

  This field has a maximum length of 50 characters.
  """
  postalCode: String

  """
  The two-letter ISO 3166-1:2013 country code of the location, in uppercase.
  """
  countryCode: String!
}

"""
An externally hosted video to display alongside advertisement details.
"""
input SeekVideoInput {
  """
  The URL of the video to display.

  Scheme requirements:

   - The `seekAnz` scheme requires a YouTube link in one of the following formats:

      - `https://www.youtube.com/embed/aAgePQvHBQM`
      - `https://www.youtube.com/watch?v=aAgePQvHBQM`
      - `https://youtube.com/embed/aAgePQvHBQM`
      - `https://youtube.com/watch?v=aAgePQvHBQM`
      - `https://youtu.be/aAgePQvHBQM`

     If the URL provided does not match the above criteria it will be ignored.
     Examples of unsupported formats include:

     - Links with additional query parameters: `https://www.youtube.com/watch?ab_channel=SEEKJobs&v=aAgePQvHBQM`
     - Mobile links: `https://m.youtube.com/watch?v=aAgePQvHBQM`
  """
  url: String!

  """
  The position relative to the advertisement details where the video should be rendered.

  Currently, two codes are defined:

  - `Above` indicates the video will render above the advertisement details.
  - `Below` indicates the video will render below the advertisement details.
  """
  seekAnzPositionCode: String!
}

"""
The input for synchronizing positions.
"""
input SyncPositionsInput {
  """
  An array of positions to synchronize.

  At least one position must be provided.
  """
  syncPositions: [SyncPositions_PositionInput!]!
}

"""
A position to be synchronized.
"""
input SyncPositions_PositionInput {
  """
  The partner's unique identifier for the position.

  This identifier is used to track the position across sync operations.
  """
  seekPartnerPositionId: String!

  """
  The organization or person requesting to post the position.
  """
  postingRequester: SyncPositions_PostingRequesterInput!

  """
  A short phrase describing the position as it would be listed on a business card or in a company directory.

  This field has a maximum length of 80 characters.
  """
  positionTitle: String!

  """
  An array of locations where the position is based.

  This field is limited to a single element.
  """
  positionLocation: [SyncPositions_PositionLocationInput!]!

  """
  An array of formatted position profile descriptions.

  At least one AdvertisementDetails description must be provided.
  """
  positionFormattedDescriptions: [PositionFormattedDescriptionInput!]!

  """
  A work type for the position.

  For example, "FullTime", "PartTime" and "Casual".
  """
  seekWorkType: String

  """
  An array of SEEK work arrangement codes.

  Currently, three codes are defined:
  - `Hybrid` indicates a hybrid position.
  - `OnSite` indicates an on-site position.
  - `Remote` indicates a remote position.

  This field is limited to a single element.
  """
  seekWorkArrangementCodes: [String!]

  """
  The salary or compensation offered for the position.
  """
  offeredRemunerationPackage: RemunerationPackageInput

  """
  The instructions related to posting the job ad.
  """
  postingInstructions: SyncPositions_PostingInstructionsInput

  """
  An optional field for storing additional data with a `PositionOpening`.

  The metadata is not used by SEEK and won't be seen by hirers or candidates.

  This field has a maximum length of 1,000 characters.
  """
  seekPartnerMetadata: String

  """
  An optional field for signalling hirer intent to post an ad via a channel other than Ad Sync eg. classic or Enhanced Job Posting.

  Currently only one code is defined `Other`.

  The inclusion of this field means that the position will not be posted to SEEK via Ad Sync.
  """
  seekPostingMethod: String

  """
  The array of questionnaire components in the order they are presented to the candidate.
  """
  applicationQuestionnaire: [SyncPositions_ApplicationQuestionnaireComponentInput!]

  """
  The date and time when the position was created in the partner's system.
  """
  createdDateTime: DateTime!

  """
  The date and time when the position was last updated in the partner's system.
  """
  updatedDateTime: DateTime!
}

"""
The organization or person requesting to post a position.
"""
input SyncPositions_PostingRequesterInput {
  """
  The partner's unique reference for the hiring organization.
  """
  seekOrgReference: String!

  """
  The name of the hiring organization.
  """
  seekOrgName: String!

  """
  The SEEK Ad Sync identifier for the hirer.

  This is assigned by SEEK when the hirer is onboarded to Ad Sync.
  This field must be exactly 24 characters.
  """
  seekAdSyncId: String

  """
  The partner's unique reference for the sub-organization.

  This identifies a subsidiary or regional entity within the hiring organization.
  """
  seekSubOrgReference: String

  """
  The name of the sub-organization.

  This identifies a subsidiary or regional entity within the hiring organization.
  """
  seekSubOrgName: String

  """
  An array of contact persons for the position.

  At least one contact person must be provided.
  A maximum of 10 contact people may be provided.
  """
  personContacts: [SpecifiedPersonInput!]!
}

"""
A subdivision of a country such as a state, province, or territory.
"""
input SyncPositions_CountrySubDivisionInput {
  """
  The type of country subdivision.

  Currently, one code is defined:
  - `State` indicates a state, province, or territory.
  """
  type: String!

  """
  The name or code of the country subdivision.

  For example, "VIC" for Victoria or "NSW" for New South Wales in Australia.
  """
  value: String!
}

"""
A physical location where work is performed.
"""
input SyncPositions_PositionLocationInput {
  """
  The city or locality of the position location.

  For example, "Melbourne" or "Sydney".
  """
  city: String

  """
  The country subdivision where the position is located.

  This typically represents a state, province, or territory.
  """
  countrySubDivisions: SyncPositions_CountrySubDivisionInput

  """
  The postal code of the position location.

  For example, "3000" for Melbourne CBD or "2000" for Sydney CBD.
  """
  postalCode: String

  """
  The ISO 3166-1 alpha-2 country code.

  For example, "AU" for Australia or "NZ" for New Zealand.
  This field has a maximum length of 2 characters.
  """
  country: String

  """
  The geographic coordinates of the position location.
  """
  geoLocation: GeoLocationInput
}

"""
Instructions for posting the job ad.
"""
input SyncPositions_PostingInstructionsInput {
  """
  An array of application method identifiers.

  If no methods are provided, SEEK's Apply Form will be used to receive candidate applications.
  """
  applicationMethods: [ApplicationMethodInput!]

  """
  The end date of the posting.
  """
  end: DateTime

  """
  Indicates whether the position should be suspended.
  """
  suspend: Boolean
}

"""
A component of the questionnaire to be created.
"""
input SyncPositions_ApplicationQuestionnaireComponentInput {
  """
  The type of the component.

  Currently, two codes are defined:

  - `Question` corresponds to the `question` field.
  - `PrivacyConsent` corresponds to the `privacyConsent` field.
  """
  componentTypeCode: String!

  """
  A question component of an `ApplicationQuestionnaire`.

  This must be provided if the `componentTypeCode` is `Question`.
  """
  question: ApplicationQuestionInput

  """
  A privacy consent component of an `ApplicationQuestionnaire`.

  This must be provided if the `componentTypeCode` is `PrivacyConsent`.
  """
  privacyConsent: ApplicationPrivacyConsentInput
}

"""
The input parameter for the `createUnpostedPositionProfileForOpening` mutation.
"""
input CreateUnpostedPositionProfileForOpeningInput {
  """
  An unposted profile of a position opening to create.
  """
  positionProfile: CreateUnpostedPositionProfileForOpeningPositionProfileInput!
}

"""
An unposted profile of a position opening to create.
"""
input CreateUnpostedPositionProfileForOpeningPositionProfileInput {
  """
  The identifier for the `PositionOpening` that this position profile belongs to.
  """
  positionOpeningId: String!

  """
  A human-readable name given to the profile.

  This in addition to the `positionTitle` can help identify the profile to an end user.
  """
  profileName: String

  """
  A short phrase describing the position as it would be listed on a business card or in a company directory.

  This field has a maximum length of 80 characters.
  """
  positionTitle: String!

  """
  The identifier for the `HiringOrganization` that has the position.

  This should contain exactly one element that matches the position opening's `PostingRequester.id`.
  """
  positionOrganizations: [String!]!

  """
  An optional hirer-provided free-text job reference.

  This field has a maximum length of 50 characters.
  """
  seekHirerJobReference: String

  """
  An optional free-text billing reference.

  SEEK does not use this field on unposted position profiles.

  This field has a maximum length of 50 characters.
  """
  seekBillingReference: String

  """
  An array of formatted position profile descriptions.

  A maximum of 10 formatted descriptions may be provided.
  """
  positionFormattedDescriptions: [PositionFormattedDescriptionInput!]!

  """
  An array of codes for the offered schedules for the position.

  Currently, two codes are defined:

  - `FullTime` indicates a full-time schedule.
  - `PartTime` indicates a part-time schedule.
  """
  positionScheduleTypeCodes: [String!]

  """
  The salary or compensation offered for the position.
  """
  offeredRemunerationPackage: RemunerationPackageInput!

  """
  An array of `JobCategory` identifiers.

  A maximum of 10 job categories may be provided.
  """
  jobCategories: [String!]!

  """
  An array of `Location` identifiers.

  A maximum of 10 locations may be provided.
  """
  positionLocation: [String!]!

  """
  An optional field for storing additional data with a `PositionProfile`.

  The metadata is not used by SEEK and won't be seen by hirers or candidates.

  This field has a maximum length of 1,000 characters.
  """
  seekPartnerMetadata: String
}

"""
The input parameter for the `updateUnpostedPositionProfile` mutation.
"""
input UpdateUnpostedPositionProfileInput {
  """
  An unposted profile of a position opening to update.
  """
  positionProfile: UpdateUnpostedPositionProfile_PositionProfileInput!
}

"""
An unposted profile of a position opening to update.
"""
input UpdateUnpostedPositionProfile_PositionProfileInput {
  """
  The identifier for the unposted `PositionProfile` to update.
  """
  profileId: String!

  """
  A human-readable name given to the profile.

  This in addition to the `positionTitle` can help identify the profile to an end user.
  """
  profileName: String

  """
  A short phrase describing the position as it would be listed on a business card or in a company directory.

  This field has a maximum length of 80 characters.
  """
  positionTitle: String!

  """
  An optional hirer-provided free-text job reference.

  This field has a maximum length of 50 characters.
  """
  seekHirerJobReference: String

  """
  An optional free-text billing reference.

  SEEK does not use this field on unposted position profiles.

  This field has a maximum length of 50 characters.
  """
  seekBillingReference: String

  """
  An array of formatted position profile descriptions.

  A maximum of 10 formatted descriptions may be provided.
  """
  positionFormattedDescriptions: [PositionFormattedDescriptionInput!]!

  """
  An array of codes for the offered schedules for the position.

  Currently, two codes are defined:

  - `FullTime` indicates a full-time schedule.
  - `PartTime` indicates a part-time schedule.
  """
  positionScheduleTypeCodes: [String!]

  """
  The salary or compensation offered for the position.
  """
  offeredRemunerationPackage: RemunerationPackageInput!

  """
  An array of `JobCategory` identifiers.

  A maximum of 10 job categories may be provided.
  """
  jobCategories: [String!]!

  """
  An array of `Location` identifiers.

  A maximum of 10 locations may be provided.
  """
  positionLocation: [String!]!

  """
  An optional field for storing additional data with a `PositionProfile`.

  The metadata is not used by SEEK and won't be seen by hirers or candidates.

  This field has a maximum length of 1,000 characters.
  """
  seekPartnerMetadata: String
}

"""
The input parameter for the `deleteUnpostedPositionProfile` mutation.
"""
input DeleteUnpostedPositionProfileInput {
  """
  The details of the unposted position profile to be deleted.
  """
  positionProfile: DeleteUnpostedPositionProfile_PositionProfileInput!
}

"""
The details of the unposted position profile to be deleted.
"""
input DeleteUnpostedPositionProfile_PositionProfileInput {
  """
  The identifier for the unposted `PositionProfile`.
  """
  profileId: String!
}

"""
The input parameter for the `updatePostedPositionProfile` mutation.
"""
input UpdatePostedPositionProfileInput {
  """
  The values to replace on a posted position profile.
  """
  positionProfile: UpdatePostedPositionProfile_PositionProfileInput!
}

"""
The values to replace on a posted position profile.
"""
input UpdatePostedPositionProfile_PositionProfileInput {
  """
  The job ad ID to update.
  """
  profileId: String!

  """
  A short phrase describing the position as it would be listed on a business card or in a company directory.

  This field has a maximum length of 80 characters.
  """
  positionTitle: String!

  """
  An array of identifiers for the `HiringOrganization`s that have the position.

  Scheme requirements:

  - The `seekAnz` scheme requires exactly one element that matches the position opening's `PostingRequester.id`.
  """
  positionOrganizations: [String!]!

  """
  An optional hirer-provided free-text job reference.

  This field has a maximum length of 50 characters.
  """
  seekHirerJobReference: String

  """
  An optional free-text billing reference.

  This appears on the invoice when SEEK bills the hirer for the job ad.

  This field has a maximum length of 50 characters.
  """
  seekBillingReference: String

  """
  An array of formatted position profile descriptions.
  """
  positionFormattedDescriptions: [PositionFormattedDescriptionInput!]!

  """
  An array of codes for the offered schedules for the position.

  Currently, two codes are defined:

  - `FullTime` indicates a full-time schedule.
  - `PartTime` indicates a part-time schedule.

  Omit this field for the `seekAnz` scheme.
  """
  positionScheduleTypeCodes: [String!]

  """
  The salary or compensation offered for the position.
  """
  offeredRemunerationPackage: RemunerationPackageInput!

  """
  A SEEK work type code.

  For positions in `seekAnz` scheme, this field is used instead of `positionScheduleTypeCodes`.

  Currently, four codes are defined:

  - `Casual` indicates a casual position.
  - `ContractTemp` indicates a fixed-length contract position.
  - `FullTime` indicates a full-time position.
  - `PartTime` indicates a part-time position.

  Scheme requirements:

  - This field is required for the `seekAnz` scheme.
  - Omit this field for other schemes.
  """
  seekAnzWorkTypeCode: String

  """
  An array of `JobCategory` identifiers.

  Scheme requirements:

  - The `seekAnz` scheme requires exactly one identifier for a child job category.
  """
  jobCategories: [String!]!

  """
  An array of `Location` identifiers.

  Scheme requirements:

  - The `seekAnz` scheme requires exactly one element.
  """
  positionLocation: [String!]!

  """
  This field is deprecated and must be omitted from all inputs.

  Do not explicitly set to `null` or any other value.
  """
  seekApplicationQuestionnaireId: String

  """
  The video to render within the job ad.
  """
  seekVideo: SeekVideoInput

  """
  The instructions related to posting the job ad.

  Scheme requirements:

  - The `seekAnz` scheme requires exactly one element.
  """
  postingInstructions: [UpdatePostingInstructionInput!]!

  """
  An optional field for storing additional data with a `PositionProfile`.

  The metadata is not used by SEEK and won't be seen by hirers or candidates.

  This field has a maximum length of 1,000 characters.
  """
  seekPartnerMetadata: String

  """
  An array of SEEK work arrangement codes.

  Currently, three codes are defined:

  - `Hybrid` indicates a hybrid position.
  - `OnSite` indicates an on-site position.
  - `Remote` indicates a remote position.

  This field is limited to a single element.
  """
  seekWorkArrangementCodes: [String!]
}

"""
Information about how to post a job ad and where to direct its candidate applications.
"""
input UpdatePostingInstructionInput {
  """
  The product ID for the update.

  The ad product selected will determine the features and performance of the job ad.
  This is not the identifier of the job ad itself; see `UpdatePostedPositionProfile_PositionProfileInput.profileId` for that.

  You cannot provide the same product ID that was initially used to post the job ad.
  Display ad selection to the user in your update workflow by using the Ad Selection Panel or `advertisementProducts` query.
  This will present a distinct set of update products along with associated pricing and payment information.
  Once an update product has been selected, its ID can be passed in this field.

  In certain scenarios, you may need to update a job ad without displaying ad selection to the hirer.
  You can retain the existing ad product by omitting this field entirely.
  """
  seekAdvertisementProductId: String

  """
  The end date of the posting.

  Scheme requirements:

  - For the `seekAnz` scheme this must be no more than 30 days after the job ad was initially posted.

    If an end date is omitted, the job ad's existing end date will be preserved.
  """
  end: DateTime

  """
  An array of methods for applying to the position.

  If no methods are provided, SEEK's Apply Form will be used to receive candidate applications.
  SEEK's Apply Form will emit a `CandidateApplicationCreated` event that points to a `CandidateProfile` object.

  This field should only be used to update the `ApplicationMethod.applicationUri` of an existing link out job ad.

  We do not recommend changing the application method of a job ad while it is live on the SEEK platform.
  Attempting to switch a live job ad between SEEK's Apply Form and linking out to an external apply form may result in unexpected behaviour and complicate applicant tracking.
  You may support closing and re-posting the job ad to SEEK with a different application method.

  Scheme requirements:

  - For the `seekAnz` scheme, this field is limited to a single element.
    Requests with more than 1 element will fail.
  """
  applicationMethods: [ApplicationMethodInput!]

  """
  The identifier for the `AdvertisementBranding` to apply to the posted job ad.

  Scheme requirements:

  - For the `seekAnz` scheme, this field's behavior is dependent on the `AdvertisementProductFeatures` of the product set in the `seekAdvertisementProductId` field.

    When the product's `AdvertisementProductFeatures.branding` value is null, this field will be silently ignored.
  """
  brandingId: String
}

"""
The details of the position profile to be previewed.
"""
input PostedPositionProfilePreview_PositionProfileInput {
  """
  A short phrase describing the position as it would be listed on a business card or in a company directory.

  This field has a maximum length of 80 characters.
  """
  positionTitle: String!

  """
  The identifier for the `PositionProfile` to be updated.

  This field should only be provided when updating an existing `PositionProfile`.
  Passing the `profileId` of an expired `PositionProfile` will result in an error.
  """
  profileId: String

  """
  An array of identifiers for the `HiringOrganization`s that have the position.

  Scheme requirements:

  - The `seekAnz` scheme requires exactly one element.
  """
  positionOrganizations: [String!]!

  """
  An array of formatted position profile descriptions.
  """
  positionFormattedDescriptions: [PositionFormattedDescriptionInput!]

  """
  A SEEK work type code.

  For positions in `seekAnz` scheme, this field is used instead of `positionScheduleTypeCodes`.

  Currently, four codes are defined:

  - `Casual` indicates a casual position.
  - `ContractTemp` indicates a fixed-length contract position.
  - `FullTime` indicates a full-time position.
  - `PartTime` indicates a part-time position.

  Scheme requirements:

  - This field is required for the `seekAnz` scheme.
  - Omit this field for other schemes.
  """
  seekAnzWorkTypeCode: String!

  """
  An array of `JobCategory` identifiers.

  Scheme requirements:

  - The `seekAnz` scheme has a maximum of one element.
  """
  jobCategories: [String!]

  """
  An array of `Location` identifiers.

  Scheme requirements:

  - The `seekAnz` scheme has a maximum of one element.
  """
  positionLocation: [String!]

  """
  The video to render within the job ad.
  """
  seekVideo: SeekVideoInput

  """
  The instructions related to posting the job ad.

  Scheme requirements:

  - The `seekAnz` scheme requires exactly one element.
  """
  postingInstructions: [PostedPositionProfilePreview_PostingInstructionInput!]!

  """
  The salary or compensation offered for the position.
  """
  offeredRemunerationPackage: PostedPositionProfilePreview_RemunerationPackageInput

  """
  The identifier for the `ApplicationQuestionnaire` to present to a candidate during their application.

  A preview of the question set may be displayed on the job ad.

  SEEK questionnaires are only supported on SEEK's Apply Form;
  omit this field if an `ApplicationMethodInput.applicationUri` is provided.
  """
  seekApplicationQuestionnaireId: String

  """
  An array of SEEK work arrangement codes.

  Currently, three codes are defined:

  - `Hybrid` indicates a hybrid position.
  - `OnSite` indicates an on-site position.
  - `Remote` indicates a remote position.

  This field is limited to a single element.
  """
  seekWorkArrangementCodes: [String!]
}

"""
The details of a job ad preview.
"""
type PostedPositionProfilePreview {
  """
  The URL of a standalone webpage to preview a job ad, with an origin of `job-ad-preview.seek.com`.

  The page can be embedded in an iframe or opened in a new tab.

  The webpage will expire after 24 hours.
  """
  previewUri: WebUrl!
}

"""
Information about how to post a job ad and where to direct its candidate applications.
"""
input PostedPositionProfilePreview_PostingInstructionInput {
  """
  The identifier for the `AdvertisementProduct`.
  """
  seekAdvertisementProductId: String

  """
  An array of methods for applying to the position.

  If no methods are provided, SEEK's Apply Form will be used to receive candidate applications.
  SEEK's Apply Form will emit a `CandidateApplicationCreated` event that points to a `CandidateProfile` object.

  Scheme requirements:

  - For the `seekAnz` scheme, this field is limited to a single element.
    Requests with more than 1 element will fail.
  """
  applicationMethods: [ApplicationMethodInput!]

  """
  The identifier for the `AdvertisementBranding` to apply to the posted job ad.

  Scheme requirements:

  - For the `seekAnz` scheme, this field's behavior is dependent on the `AdvertisementProductFeatures` of the product set in the `seekAdvertisementProductId` field.

    When the product's `AdvertisementProductFeatures.branding` value is null, this field will be silently ignored.
  """
  brandingId: String
}

"""
A monetary amount of remuneration in a specified currency for a position to be previewed prior to posting.
"""
input PostedPositionProfilePreview_RemunerationAmountInput {
  """
  A non-negative float in the major currency unit for the ISO currency code.

  For example, this is the number of dollars in dollar-denominated currencies.
  """
  value: Float!

  """
  The three-letter ISO 4217 currency code, in uppercase.

  For the `seekAnz` scheme, the following currencies are accepted:

  - `AUD`
  - `BDT`
  - `CNY`
  - `EUR`
  - `GBP`
  - `HKD`
  - `IDR`
  - `INR`
  - `JPY`
  - `MYR`
  - `NZD`
  - `PHP`
  - `SGD`
  - `THB`
  - `USD`
  - `VND`
  """
  currency: String!
}

"""
The salary or compensation for a position to be previewed prior to posting.
"""
input PostedPositionProfilePreview_RemunerationPackageInput {
  """
  A code classifying the primary method of payment for a position.

  Currently, three codes are defined:

  - `Hourly` employment is paid for the number of hours worked.

  - `Salaried` employment is paid on a monthly or annual basis.

  - `SalariedPlusCommission` employment is paid on an annual basis plus a results-based commission.
  """
  basisCode: String

  """
  Human readable descriptions of the remuneration package.

  Scheme requirements:

  - The `seekAnz` scheme is limited to a single element with a maximum length of 50 characters.

  An empty array must be provided to signify the absence of salary descriptions.
  """
  descriptions: [String!]!

  """
  An array of offered salary ranges.

  Scheme requirements:

  - The `seekAnz` scheme is limited to a single element containing the amount for the `basisCode`.

  Salary ranges are used to refine candidate job searches.
  While the monetary values in `minimumAmount` and `maximumAmount` are not visible on job ads,
  the currency and interval may be displayed alongside the `descriptions` of the remuneration package for clarity.
  """
  ranges: [PostedPositionProfilePreview_RemunerationRangeInput!]
}

"""
A salary or compensation range for a position to be previewed prior to posting.

Salary ranges are used to refine candidate job searches.
While the monetary values in `minimumAmount` and `maximumAmount` are not visible on job ads,
the currency and interval may be displayed alongside the `descriptions` of the remuneration package for clarity.
"""
input PostedPositionProfilePreview_RemunerationRangeInput {
  """
  The minimum amount an organization is willing to pay for a position.

  The value must be greater than 0.
  """
  minimumAmount: RemunerationAmountInput!

  """
  The maximum amount an organization is willing to pay for a position.

  The value must be greater than or equal to the value of `minimumAmount` and the currency must match `minimumAmount`.

  This should be a mandatory input in your software and will be required in our schema in future.
  Currently, omitting the field will default it to `minimumAmount` and harm the performance of the job ad.
  """
  maximumAmount: RemunerationAmountInput

  """
  The interval the remuneration amounts are calculated over.

  Currently three interval codes are defined:

  - `Hour` is used to express hourly rates.
  - `Month` is used to express monthly salaries.
  - `Year` is used to express annual salaries.

  The specified value must correspond to `RemunerationPackageInput.basisCode`:

  - When `RemunerationPackageInput.basisCode` equals `Hourly`, the `RemunerationRangeInput.intervalCode` must be `Hour`.

  - When `RemunerationPackageInput.basisCode` equals `Salaried`, the `RemunerationRangeInput.intervalCode` must be `Month` or `Year`.

  - When `RemunerationPackageInput.basisCode` equals `SalariedPlusCommission`, the `RemunerationRangeInput.intervalCode` must be `Year`.
  """
  intervalCode: String!
}

"""
The input parameter for the `applicationLibraryQuestionSuggestions` query.
"""
input ApplicationLibraryQuestionSuggestions_PositionProfileInput {
  """
  The position title.

  This field has a maximum length of 80 characters.
  """
  positionTitle: String!

  """
  An array of formatted position profile descriptions.
  """
  positionFormattedDescriptions: [PositionFormattedDescriptionInput!]

  """
  An array of `JobCategory` identifiers.

  Scheme requirements:

  - The `seekAnz` scheme requires exactly one element.
  """
  jobCategories: [String!]!

  """
  An array of `Location` identifiers.

  Scheme requirements:

  - The `seekAnz` scheme requires exactly one element.
  """
  positionLocation: [String!]!

  """
  An array of identifiers for the `HiringOrganization`s that have the position.

  Scheme requirements:

  The `seekAnz` scheme requires exactly one element.
  """
  positionOrganizations: [String!]!
}

"""
The input parameter for the `applicationLibraryQuestionSearch` query.
"""
input ApplicationLibraryQuestionSearch_PositionProfileInput {
  """
  An array of `Location` identifiers.

  Scheme requirements:

  - The `seekAnz` scheme requires exactly one element.
  """
  positionLocation: [String!]!

  """
  An array of identifiers for the `HiringOrganization`s that have the position.

  Scheme requirements:

  The `seekAnz` scheme requires exactly one element.
  """
  positionOrganizations: [String!]!
}

"""
The way in which the response choices should be presented for selection.
"""
type ApplicationLibraryQuestionPreferenceSelection {
  """
  The way to present the response choice selection.

  Currently, three codes are defined:

  - `SingleChoice` indicates a question that expects a single preferred response choice.

    A radio group or dropdown may be used.

  - `MultiChoice` indicates a question that expects multiple preferred response choices.

    A checkbox group may be used.

  - `Range` indicates a question that expects two response choices representing the minimum and maximum preferred values.

    A set of two dropdowns may be used.
  """
  typeCode: String!

  """
  A human-readable description of the way in which the response choice selection will apply.

  For example, the message can be:

  - `I will only accept this answer`
  - `I will accept any of these answers`
  - `I will only accept this combination of answers`
  - `I will accept above and including`
  - `I will accept up to and including`
  - `I will accept between this range`
  """
  message: String!
}

"""
A possible response to an `ApplicationLibraryQuestion`.
"""
type ApplicationLibraryQuestionChoice {
  """
  The identifier for the `ApplicationLibraryQuestionChoice`.
  """
  id: ObjectIdentifier!

  """
  Text of the choice displayed to the candidate.
  """
  text: String!
}

"""
A question from SEEK's library.

This consists of label text displayed to a user and an input for them to select a response.
"""
type ApplicationLibraryQuestion {
  """
  The identifier for the `ApplicationLibraryQuestion`.
  """
  id: ObjectIdentifier!

  """
  Text of the question displayed to the candidate.
  """
  text: String!

  """
  The status of the question.

  Currently, two codes are defined:

  - `Active` indicates that a question is active and can be used as input for the `createApplicationQuestionnaire` mutation.
  - `Retired` indicates that a question has been retired and cannot be used as input for the `createApplicationQuestionnaire` mutation.
  """
  statusCode: String!

  """
  The type of the question response.

  Currently, two codes are defined:

  - `MultiSelect` for choosing one or more responses from `responseChoice`.
  - `SingleSelect` for choosing a single response from `responseChoice`.
  """
  responseTypeCode: String!

  """
  The way in which the response choices should be presented for selection.
  """
  preferenceSelection: ApplicationLibraryQuestionPreferenceSelection!

  """
  The collection of possible responses.

  - `MultiSelect` questions contain at least two elements.
  - `SingleSelect` questions contain at least two elements.
  """
  responseChoice: [ApplicationLibraryQuestionChoice!]
}

"""
A suggested question component.
"""
type ApplicationLibraryQuestionSuggestion {
  """
  The identifier for the `ApplicationLibraryQuestionSuggestion`.
  """
  id: ObjectIdentifier!

  """
  The application question information of the suggestion.

  This will be a SEEK library question suitable for use in an application questionnaire.
  """
  applicationLibraryQuestion: ApplicationLibraryQuestion!
}

"""
A question search result.
"""
type ApplicationLibraryQuestionSearchResult {
  """
  The identifier for the `ApplicationLibraryQuestionSearchResult`.
  """
  id: ObjectIdentifier!

  """
  The application question information of the search result.

  This will be a SEEK library question suitable for use in an application questionnaire.
  """
  applicationLibraryQuestion: ApplicationLibraryQuestion!
}

"""
A set of questions presented to a candidate during an application.

This can be associated with one or more `PositionProfile`s when they are created.
"""
type ApplicationQuestionnaire {
  """
  The identifier for the `ApplicationQuestionnaire`.
  """
  id: ObjectIdentifier!

  """
  The hiring organization that created the questionnaire.

  The questionnaire can be attached to job ads posted by this organization.
  """
  hirer: HiringOrganization!

  """
  The array of components in the order they are presented to the candidate.
  """
  components: [ApplicationQuestionnaireComponent!]!
}

"""
A component of an application questionnaire.

This only contains identifying metadata;
the `componentTypeCode` can be used to determine the concrete type of the component.
"""
interface ApplicationQuestionnaireComponent {
  """
  The identifier for the `ApplicationQuestionnaireComponent`.
  """
  id: ObjectIdentifier!

  """
  The type of the component.

  Currently, two codes are defined:

  - `PrivacyConsent` which corresponds to the `ApplicationPrivacyConsent` type.
  - `Question` which corresponds to the `ApplicationQuestion` type.
  """
  componentTypeCode: String!

  """
  A partner-provided unique ID for the component.

  This can be used to correlate the component back to its corresponding representation in your software.
  """
  value: String
}

"""
A question component of an `ApplicationQuestionnaire`.

This consists of label text displayed to a user and an input for them to select a response.
"""
type ApplicationQuestion implements ApplicationQuestionnaireComponent {
  """
  The identifier for the `ApplicationQuestionnaireComponent`.
  """
  id: ObjectIdentifier!

  """
  The type of the component.

  This is always `Question`.
  """
  componentTypeCode: String!

  """
  The source of the component.

  Currently, two codes are defined:

  - `Custom` indicates that the question was authored by the hirer.
  - `Library` indicates that the question was sourced from SEEK's question library.
  """
  sourceCode: String!

  """
  The underlying library question that the component is based on.

  The availability of this field is dependent on `sourceCode`:

  - `Custom` is always null.
  - `Library` is always non-null.
  """
  applicationLibraryQuestion: ApplicationLibraryQuestion

  """
  A partner-provided unique ID for the question.

  This can be used to correlate the question back to its corresponding representation in your software.
  """
  value: String

  """
  The HTML snippet of the question being asked to the candidate.

  Unsupported tags will be silently stripped when creating a questionnaire.

  This field has a maximum length of 1,000 characters.
  """
  questionHtml: String!

  """
  The type of the question response.

  Currently, three codes are defined:

  - `FreeText` for a free text response.
  - `MultiSelect` for choosing one or more responses from `responseChoice`.
  - `SingleSelect` for choosing a single response from `responseChoice`.
  """
  responseTypeCode: String!

  """
  The collection of possible responses.

  - `FreeText` must not contain any elements.
  - `MultiSelect` must contain at least two elements.
  - `SingleSelect` must contain at least two elements.
  """
  responseChoice: [ApplicationQuestionChoice!]
}

"""
A privacy policy consent component of an `ApplicationQuestionnaire`.

This consists of a URL for candidates to view the privacy policy and text to prompt the candidate as to whether or not they agree.

The privacy policy consent component presents the candidate with a 'Yes' or 'No' choice.
"""
type ApplicationPrivacyConsent implements ApplicationQuestionnaireComponent {
  """
  The identifier for the `ApplicationQuestionnaireComponent`.
  """
  id: ObjectIdentifier!

  """
  The type of the component.

  This is always `PrivacyConsent`.
  """
  componentTypeCode: String!

  """
  A partner-provided unique ID for the question.

  This can be used to correlate the question back to its corresponding representation in your software.
  """
  value: String

  """
  The URL of the privacy policy to show to the candidate.
  """
  privacyPolicyUrl: WebUrl!

  """
  The HTML snippet to prompt the candidate for consent.

  Unsupported tags will be silently stripped when creating a questionnaire.

  This is optional and will default to 'Do you agree to the privacy policy?'.
  """
  descriptionHtml: String
}

"""
A possible response to an `ApplicationQuestion`.
"""
type ApplicationQuestionChoice {
  """
  The identifier for the `ApplicationQuestionChoice`.
  """
  id: ObjectIdentifier!

  """
  Text of the choice displayed to the candidate.
  """
  text: String!

  """
  A partner-provided unique ID for the question choice.

  This can be used to correlate the choice back to its corresponding representation in your software.
  """
  value: String

  """
  Whether this choice is preferred when scoring the answers.

  This is not displayed to the candidate.
  """
  preferredIndicator: Boolean!

  """
  Whether this choice was explicitly selected as a preference.

  When authoring a new questionnaire based on existing questions,
  response choice selections may be pre-filled from this indicator.

  - For a custom question, this always matches `preferredIndicator`.

  - For a library question, this is set for bounding preferences only.

    For example, a library question may define choices 1–5,
    and require a minimum and maximum to be selected as a range preference.
    Selecting the range 2–4 will set `preferredIndicator` for choices 2, 3, and 4,
    and `selectedIndicator` for bounding choices 2 and 4.

  This is not displayed to the candidate.
  """
  selectedIndicator: Boolean!

  """
  The underlying library question choice that the question choice is based on.

  The availability of this field is dependent on the parent `ApplicationQuestion.sourceCode`:

  - `Custom` is always null.
  - `Library` is always non-null.
  """
  applicationLibraryQuestionChoice: ApplicationLibraryQuestionChoice
}

"""
A response to a component in a questionnaire.

This only contains metadata related to the component responded to in the questionnaire.
The implementation of a response is based on the `componentTypeCode` of its component.
"""
interface ApplicationQuestionnaireComponentResponse {
  """
  The component this is responding to.
  """
  component: ApplicationQuestionnaireComponent!

  """
  The type of the component.

  Currently, two codes are defined:

  - `PrivacyConsent` which corresponds to the `ApplicationPrivacyConsent` type.
  - `Question` which corresponds to the `ApplicationQuestion` type.
  """
  componentTypeCode: String!
}

"""
A single answer to a question in the questionnaire.
"""
type ApplicationQuestionAnswer {
  """
  The questionnaire choice for the answer.

  For `FreeText` questions this will be `null`.
  """
  choice: ApplicationQuestionChoice

  """
  The text value of the answer.

  For `FreeText` questions this may contain whitespace such as the `\n` newline escape sequence.
  For readability, you should display [whitespace characters](https://developer.mozilla.org/en-US/docs/Web/CSS/white-space) instead of collapsing them.
  """
  answer: String!
}

"""
A candidate's response to a question in the questionnaire.
"""
type ApplicationQuestionResponse implements ApplicationQuestionnaireComponentResponse {
  """
  The question this is responding to.
  """
  component: ApplicationQuestion!

  """
  The type of the component.

  This is always `Question`.
  """
  componentTypeCode: String!

  """
  The answers provided by the candidate.

  For `SingleSelect` and `FreeText` this will be a single element array.
  """
  answers: [ApplicationQuestionAnswer!]!

  """
  How well the candidate answered the question against the hirer's preferences.

  The score is calculated differently based on the `responseTypeCode`:

  - For `FreeText`, the score will be null.
  - For `MultiSelect`, the score will be between 0 and 1 as a floating point.
    For example, if the candidate selected half of the preferred answers, the score would be 0.5.
  - For `SingleSelect`, the score will be either 1 or 0 based off whether or not the candidate selected a preferred answer.
  """
  score: Float
}

"""
A candidate's response to a privacy policy consent component in the questionnaire.
"""
type ApplicationPrivacyConsentResponse implements ApplicationQuestionnaireComponentResponse {
  """
  The privacy consent component this is responding to.
  """
  component: ApplicationPrivacyConsent!

  """
  The type of the component.

  This is always `PrivacyConsent`.
  """
  componentTypeCode: String!

  """
  This indicates whether or not the candidate agrees to the provided privacy policy.
  """
  consentGivenIndicator: Boolean!
}

"""
A completed candidate submission for an `ApplicationQuestionnaire`.
"""
type ApplicationQuestionnaireSubmission {
  """
  The set of questions presented to the candidate during the application.
  """
  questionnaire: ApplicationQuestionnaire!

  """
  The candidate's responses to the application's questionnaire.
  """
  responses: [ApplicationQuestionnaireComponentResponse!]!

  """
  The indication of how well the candidate scored on the questionnaire overall.

  The score is a calculation between 0 and 1 as a floating point.
  For example, if the candidate received a score of 1 on one question, and a score of 0 on another, this overall score would be calculated as 0.5.
  If there are no scored questions this score will be null.
  """
  score: Float
}

"""
A privacy policy consent component of an `ApplicationQuestionnaire`.

This consists of a URL for candidates to view the privacy policy and text to prompt the candidate as to whether or not they agree.

The privacy policy consent component always defaults the available response choices for the candidate to 'Yes' and 'No'.
"""
input ApplicationPrivacyConsentInput {
  """
  The type of the component.

  This is always `PrivacyConsent`.
  """
  componentTypeCode: String!

  """
  A partner-provided unique ID for the consent component.

  This can be used to correlate the consent component back to its corresponding representation in your software.
  This must be unique across all components in the questionnaire.
  """
  value: String

  """
  The URL of the privacy policy to show to the candidate.

  The `url` field has a maximum length of 1,000 characters.
  """
  privacyPolicyUrl: WebUrlInput!

  """
  The HTML snippet to prompt the candidate for consent.

  Unsupported tags will be silently stripped when creating a questionnaire.

  This is optional and will default to 'Do you agree to the privacy policy?'.
  """
  descriptionHtml: String
}

"""
A question component of an `ApplicationQuestionnaire`.

This consists of label text displayed to a user and an input for them to select a response.
"""
input ApplicationQuestionInput {
  """
  The type of the component.

  This is always `Question`.
  """
  componentTypeCode: String!

  """
  The HTML snippet of the question being asked to the candidate.

  Unsupported tags will be silently stripped when creating a questionnaire.

  This field has a maximum length of 1,000 characters.
  """
  questionHtml: String!

  """
  The type of the question response.

  Currently, three codes are defined:

  - `FreeText` for a free text response.
  - `MultiSelect` for choosing one or more responses from `responseChoice`.
  - `SingleSelect` for choosing a single response from `responseChoice`.
  """
  responseTypeCode: String!

  """
  A partner-provided unique ID for this question.

  This can be used to correlate the question back to its corresponding representation in your software.
  This must be unique across all components in the questionnaire.
  """
  value: String

  """
  The collection of possible responses.

  For `SingleSelect` and `MultiSelect` this must contain between 2 and 99 elements, inclusive.
  """
  responseChoice: [ApplicationQuestionChoiceInput!]
}

"""
A possible response to an `ApplicationQuestion`.
"""
input ApplicationQuestionChoiceInput {
  """
  Text of the choice displayed to the candidate.

  This must be unique across all choices in the question.
  """
  text: String!

  """
  A partner-provided unique ID for this choice.

  This can be used to correlate the choice back to its corresponding representation in your software.

  This must be unique across all choices in the question.
  """
  value: String

  """
  Whether this choice is preferred when scoring the answers.

  This is not displayed to the candidate.
  """
  preferredIndicator: Boolean!
}

"""
A question component of an `ApplicationQuestionnaire`.

This consists of identifiers for the library question and its preferred response choices.
"""
input ApplicationLibraryQuestionInput {
  """
  The type of the component.

  This is always `Question`.
  """
  componentTypeCode: String!

  """
  The identifier of the library question or suggestion that the component is based on.

  If the library question was suggested via the `applicationLibraryQuestionSuggestions` query,
  you should supply the `ApplicationLibraryQuestionSuggestion.id` rather than the `ApplicationLibraryQuestion.id`.

  If the library question was retrieved outside of a suggestion context,
  you may supply its underlying `ApplicationLibraryQuestion.id`.
  """
  id: String!

  """
  The selected response choice identifiers for the question.

  The identifiers should be populated from available response choices within `ApplicationQuestion.responseChoice` returned with question suggestions.
  """
  selectedResponseChoice: [String!]
}

"""
A component of the questionnaire to be created.
"""
input ApplicationQuestionnaireComponentInput {
  """
  The type of the component.

  Currently, two codes are defined:

  - `PrivacyConsent` corresponds to the `privacyConsent` field.
  - `Question` corresponds to the `question` and `libraryQuestion` fields.
  """
  componentTypeCode: String!

  """
  A question component of an `ApplicationQuestionnaire`.

  Either this or `libraryQuestion` must be provided if the `componentTypeCode` is `Question`.
  """
  question: ApplicationQuestionInput

  """
  A library question component of an `ApplicationQuestionnaire`.

  Either this or `question` must be provided if the `componentTypeCode` is `Question`.

  The SEEK library question is sourced from the `applicationLibraryQuestionSuggestions` query.
  """
  libraryQuestion: ApplicationLibraryQuestionInput

  """
  A privacy consent component of an `ApplicationQuestionnaire`.

  This must be provided if the `componentTypeCode` is `PrivacyConsent`.
  """
  privacyConsent: ApplicationPrivacyConsentInput
}

"""
The details of the questionnaire to be created.
"""
input CreateApplicationQuestionnaire_ApplicationQuestionnaireInput {
  """
  The identifier for the `HiringOrganization` that will own the questionnaire.

  Hirers can only associate position profiles with questionnaires they own.
  """
  hirerId: String!

  """
  The array of components in the order they are presented to the candidate.
  """
  components: [ApplicationQuestionnaireComponentInput!]!
}

"""
The input parameter for the `createApplicationQuestionnaire` mutation.

This must not exceed 56 KiB in length.
"""
input CreateApplicationQuestionnaireInput {
  """
  The details of the questionnaire to be created.
  """
  applicationQuestionnaire: CreateApplicationQuestionnaire_ApplicationQuestionnaireInput!
}

"""
The response from the `createApplicationQuestionnaire` mutation.
"""
type CreateApplicationQuestionnairePayload {
  """
  The details of the created questionnaire.
  """
  applicationQuestionnaire: ApplicationQuestionnaire!
}
